import React, { useState, Component, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Dropdown,
  Button,
  Col,
  Table,
  Card,
  Tabs,
  Tab,
  Modal,
  Pagination,
} from "react-bootstrap";
import Calendar from "react-calendar";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import loadable from "@loadable/component";
import pMinDelay from "p-min-delay";
import PageTitle from "./../../layouts/PageTitle";

//import Images:
import avatar from "../../../images/avatar/1.png";
import avatar1 from "../../../images/avatar/2.jpg";
import avatar2 from "../../../images/avatar/3.jpg";
import { useAuth } from "../../../context/AuthContext";
import { usePaymentAccessScope } from "../../../hook/usePaymentAccessScope";
import {
  canEditTechnicianPendingBatch,
  canManageOwnTechnicianPayments,
  canViewOwnTechnicianPaidCases,
  getTechnicianDashboardScope,
} from "../../paymentDashboardAccess";
import {
  formatTechnicianPaymentOtherCaseBreakdown,
  getTechnicianPaymentCandidateOrders,
  getTechnicianPaymentPhaseCounts,
  hasTechnicianPaymentHn,
} from "./technicianPaymentQuery";
import {
  findTechnicianPaymentBooking,
  normalizeTechnicianPaymentOrder,
} from "./technicianPaymentOrderLookup";
import { getFullTimeExternalScoreRate } from "./technicianScoreRate";
import { db, functions, storage } from "../../../firebase";
import { httpsCallable } from "firebase/functions";
import {
  query,
  endAt,
  getDocs,
  collection,
  orderBy,
  limit,
  startAfter,
  endBefore,
  limitToLast,
  startAt,
  getCountFromServer,
  where,
  doc,
  deleteDoc,
  deleteField,
  updateDoc,
  setDoc,
  collectionGroup,
  Timestamp,
  getDoc,
  serverTimestamp,
  writeBatch,
  or,
  onSnapshot,
} from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import {
  notifyTopRight,
  notifyWarning,
  notifyError,
} from "../../components/toast/SetToast";
import Toast from "../../components/toast/SetToast";
import { gu, te } from "date-fns/locale";
import {
  toNumber,
  togglePay,
  PaidIcon,
  Phase,
  money,
  parseNum,
  formatDate,
  hasText,
  safeSheetName,
  norm,
  safeExt,
  tsToLocalString,
  dateFromInputYMD,
  getTechnicianFrom,
  getThaiMonth,
  togglePayTechnician,
} from "../Admin/DashboardUtils";
import { rangeContainsMarker } from "@fullcalendar/core/internal";
import { getPatientPagePath } from "../../../utils/patientPageUtils";

const DashboardLoading = () => (
  <React.Fragment>
    <Toast />

    <div className="mb-3 text-center">
      <h2>
        ภาพรวมประจำเดือน{" "}
        <strong>{getThaiMonth(new Date().getMonth())}</strong>
      </h2>
    </div>

    <div
      className="d-flex flex-column align-items-center justify-content-center text-center py-5 my-4"
      aria-live="polite"
    >
      <div
        className="spinner-border text-primary mb-3"
        style={{ width: "3rem", height: "3rem" }}
        role="status"
      >
        <span className="sr-only">Loading...</span>
      </div>
      <h5 className="mb-1">กำลังโหลดข้อมูล Dashboard...</h5>
      <div className="text-muted">
        Please wait while the latest records are loading.
      </div>
    </div>
  </React.Fragment>
);

const getNodeShortName = (nodeName, nodeCodeByName = {}) => {
  const trimmedName = (nodeName || "").trim();
  if (!trimmedName) return "No Node";

  const nodeCode =
    nodeCodeByName[trimmedName] || nodeCodeByName[trimmedName.toLowerCase()];
  if (nodeCode) return nodeCode;

  if (trimmedName === "No Node") return trimmedName;

  return trimmedName
    .replace(/[()]/g, " ")
    .split(/\s+/)
    .filter(
      (word) =>
        word &&
        !["hospital", "center", "clinic", "group", "the", "of", "and"].includes(
          word.toLowerCase(),
        ),
    )
    .map((word) => word.charAt(0))
    .join("")
    .toUpperCase();
};

const getScheduledNodeSummary = (recordList, nodeCodeByName) => {
  const nodeCounts = {};

  (recordList || []).forEach((activity) => {
    const props = activity.extendedProps || {};
    const nodeName = (props.node || "").trim() || "No Node";
    nodeCounts[nodeName] = (nodeCounts[nodeName] || 0) + 1;
  });

  return Object.keys(nodeCounts)
    .map((node) => ({
      node,
      label: getNodeShortName(node, nodeCodeByName),
      count: nodeCounts[node],
    }))
    .sort((a, b) => b.count - a.count || a.node.localeCompare(b.node));
};

const getPaymentDate = (value) => {
  const date =
    value && typeof value.toDate === "function"
      ? value.toDate()
      : value instanceof Date
        ? value
        : value
          ? new Date(value)
          : null;

  return date && !Number.isNaN(date.getTime()) ? date : null;
};

const paymentTimestamp = (value) => {
  const date = getPaymentDate(value);
  return date ? date.getTime() : 0;
};

const formatPaymentDate = (value, includeTime = false) => {
  const date = getPaymentDate(value);
  if (!date) return "-";

  const options = includeTime
    ? {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }
    : { day: "2-digit", month: "short", year: "numeric" };

  return includeTime
    ? date.toLocaleString("en-GB", options)
    : date.toLocaleDateString("en-GB", options);
};

const getPaidDateTooltip = (phase, paidDate) => {
  const formattedDate = formatPaymentDate(paidDate, true);
  return formattedDate === "-"
    ? `${phase} payment is marked as paid (date unavailable).`
    : `${phase} paid on ${formattedDate}`;
};

const normalizePatientNoteTitle = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const PATIENT_NOTE_TYPES = {
  sleepStudyForm: {
    label: "Sleep Study Form",
    normalizedTitle: "sleep study form",
    notesKey: "sleepStudyFormNotes",
  },
  consentForm: {
    label: "Consent Form",
    normalizedTitle: "consent form",
    notesKey: "consentFormNotes",
  },
};

const PAYMENT_TABLE_WIDTHS = {
  case: 235,
  phase: 96,
  total: 90,
  action: 52,
};
const CONFIRMED_BATCHES_PER_PAGE = 15;

const getPaymentStickyColumnStyle = ({
  side,
  offset,
  width,
  header = false,
  edge = false,
}) => ({
  position: "sticky",
  [side]: offset,
  width,
  minWidth: width,
  maxWidth: width,
  zIndex: header ? 4 : 2,
  backgroundColor: header ? "#f8f9fa" : "#fff",
  ...(edge
    ? {
        boxShadow:
          side === "left"
            ? "5px 0 7px -6px rgba(0, 0, 0, 0.45)"
            : "-5px 0 7px -6px rgba(0, 0, 0, 0.45)",
      }
    : {}),
});

const getPatientHn = (booking) => {
  const props = (booking && booking.extendedProps) || {};
  return String(props.hn || (booking && booking.hn) || "").trim();
};

const hasPaymentEntryStudyType = (entry) =>
  Boolean(String((entry && (entry.type || entry.studyType)) || "").trim());

const hydratePaymentBatchStudyTypes = async (batches = []) => {
  const bookingRefPaths = [
    ...new Set(
      (batches || []).flatMap((batch) =>
        (batch.entries || [])
          .filter(
            (entry) =>
              entry &&
              entry.bookingRefPath &&
              !hasPaymentEntryStudyType(entry),
          )
          .map((entry) => entry.bookingRefPath),
      ),
    ),
  ];

  if (!bookingRefPaths.length) return batches;

  const studyTypes = await Promise.all(
    bookingRefPaths.map(async (bookingRefPath) => {
      try {
        const bookingSnapshot = await getDoc(doc(db, bookingRefPath));
        const props = bookingSnapshot.exists()
          ? (bookingSnapshot.data() || {}).extendedProps || {}
          : {};
        return [bookingRefPath, String(props.type || "").trim()];
      } catch (error) {
        console.warn(
          "Unable to load study type for technician payment batch",
          bookingRefPath,
          error,
        );
        return [bookingRefPath, ""];
      }
    }),
  );
  const studyTypeByBookingPath = new Map(studyTypes);

  return (batches || []).map((batch) => ({
    ...batch,
    entries: (batch.entries || []).map((entry) => {
      if (!entry || hasPaymentEntryStudyType(entry)) return entry;

      const studyType = studyTypeByBookingPath.get(entry.bookingRefPath);
      return studyType ? { ...entry, type: studyType } : entry;
    }),
  }));
};

const PaymentPeriodControls = ({ startDate, endDate, onQuery }) => {
  const [draftStartDate, setDraftStartDate] = useState(startDate);
  const [draftEndDate, setDraftEndDate] = useState(endDate);

  useEffect(() => {
    setDraftStartDate(startDate);
  }, [startDate]);

  useEffect(() => {
    setDraftEndDate(endDate);
  }, [endDate]);

  return (
    <React.Fragment>
      <div className="ml-3 d-inline-flex">
        <label className="mr-2 mb-0 align-self-center">Start:</label>
        <input
          type="date"
          className="form-control"
          value={draftStartDate}
          onChange={(e) => setDraftStartDate(e.target.value)}
        />
      </div>

      <div className="ml-3 d-inline-flex">
        <label className="mr-2 mb-0 align-self-center">End:</label>
        <input
          type="date"
          className="form-control"
          value={draftEndDate}
          onChange={(e) => setDraftEndDate(e.target.value)}
        />
      </div>
      <div className="ml-3 d-inline-flex">
        <Button
          className="btn-danger"
          onClick={() => onQuery(draftStartDate, draftEndDate)}
        >
          Query
        </Button>
      </div>
    </React.Fragment>
  );
};

const PaymentBatchAddByOrderControls = ({ onAdd, disabled = false }) => {
  const [order, setOrder] = useState("");
  const [adding, setAdding] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (disabled || adding) return;

    setAdding(true);
    try {
      const added = await onAdd(order);
      if (added) setOrder("");
    } finally {
      setAdding(false);
    }
  };

  return (
    <form className="d-flex align-items-end" onSubmit={handleSubmit}>
      <div className="mr-2">
        <label
          className="mb-1 text-muted font-weight-bold"
          htmlFor="technician-batch-payment-order"
        >
          Add case by Order
        </label>
        <input
          id="technician-batch-payment-order"
          type="text"
          className="form-control"
          style={{ minWidth: 220 }}
          placeholder="e.g. CHG-SS-123"
          value={order}
          onChange={(event) => setOrder(event.target.value)}
          disabled={disabled || adding}
        />
      </div>
      <Button type="submit" className="mb-0" disabled={disabled || adding}>
        <i className="fa fa-plus mr-1" />
        {adding ? "Adding…" : "Add"}
      </Button>
    </form>
  );
};

const PaymentDraftControls = ({
  amount,
  wht,
  guaranteeCase,
  initialDeduction,
  initialTransport,
  initialAdjustmentPay,
  initialPayDate,
  initialPayNote,
  resetKey,
  canManagePayments,
  uploadingTransportFile,
  transportFiles,
  transportFileInputRef,
  onTransportFileChange,
  onClearTransportFiles,
  onRemoveTransportFile,
  getTransportFileKey,
  getTransportFileName,
  onCreatePayment,
}) => {
  const [deduction, setDeduction] = useState(initialDeduction);
  const [transport, setTransport] = useState(initialTransport);
  const [adjustmentPay, setAdjustmentPay] = useState(initialAdjustmentPay);
  const [payDate, setPayDate] = useState(initialPayDate);
  const [payNote, setPayNote] = useState(initialPayNote);

  useEffect(() => {
    setDeduction(initialDeduction);
    setTransport(initialTransport);
    setAdjustmentPay(initialAdjustmentPay);
    setPayDate(initialPayDate);
    setPayNote(initialPayNote);
  }, [
    initialAdjustmentPay,
    initialDeduction,
    initialPayDate,
    initialPayNote,
    initialTransport,
    resetKey,
  ]);

  const totalPay = Math.max(
    0,
    amount -
      wht -
      toNumber(deduction) -
      guaranteeCase +
      toNumber(transport) +
      toNumber(adjustmentPay),
  );
  const createDisabled =
    amount <= 0 || !payDate || !canManagePayments || uploadingTransportFile;

  return (
    <React.Fragment>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <label className="mb-0">Deduction</label>
        <div className="d-flex align-items-center">
          <strong>
            <span className="ms-2">-</span>
          </strong>
          <input
            type="number"
            className="form-control form-control-sm"
            style={{ width: 140 }}
            value={deduction}
            onChange={(event) => setDeduction(event.target.value)}
            min="0"
          />
        </div>
      </div>
      <hr className="my-2" />
      <div className="d-flex justify-content-between align-items-center mb-2">
        <label className="mb-0">Transport</label>
        <div className="d-flex align-items-center">
          <strong>
            <span className="ms-2">+</span>
          </strong>
          <input
            type="number"
            className="form-control form-control-sm"
            style={{ width: 140 }}
            value={transport}
            onChange={(event) => setTransport(event.target.value)}
            min="0"
          />
        </div>
      </div>
      <div className="mb-2">
        <label className="mb-1">
          Transport documents{" "}
          <small className="text-muted">(optional PDFs or images)</small>
        </label>
        <div className="d-flex align-items-center flex-wrap">
          <input
            ref={transportFileInputRef}
            type="file"
            className="form-control-file mr-2"
            accept="application/pdf,image/*"
            multiple
            onChange={onTransportFileChange}
            disabled={uploadingTransportFile}
          />
          {transportFiles.length > 0 && (
            <Button
              type="button"
              size="sm"
              variant="outline-secondary"
              onClick={onClearTransportFiles}
              disabled={uploadingTransportFile}
            >
              Remove all
            </Button>
          )}
        </div>
        {transportFiles.length > 0 && (
          <div className="small text-muted mt-1">
            {transportFiles.map((file) => (
              <div
                key={getTransportFileKey(file)}
                className="d-flex align-items-center flex-wrap mb-1"
              >
                <span className="mr-2 text-truncate">
                  <i className="fa fa-paperclip mr-1" />
                  {getTransportFileName(file)}
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => onRemoveTransportFile(file)}
                  disabled={uploadingTransportFile}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <label className="mb-0">Adjustment Pay</label>
        <div className="d-flex align-items-center">
          <strong>
            <span className="ms-2">+</span>
          </strong>
          <input
            type="number"
            className="form-control form-control-sm"
            style={{ width: 140 }}
            value={adjustmentPay}
            onChange={(event) => setAdjustmentPay(event.target.value)}
            min="0"
          />
        </div>
      </div>
      <hr className="my-2" />
      <div className="mb-2">
        <label className="mb-1">Payment Note</label>
        <textarea
          className="form-control form-control-sm"
          value={payNote}
          onChange={(event) => setPayNote(event.target.value)}
          rows={3}
        />
      </div>

      <hr className="my-2" />

      <div className="d-flex justify-content-between mb-3 mt-3">
        <span>Total Pay</span>
        <h3 className="mb-0">
          <strong>{money(totalPay)} THB</strong>
        </h3>
      </div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <label className="mb-0">Pay Date</label>
        <input
          type="date"
          className="form-control form-control-sm"
          style={{ width: 160 }}
          value={payDate}
          onChange={(event) => setPayDate(event.target.value)}
        />
      </div>
      <div className="d-flex justify-content-end">
        <Button
          onClick={() =>
            onCreatePayment({
              deduction,
              transport,
              adjustmentPay,
              payDate,
              payNote,
            })
          }
          disabled={createDisabled}
          title={
            uploadingTransportFile
              ? "Creating payment..."
              : amount <= 0 || !payDate
                ? "Select items and choose a pay date"
                : "Create Payment"
          }
        >
          {uploadingTransportFile ? "Creating Payment..." : "Create Payment"}
        </Button>
      </div>
    </React.Fragment>
  );
};

const PaymentBatchReviewTable = ({
  entries = [],
  editing = false,
  onUpdatePhaseAmount,
  onRemoveEntry,
  renderPatientNoteAvailability,
  bookingDetailsByPath = {},
}) => {
  const canRemoveEntries = editing && typeof onRemoveEntry === "function";
  const actionOffset = canRemoveEntries ? PAYMENT_TABLE_WIDTHS.action : 0;

  return (
  <div className="table-responsive border rounded shadow-sm mb-3">
    <Table hover className="table-striped table-sm mb-0" style={{ minWidth: 1250 }}>
      <thead className="thead-light">
        <tr>
          <th
            rowSpan="2"
            className="align-middle text-nowrap"
            style={getPaymentStickyColumnStyle({
              side: "left",
              offset: 0,
              width: PAYMENT_TABLE_WIDTHS.case,
              header: true,
              edge: true,
            })}
          >
            Patient / Order
          </th>
          <th
            rowSpan="2"
            className="align-middle text-nowrap"
            style={{ minWidth: 210 }}
          >
            Insurance / Status
          </th>
          <th
            rowSpan="2"
            className="align-middle text-nowrap"
            style={{ minWidth: 220 }}
          >
            Technicians
          </th>
          <th
            rowSpan="2"
            className="text-center align-middle border-left text-nowrap"
            style={{ minWidth: 180 }}
          >
            Patient Notes
          </th>
          <th colSpan="2" className="text-center border-left text-nowrap">
            Paid Status
          </th>
          <th
            colSpan="2"
            className="text-center border-left text-nowrap"
            style={{
              ...getPaymentStickyColumnStyle({
                side: "right",
                offset: PAYMENT_TABLE_WIDTHS.total + actionOffset,
                width: PAYMENT_TABLE_WIDTHS.phase * 2,
                header: true,
                edge: true,
              }),
              zIndex: 5,
            }}
          >
            Phases (This Batch)
          </th>
          <th
            rowSpan="2"
            className="text-right align-middle text-nowrap border-left"
            style={getPaymentStickyColumnStyle({
              side: "right",
              offset: actionOffset,
              width: PAYMENT_TABLE_WIDTHS.total,
              header: true,
            })}
          >
            <span className="d-block">Total</span>
            <small className="d-block font-weight-normal">THB</small>
          </th>
          {canRemoveEntries && (
            <th
              rowSpan="2"
              className="text-center align-middle text-nowrap border-left"
              style={getPaymentStickyColumnStyle({
                side: "right",
                offset: 0,
                width: PAYMENT_TABLE_WIDTHS.action,
                header: true,
                edge: true,
              })}
            >
              Delete
            </th>
          )}
        </tr>
        <tr>
          <th
            className="text-center text-nowrap border-left"
            style={{ minWidth: 72 }}
          >
            Record
          </th>
          <th className="text-center text-nowrap" style={{ minWidth: 72 }}>
            Score
          </th>
          <th
            className="text-center text-nowrap border-left"
            style={getPaymentStickyColumnStyle({
              side: "right",
              offset:
                PAYMENT_TABLE_WIDTHS.phase +
                PAYMENT_TABLE_WIDTHS.total +
                actionOffset,
              width: PAYMENT_TABLE_WIDTHS.phase,
              header: true,
            })}
          >
            Record
          </th>
          <th
            className="text-center text-nowrap"
            style={getPaymentStickyColumnStyle({
              side: "right",
              offset: PAYMENT_TABLE_WIDTHS.total + actionOffset,
              width: PAYMENT_TABLE_WIDTHS.phase,
              header: true,
            })}
          >
            Score
          </th>
        </tr>
      </thead>
      <tbody>
        {entries.map((entry, index) => {
          const phases = entry.phases || [];
          const patientHn = getPatientHn(entry);
          const patientName =
            [entry.firstname, entry.lastname].filter(Boolean).join(" ") ||
            "-";
          const bookingDetails =
            bookingDetailsByPath[entry.bookingRefPath] || {};
          const caseDetail =
            entry.latestSleepStudyDate ||
            entry.sleepStudyDate ||
            bookingDetails.latestSleepStudyDate ||
            "-";
          const rowTotal = phases.reduce(
            (sum, phase) => sum + Math.max(0, toNumber(phase.amount)),
            0,
          );
          const phaseIndex = (phaseName) =>
            phases.findIndex((phase) => phase.phase === phaseName);
          const phaseAmount = (phaseName) =>
            phases
              .filter((phase) => phase.phase === phaseName)
              .reduce(
                (sum, phase) => sum + Math.max(0, toNumber(phase.amount)),
                0,
              );
          const paid = entry.paid || {};
          const paidRecord = Boolean(paid.record && paid.record.isPaid);
          const paidScore = Boolean(paid.score && paid.score.isPaid);
          const recordPhaseIndex = phaseIndex("record");
          const scorePhaseIndex = phaseIndex("score");

          return (
            <tr key={`${entry.order || "entry"}-${index}`}>
              <td
                className="align-middle"
                style={getPaymentStickyColumnStyle({
                  side: "left",
                  offset: 0,
                  width: PAYMENT_TABLE_WIDTHS.case,
                  edge: true,
                })}
              >
                <div
                  className="font-weight-bold text-truncate"
                  style={{ lineHeight: "18px" }}
                  title={entry.order || "-"}
                >
                  {entry.order || "-"}
                </div>
                {patientHn ? (
                  <Link
                    to={getPatientPagePath(patientHn)}
                    className="d-block font-weight-bold text-primary text-truncate"
                    style={{ lineHeight: "18px", textDecoration: "none" }}
                    title={`Open patient: ${patientName}`}
                  >
                    {patientName}
                  </Link>
                ) : (
                  <div
                    className="font-weight-bold text-truncate"
                    style={{ lineHeight: "18px" }}
                    title={patientName}
                  >
                    {patientName}
                  </div>
                )}
                <div
                  className="text-muted text-truncate"
                  style={{ fontSize: 11, lineHeight: "15px" }}
                  title={caseDetail}
                >
                  <i className="fa fa-calendar-o mr-1" />
                  {caseDetail}
                </div>
              </td>
              <td className="align-middle">
                <div className="d-flex align-items-center mb-1">
                  <small className="text-muted mr-2" style={{ minWidth: 58 }}>
                    Insurance
                  </small>
                  <span
                    className="badge badge-light border text-dark d-inline-block text-truncate"
                    style={{ maxWidth: 135 }}
                    title={entry.healthInsurance || "-"}
                  >
                    {entry.healthInsurance || "-"}
                  </span>
                </div>
                <div className="d-flex align-items-center">
                  <small className="text-muted mr-2" style={{ minWidth: 58 }}>
                    Status
                  </small>
                  <span
                    className="badge badge-light border text-dark d-inline-block text-truncate"
                    style={{ maxWidth: 135 }}
                    title={entry.status || bookingDetails.status || "-"}
                  >
                    {entry.status || bookingDetails.status || "-"}
                  </span>
                </div>
              </td>
              <td className="align-middle">
                <div className="d-flex align-items-center mb-1">
                  <small className="text-muted mr-2" style={{ minWidth: 42 }}>
                    Record
                  </small>
                  <span
                    className="d-inline-block text-truncate"
                    style={{ maxWidth: 158 }}
                    title={entry.techrecord || "-"}
                  >
                    <i className="fa fa-user-o text-muted mr-1" />
                    {entry.techrecord || "-"}
                  </span>
                </div>
                <div className="d-flex align-items-center">
                  <small className="text-muted mr-2" style={{ minWidth: 42 }}>
                    Score
                  </small>
                  <span
                    className="d-inline-block text-truncate"
                    style={{ maxWidth: 158 }}
                    title={entry.technician || "-"}
                  >
                    <i className="fa fa-user-o text-muted mr-1" />
                    {entry.technician || "-"}
                  </span>
                </div>
              </td>
              <td className="align-middle border-left">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <small className="text-muted text-nowrap mr-2">
                    Sleep Study Form
                  </small>
                  {renderPatientNoteAvailability(entry, "sleepStudyForm")}
                </div>
                <div className="d-flex align-items-center justify-content-between">
                  <small className="text-muted text-nowrap mr-2">
                    Consent Form
                  </small>
                  {renderPatientNoteAvailability(entry, "consentForm")}
                </div>
              </td>
              <td className="text-center align-middle border-left">
                <PaidIcon
                  on={paidRecord}
                  title={
                    paidRecord
                      ? getPaidDateTooltip(
                          "Record",
                          paid.record && paid.record.paidDate,
                        )
                      : undefined
                  }
                />
              </td>
              <td className="text-center align-middle">
                <PaidIcon
                  on={paidScore}
                  title={
                    paidScore
                      ? getPaidDateTooltip(
                          "Score",
                          paid.score && paid.score.paidDate,
                        )
                      : undefined
                  }
                />
              </td>
              <td
                className="text-center align-middle border-left"
                style={getPaymentStickyColumnStyle({
                  side: "right",
                  offset:
                    PAYMENT_TABLE_WIDTHS.phase +
                    PAYMENT_TABLE_WIDTHS.total +
                    actionOffset,
                  width: PAYMENT_TABLE_WIDTHS.phase,
                  edge: true,
                })}
              >
                {editing && recordPhaseIndex >= 0 ? (
                  <input
                    type="number"
                    min="0"
                    className="form-control form-control-sm text-end"
                    style={{ width: 82 }}
                    aria-label={`Record amount for ${entry.order}`}
                    value={phases[recordPhaseIndex].amount || 0}
                    onChange={(event) =>
                      onUpdatePhaseAmount(
                        index,
                        recordPhaseIndex,
                        event.target.value,
                      )
                    }
                  />
                ) : (
                  <Phase
                    checked={recordPhaseIndex >= 0}
                    amount={phaseAmount("record")}
                    disabled={true}
                  />
                )}
              </td>
              <td
                className="text-center align-middle"
                style={getPaymentStickyColumnStyle({
                  side: "right",
                  offset: PAYMENT_TABLE_WIDTHS.total + actionOffset,
                  width: PAYMENT_TABLE_WIDTHS.phase,
                })}
              >
                {editing && scorePhaseIndex >= 0 ? (
                  <input
                    type="number"
                    min="0"
                    className="form-control form-control-sm text-end"
                    style={{ width: 82 }}
                    aria-label={`Score amount for ${entry.order}`}
                    value={phases[scorePhaseIndex].amount || 0}
                    onChange={(event) =>
                      onUpdatePhaseAmount(
                        index,
                        scorePhaseIndex,
                        event.target.value,
                      )
                    }
                  />
                ) : (
                  <Phase
                    checked={scorePhaseIndex >= 0}
                    amount={phaseAmount("score")}
                    disabled={true}
                  />
                )}
              </td>
              <td
                className="text-right align-middle border-left text-nowrap"
                style={getPaymentStickyColumnStyle({
                  side: "right",
                  offset: actionOffset,
                  width: PAYMENT_TABLE_WIDTHS.total,
                })}
              >
                <strong className={rowTotal > 0 ? "text-primary" : ""}>
                  {money(rowTotal)}
                </strong>
              </td>
              {canRemoveEntries && (
                <td
                  className="text-center align-middle border-left"
                  style={getPaymentStickyColumnStyle({
                    side: "right",
                    offset: 0,
                    width: PAYMENT_TABLE_WIDTHS.action,
                    edge: true,
                  })}
                >
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    title={`Delete ${entry.order || "this case"} from this draft`}
                    aria-label={`Delete ${entry.order || "this case"} from this draft`}
                    onClick={() => onRemoveEntry(index)}
                  >
                    <i className="fa fa-trash" />
                  </button>
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </Table>
  </div>
  );
};

const timestampToDateInput = (value) => {
  const date = getPaymentDate(value);

  if (!date) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const TechDashboard = () => {
  const { currentUser } = useAuth();
  const {
    loading: paymentScopeLoading,
    paymentAccess: configuredPaymentAccess,
    providerName: configuredTechName,
    mapping: paymentUserMapping,
    canManagePayments: hasConfiguredPaymentManagement,
  } = usePaymentAccessScope("sleep_technician");
  const {
    providerName: scopedTechName,
    hasFullPaymentAccess: paymentAccess,
    canViewAllTechnicians,
    canQueryAllTechnicianCases,
    canViewOwnTechnician,
  } = getTechnicianDashboardScope({
    currentRoles: currentUser && currentUser.roles,
    displayName: currentUser && currentUser.displayName,
    mappedProviderName:
      (paymentUserMapping && paymentUserMapping.bookingName) ||
      configuredTechName,
    hasConfiguredPaymentAccess: configuredPaymentAccess,
  });
  const canManagePayments =
    hasConfiguredPaymentManagement ||
    canManageOwnTechnicianPayments({
      currentRoles: currentUser && currentUser.roles,
      providerName: scopedTechName,
    });
  const canViewConfirmedPayments =
    paymentAccess ||
    canViewOwnTechnicianPaidCases({
      currentRoles: currentUser && currentUser.roles,
      providerName: scopedTechName,
    });
  // Super admins may see every technician in Overview. Case queries and
  // payment actions still use the own mapping unless full payment access exists.
  const canViewOverall =
    canManagePayments || canViewAllTechnicians || canViewOwnTechnician;

  const [technicianList, setTechnicianList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activityList, setActivityList] = useState([]);
  const [scoredList, setScoredList] = useState([]);
  const [allActivityList, setAllActivityList] = useState([]);
  const [nodeCodeByName, setNodeCodeByName] = useState({});
  const [selectedTech, setSelectedTech] = useState("Summary");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [bookingTmpList, setBookingTmpList] = useState([]);
  const [activityTmpList, setActivityTmpList] = useState([]);
  const [activeTab, setActiveTab] = useState("overview"); // 'overview' | 'payments'

  const [payThisRound, setPayThisRound] = useState({});
  const [deduction, setDeduction] = React.useState(0);
  const [transport, setTransport] = React.useState(0);
  const [adjustmentPay, setAdjustmentPay] = React.useState(0);
  const [payDate, setPayDate] = React.useState(""); // "YYYY-MM-DD"
  const [payNote, setPayNote] = React.useState("");
  const [paymentDraftResetKey, setPaymentDraftResetKey] = useState(0);
  const [pendingBatches, setPendingBatches] = useState([]); // list of draft batches for the doctor
  const [loadingPending, setLoadingPending] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null); // batch doc to review (opens modal/section)
  const [batchEdit, setBatchEdit] = useState(null);
  const [savingBatchEdit, setSavingBatchEdit] = useState(false);
  const [rollingBackBatchKey, setRollingBackBatchKey] = useState("");
  const [pendingBatchConfirmation, setPendingBatchConfirmation] = useState(
    null,
  );
  const [processingBatchConfirmation, setProcessingBatchConfirmation] =
    useState(false);
  const [readyToEmail, setReadyToEmail] = useState([]);
  const [loadingReady, setLoadingReady] = useState(false);
  const [confirmedPaymentsPage, setConfirmedPaymentsPage] = useState(1);
  const [technicianRecordNoteCheckEnabled, setTechnicianRecordNoteCheckEnabled] =
    useState(false);
  const [technicianRecordNoteCheckLoading, setTechnicianRecordNoteCheckLoading] =
    useState(true);
  const [patientNoteStatusByHn, setPatientNoteStatusByHn] = useState({});
  const [loadingPatientNoteStatuses, setLoadingPatientNoteStatuses] =
    useState(false);
  const [batchBookingDetailsByPath, setBatchBookingDetailsByPath] = useState(
    {},
  );
  const [transportFiles, setTransportFiles] = useState([]);
  const [uploadingTransportFile, setUploadingTransportFile] = useState(false);
  const [transportFilePreview, setTransportFilePreview] = useState(null);
  const [patientNoteViewer, setPatientNoteViewer] = useState(null);
  const [patientNoteFilePreview, setPatientNoteFilePreview] = useState(null);
  const [loadingPatientNoteFilePreview, setLoadingPatientNoteFilePreview] =
    useState(false);

  const manualOrderRef = useRef("");
  const transportFileInputRef = useRef(null);
  const batchEditTransportFileInputRef = useRef(null);
  const patientNotePreviewRequestRef = useRef(0);
  const recordAllRef = React.useRef(null);
  const scoreAllRef = React.useRef(null);

  const BATCH_SUBCOL = "batches";
  const getPatientNoteSignedUrl = httpsCallable(functions, "getSignedUrl");

  const PHASE_FIELD_MAP = {
    record: {
      paidFlag: "isPaidRecord",
      paidDate: "paidRecordDate",
      paidBatch: "paidRecordBatchId",
    },
    score: {
      paidFlag: "isPaidScore",
      paidDate: "paidScoreDate",
      paidBatch: "paidScoreBatchId",
    },
  };

  const technicianKeyFrom = (tech, selectedTech) =>
    (tech.techId || "").trim() || (selectedTech || "").trim();

  const scopedTech = technicianList.find(
    (technician) => technician.name === scopedTechName,
  );
  const scopedTechKey = scopedTech
    ? technicianKeyFrom(scopedTech, scopedTechName)
    : "";
  const canAccessBatch = (batch) =>
    paymentAccess ||
    (!!scopedTechKey && batch && batch.techKey === scopedTechKey);
  const canEditPendingBatch = (batch) =>
    canEditTechnicianPendingBatch({
      canManagePayments,
      hasFullPaymentAccess: paymentAccess,
      providerKey: scopedTechKey,
      batchTechKey: batch && batch.techKey,
      isConfirmed: batch && batch.isConfirmed,
    });
  const canRollbackConfirmedBatch = paymentAccess;

  const getTransportFileName = (file) =>
    String(
      (file && (file.fileName || file.name || file.savedFileName)) ||
        "Transport document",
    );

  const getTransportFileExtension = (file) => {
    const fileName = getTransportFileName(file).split("?")[0];
    const lastDot = fileName.lastIndexOf(".");
    return lastDot >= 0 ? fileName.slice(lastDot + 1).toLowerCase() : "";
  };

  const getTransportFileKey = (file) =>
    String(
      (file && (file.storagePath || file.url)) ||
        [
          getTransportFileName(file),
          file && file.size,
          file && (file.lastModified || file.uploadedAt),
        ].join("|"),
    );

  const uniqueTransportFiles = (files) => {
    const keys = new Set();
    return (files || []).filter((file) => {
      if (!file) return false;
      const key = getTransportFileKey(file);
      if (keys.has(key)) return false;
      keys.add(key);
      return true;
    });
  };

  const getBatchTransportFiles = (meta) => {
    const batchMeta = meta || {};
    const files = Array.isArray(batchMeta.transportFiles)
      ? batchMeta.transportFiles
      : [];
    return uniqueTransportFiles(
      batchMeta.transportFile
        ? [...files, batchMeta.transportFile]
        : files,
    );
  };

  const isAllowedTransportFile = (file) => {
    const contentType = String((file && file.type) || "").toLowerCase();
    const extension = getTransportFileExtension(file);
    return (
      contentType === "application/pdf" ||
      contentType.indexOf("image/") === 0 ||
      ["pdf", "jpg", "jpeg", "png", "gif", "webp"].includes(extension)
    );
  };

  const getAllowedTransportFiles = (fileList) => {
    const files = Array.from(fileList || []);
    const allowedFiles = files.filter(isAllowedTransportFile);

    if (allowedFiles.length !== files.length) {
      notifyWarning("Only PDF or image transport documents can be uploaded.");
    }

    return allowedFiles;
  };

  const removeTransportFilesFromStorage = async (files, warningMessage) => {
    await Promise.all(
      uniqueTransportFiles(files)
        .filter((file) => file.storagePath)
        .map(async (file) => {
          try {
            await deleteObject(ref(storage, file.storagePath));
          } catch (cleanupError) {
            console.warn(warningMessage, cleanupError);
          }
        }),
    );
  };

  const isTransportImageFile = (file) => {
    const contentType = String(
      (file && (file.contentType || file.type)) || "",
    ).toLowerCase();
    return (
      contentType.indexOf("image/") === 0 ||
      ["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(
        getTransportFileExtension(file),
      )
    );
  };

  const isTransportPdfFile = (file) =>
    String((file && (file.contentType || file.type)) || "").toLowerCase() ===
      "application/pdf" || getTransportFileExtension(file) === "pdf";

  const uploadTransportFile = async (file, techKey, batchId) => {
    const originalName = getTransportFileName(file);
    const safeName = originalName.replace(/[^a-zA-Z0-9._-]/g, "_") || "transport";
    const savedFileName = `${uuidv4()}_${safeName}`;
    const storagePath = `paymentTechBatches/${techKey}/batches/${batchId}/transport/${savedFileName}`;
    const storageRef = ref(storage, storagePath);

    try {
      await uploadBytes(storageRef, file, {
        contentType: file.type || "application/octet-stream",
      });
      const url = await getDownloadURL(storageRef);

      return {
        fileName: originalName,
        savedFileName,
        storagePath,
        url,
        contentType: file.type || "",
        size: Number(file.size || 0),
        uploadedAt: new Date().toISOString(),
        uploadedBy: currentUser.displayName || currentUser.email || "",
        fileType: "transport",
      };
    } catch (error) {
      try {
        await deleteObject(storageRef);
      } catch (cleanupError) {
        console.warn(
          "Unable to clean up an incomplete transport file upload.",
          cleanupError,
        );
      }
      throw error;
    }
  };

  const openTransportFilePreview = async (file) => {
    if (!file) return;

    try {
      const url =
        file.url ||
        (file.storagePath
          ? await getDownloadURL(ref(storage, file.storagePath))
          : "");
      if (!url) throw new Error("Transport file URL is unavailable.");

      setTransportFilePreview({ ...file, url });
    } catch (error) {
      console.error("Unable to open transport file", error);
      notifyError("Unable to open transport file", error.message || error);
    }
  };

  const renderTransportFileLinks = (files, className = "") => {
    const transportFileList = uniqueTransportFiles(files);
    if (!transportFileList.length) return null;

    return (
      <div className={className}>
        {transportFileList.map((file) => (
          <div key={getTransportFileKey(file)}>
            <button
              type="button"
              className="btn btn-link btn-sm p-0 text-left"
              onClick={() => openTransportFilePreview(file)}
            >
              <i className="fa fa-paperclip mr-1" />
              {getTransportFileName(file)}
            </button>
          </div>
        ))}
      </div>
    );
  };

  const getPatientNoteFiles = (notes) =>
    (notes || []).reduce((files, note) => {
      const fileNames = Array.isArray(note.filename)
        ? note.filename
        : note.filename
          ? [note.filename]
          : [];

      return [
        ...files,
        ...fileNames.filter(Boolean).map((fileName) => ({
          hn: note.hn,
          noteId: note.id,
          noteTitle: note.title,
          noteDetail: note.detail,
          fileName,
        })),
      ];
    }, []);

  const getPatientNoteFileExtension = (file) => {
    const fileName = String((file && file.fileName) || "").split("?")[0];
    const lastDot = fileName.lastIndexOf(".");
    return lastDot >= 0 ? fileName.slice(lastDot + 1).toLowerCase() : "";
  };

  const isPatientNoteImageFile = (file) =>
    ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].includes(
      getPatientNoteFileExtension(file),
    );

  const isPatientNotePdfFile = (file) =>
    getPatientNoteFileExtension(file) === "pdf";

  const getPatientNoteFileKey = (file) =>
    `${(file && file.noteId) || ""}|${(file && file.fileName) || ""}`;

  const openPatientNoteFile = async (file) => {
    if (!file || !file.hn || !file.noteId || !file.fileName) return;

    const requestId = patientNotePreviewRequestRef.current + 1;
    patientNotePreviewRequestRef.current = requestId;
    setPatientNoteFilePreview({ ...file, url: "" });
    setLoadingPatientNoteFilePreview(true);

    try {
      const result = await getPatientNoteSignedUrl({
        folder: "files",
        hn: file.hn,
        id: file.noteId,
        filename: file.fileName,
      });

      if (patientNotePreviewRequestRef.current !== requestId) return;

      setPatientNoteFilePreview({
        ...file,
        url: result.data,
      });
    } catch (error) {
      if (patientNotePreviewRequestRef.current !== requestId) return;
      console.error("Unable to open patient note file", error);
      setPatientNoteFilePreview(null);
      notifyError("Unable to open patient note file", error.message || error);
    } finally {
      if (patientNotePreviewRequestRef.current === requestId) {
        setLoadingPatientNoteFilePreview(false);
      }
    }
  };

  const openPatientNoteViewer = (row, noteType) => {
    const config = PATIENT_NOTE_TYPES[noteType];
    const hn = getPatientHn(row);
    const status = patientNoteStatusByHn[hn] || {};
    const notes = config ? status[config.notesKey] || [] : [];
    const files = getPatientNoteFiles(notes);
    const patient = (row && row.extendedProps) || row || {};
    const patientName = [patient.firstname, patient.lastname]
      .filter(Boolean)
      .join(" ");

    setPatientNoteViewer({
      hn,
      patientName,
      title: config ? config.label : "Patient Notes",
      notes,
      files,
    });
    setPatientNoteFilePreview(null);

    if (files.length) {
      openPatientNoteFile(files[0]);
    }
  };

  const closePatientNoteViewer = () => {
    patientNotePreviewRequestRef.current += 1;
    setPatientNoteViewer(null);
    setPatientNoteFilePreview(null);
    setLoadingPatientNoteFilePreview(false);
  };

  const renderPatientNoteAvailability = (row, noteType) => {
    const config = PATIENT_NOTE_TYPES[noteType];
    const hn = getPatientHn(row);
    const status = patientNoteStatusByHn[hn] || null;
    const isLoading = Boolean(hn && !status);
    const hasNote = Boolean(status && status[noteType]);
    const notes = status && config ? status[config.notesKey] || [] : [];
    const fileCount = getPatientNoteFiles(notes).length;

    if (isLoading) {
      return <span className="badge badge-light">Checking…</span>;
    }

    if (hasNote) {
      return (
        <button
          type="button"
          className="btn btn-link p-0"
          style={{ textDecoration: "none" }}
          onClick={() => openPatientNoteViewer(row, noteType)}
          title={`View ${config.label}${fileCount ? ` (${fileCount} files)` : ""}`}
          aria-label={`View ${config.label} for HN ${hn}`}
        >
          <span className="badge badge-success">
            Yes{fileCount ? ` (${fileCount})` : ""}
          </span>
        </button>
      );
    }

    return (
      <span className="badge badge-danger">
        {hn ? "Missing" : "HN missing"}
      </span>
    );
  };

  const clearTransportFileSelection = () => {
    setTransportFiles([]);
    if (transportFileInputRef.current) {
      transportFileInputRef.current.value = "";
    }
  };

  const handleTransportFileChange = (event) => {
    const files = getAllowedTransportFiles(event.target.files);
    event.target.value = "";
    if (!files.length) return;

    setTransportFiles((previous) =>
      uniqueTransportFiles([...(previous || []), ...files]),
    );
  };

  const removeSelectedTransportFile = (file) => {
    const key = getTransportFileKey(file);
    setTransportFiles((previous) =>
      (previous || []).filter(
        (selectedFile) => getTransportFileKey(selectedFile) !== key,
      ),
    );
  };

  const clearBatchEditTransportFileSelection = () => {
    if (batchEditTransportFileInputRef.current) {
      batchEditTransportFileInputRef.current.value = "";
    }
    setBatchEdit((previous) =>
      previous
        ? { ...previous, newTransportFiles: [] }
        : previous,
    );
  };

  const handleBatchEditTransportFileChange = (event) => {
    const files = getAllowedTransportFiles(event.target.files);
    event.target.value = "";
    if (!files.length) return;

    setBatchEdit((previous) =>
      previous
        ? {
            ...previous,
            newTransportFiles: uniqueTransportFiles([
              ...(previous.newTransportFiles || []),
              ...files,
            ]),
          }
        : previous,
    );
  };

  const removeBatchEditNewTransportFile = (file) => {
    const key = getTransportFileKey(file);
    setBatchEdit((previous) =>
      previous
        ? {
            ...previous,
            newTransportFiles: (previous.newTransportFiles || []).filter(
              (newFile) => getTransportFileKey(newFile) !== key,
            ),
          }
        : previous,
    );
  };

  const toggleBatchEditTransportFileDeletion = (file) => {
    const key = getTransportFileKey(file);
    setBatchEdit((previous) => {
      if (!previous) return previous;
      const removedKeys = previous.removedTransportFileKeys || [];
      const isRemoved = removedKeys.includes(key);

      return {
        ...previous,
        removedTransportFileKeys: isRemoved
          ? removedKeys.filter((removedKey) => removedKey !== key)
          : [...removedKeys, key],
      };
    });
  };

  useEffect(() => {
    const settingsRef = doc(
      db,
      "paymentAccessSettings",
      "technicianRecordNoteCheck",
    );

    return onSnapshot(
      settingsRef,
      (snapshot) => {
        const settings = snapshot.exists() ? snapshot.data() || {} : {};
        setTechnicianRecordNoteCheckEnabled(Boolean(settings.enabled));
        setTechnicianRecordNoteCheckLoading(false);
      },
      (error) => {
        console.error("Unable to load technician payment note check", error);
        setTechnicianRecordNoteCheckEnabled(false);
        setTechnicianRecordNoteCheckLoading(false);
      },
    );
  }, []);

  useEffect(() => {
    let active = true;
    const patientHnSet = new Set(
      bookingTmpList.map(getPatientHn).filter(Boolean),
    );
    ((selectedBatch && selectedBatch.entries) || []).forEach((entry) => {
      const hn = getPatientHn(entry);
      if (hn) patientHnSet.add(hn);
    });
    const patientHns = [...patientHnSet];

    if (!patientHns.length) {
      setPatientNoteStatusByHn({});
      setLoadingPatientNoteStatuses(false);
      return undefined;
    }

    const loadPatientNoteStatuses = async () => {
      setLoadingPatientNoteStatuses(true);
      setPatientNoteStatusByHn({});

      const statuses = await Promise.all(
        patientHns.map(async (hn) => {
          try {
            const noteSnapshot = await getDocs(
              collection(db, "patients", hn, "note"),
            );
            const notes = noteSnapshot.docs.map((noteDoc) => {
              const note = noteDoc.data() || {};
              return {
                id: noteDoc.id,
                hn: String(note.hn || hn).trim(),
                title: note.title || "",
                detail: note.detail || "",
                filename: Array.isArray(note.filename)
                  ? note.filename.filter(Boolean)
                  : note.filename
                    ? [note.filename]
                    : [],
                normalizedTitle: normalizePatientNoteTitle(note.title),
              };
            });
            const sleepStudyFormNotes = notes.filter(
              (note) =>
                note.normalizedTitle ===
                PATIENT_NOTE_TYPES.sleepStudyForm.normalizedTitle,
            );
            const consentFormNotes = notes.filter(
              (note) =>
                note.normalizedTitle ===
                PATIENT_NOTE_TYPES.consentForm.normalizedTitle,
            );

            return [
              hn,
              {
                sleepStudyForm: sleepStudyFormNotes.length > 0,
                consentForm: consentFormNotes.length > 0,
                sleepStudyFormNotes,
                consentFormNotes,
              },
            ];
          } catch (error) {
            console.error(`Unable to load patient notes for ${hn}`, error);
            return [
              hn,
              {
                sleepStudyForm: false,
                consentForm: false,
                sleepStudyFormNotes: [],
                consentFormNotes: [],
                loadError: true,
              },
            ];
          }
        }),
      );

      if (!active) return;

      setPatientNoteStatusByHn(Object.fromEntries(statuses));
      setLoadingPatientNoteStatuses(false);
    };

    loadPatientNoteStatuses();

    return () => {
      active = false;
    };
  }, [bookingTmpList, selectedBatch]);

  useEffect(() => {
    let active = true;
    const entries = (selectedBatch && selectedBatch.entries) || [];
    const bookingPaths = [
      ...new Set(
        entries
          .filter(
            (entry) =>
              entry &&
              entry.bookingRefPath &&
              (!entry.latestSleepStudyDate || !entry.status),
          )
          .map((entry) => entry.bookingRefPath),
      ),
    ];

    if (!bookingPaths.length) {
      setBatchBookingDetailsByPath({});
      return undefined;
    }

    const loadBatchBookingDetails = async () => {
      const details = await Promise.all(
        bookingPaths.map(async (bookingRefPath) => {
          try {
            const bookingSnapshot = await getDoc(doc(db, bookingRefPath));
            const props = bookingSnapshot.exists()
              ? (bookingSnapshot.data() || {}).extendedProps || {}
              : {};

            return [
              bookingRefPath,
              {
                latestSleepStudyDate: props.latestSleepStudyDate || "",
                status: props.status || "",
              },
            ];
          } catch (error) {
            console.warn(
              "Unable to load booking details for payment batch review",
              bookingRefPath,
              error,
            );
            return [bookingRefPath, {}];
          }
        }),
      );

      if (active) setBatchBookingDetailsByPath(Object.fromEntries(details));
    };

    loadBatchBookingDetails();

    return () => {
      active = false;
    };
  }, [selectedBatch]);

  const addBookingByOrder = async () => {
    if (!canManagePayments) {
      notifyWarning("Payment access is not configured for this account.");
      return;
    }

    console.log("Adding booking for order:", manualOrderRef.current);
    const order = normalizeTechnicianPaymentOrder(manualOrderRef.current);
    if (!order) {
      notifyWarning("Please enter an order ID.");
      return;
    }

    // prevent duplicates in current list
    const exists = bookingTmpList.some(
      (b) =>
        normalizeTechnicianPaymentOrder((b.extendedProps || {}).order) === order,
    );
    console.log("Exists check for order:", order, exists);
    if (exists) {
      notifyWarning(`Order ${order} is already in the list.`);
      return;
    }
    console.log("Searching booking for order:", order);

    try {
      const d = await findTechnicianPaymentBooking(db, order, {
        paymentAccess,
        scopedTechName,
      });

      if (!d) {
        notifyWarning(`No booking found for order: ${order}`);
        return;
      }

      const booking = { refPath: d.ref.path, id: d.id, ...d.data() };

      if (!hasTechnicianPaymentHn(booking)) {
        notifyWarning(
          `Order ${order} cannot be added because the patient HN is missing.`,
        );
        return;
      }

      // Optional: if you only want bookings for the selected doctor, enforce here:
      if (
        selectedTech &&
        selectedTech !== "Summary" &&
        booking.extendedProps.technician !== selectedTech &&
        booking.extendedProps.techrecord !== selectedTech
      ) {
        notifyWarning(
          `Order ${order} belongs to tech record "${
            (booking.extendedProps || {}).technician || "-"
          }" and tech score "${
            (booking.extendedProps || {}).techrecord || "-"
          }", not "${selectedTech}".`,
        );
        // return; // uncomment to block adding if doctor mismatch
        return;
      }

      // Keep Activity column aligned; put a blank or derived timestamp
      // setActivityTmpList((prev) => [
      //   ...prev,
      //   {
      //     ScoredAt: "-", // or tsToLocalString(booking.extendedProps.createdAt)
      //   },
      // ]);

      // Add to the table list
      setBookingTmpList((prev) => [...prev, booking]);

      manualOrderRef.current = "";
      notifyTopRight(`Added order ${order}.`);
    } catch (err) {
      console.error(err);
      notifyError("Failed to add booking by order.", err);
    }
  };

  // const confirmPaymentBatch = async (techKey, batchId) => {
  //   // fetch the batch doc
  //   const batchRef = doc(
  //     db,
  //     "paymentTechBatches",
  //     techKey,
  //     BATCH_SUBCOL,
  //     batchId
  //   );
  //   const snap = await getDoc(batchRef);
  //   if (!snap.exists()) throw new Error("Batch not found.");
  //   const data = snap.data();
  //   if (data.isConfirmed) throw new Error("Batch already confirmed.");

  //   const payDateTs =
  //     data.meta.payDate instanceof Timestamp
  //       ? data.meta.payDate
  //       : Timestamp.fromDate(new Date());

  //   const wb = writeBatch(db);

  //   for (const entry of data.entries || []) {
  //     const { bookingRefPath, phases = [] } = entry || {};
  //     if (!bookingRefPath || !phases.length) continue;

  //     const bRef = doc(db, bookingRefPath);
  //     const patch = {};
  //     for (const { phase } of phases) {
  //       const m = PHASE_FIELD_MAP[phase];
  //       if (!m) continue;
  //       patch[`extendedProps.${m.paidFlag}`] = true;
  //       patch[`extendedProps.${m.paidDate}`] = payDateTs;
  //       patch[`extendedProps.${m.paidBatch}`] = batchId;
  //     }
  //     wb.update(bRef, patch);
  //   }

  //   wb.update(batchRef, {
  //     isConfirmed: true,
  //     isSendEmail: false,
  //     confirmedBy: currentUser.displayName,
  //     confirmedAt: serverTimestamp(),
  //   });

  //   await wb.commit();

  //   notifyTopRight("Payment confirmed.");
  //   // refresh list
  //   await loadPendingBatches();
  //   setSelectedBatch(null);
  // };

  const confirmPaymentBatch = async (techKey, batchId) => {
    if (!paymentAccess) {
      throw new Error("Only full payment-access users can confirm payments.");
    }

    // 1️⃣ fetch batch
    const batchRef = doc(
      db,
      "paymentTechBatches",
      techKey,
      BATCH_SUBCOL,
      batchId,
    );

    const snap = await getDoc(batchRef);
    if (!snap.exists()) throw new Error("Batch not found.");

    const data = snap.data();
    if (data.isConfirmed) throw new Error("Batch already confirmed.");

    const payDateTs =
      data.meta.payDate instanceof Timestamp
        ? data.meta.payDate
        : Timestamp.fromDate(new Date());

    const wb = writeBatch(db);

    // 2️⃣ update bookings
    for (const entry of data.entries || []) {
      const { bookingRefPath, phases = [] } = entry || {};
      if (!bookingRefPath || !Array.isArray(phases) || phases.length === 0)
        continue;

      const bRef = doc(db, bookingRefPath);
      const patch = {};

      for (const { phase } of phases) {
        const m = PHASE_FIELD_MAP[phase];

        if (!m) {
          console.warn(
            "[TECH PAYMENT] Unknown phase:",
            phase,
            "booking:",
            bookingRefPath,
          );
          continue;
        }

        patch[`extendedProps.${m.paidFlag}`] = true;
        patch[`extendedProps.${m.paidDate}`] = payDateTs;
        patch[`extendedProps.${m.paidBatch}`] = batchId;
      }

      // ⚠️ only update if something valid exists
      if (Object.keys(patch).length > 0) {
        wb.update(bRef, patch);
      }
    }

    // 3️⃣ confirm batch
    wb.update(batchRef, {
      isConfirmed: true,
      isSendEmail: false,
      confirmedBy: currentUser.displayName,
      confirmedAt: serverTimestamp(),
    });

    // 4️⃣ commit
    await wb.commit();

    notifyTopRight("Payment confirmed.");
    setBatchEdit(null);
    setSelectedBatch(null);
    try {
      await loadPendingBatches();
    } catch (refreshError) {
      console.error("Unable to refresh pending payments after confirmation", refreshError);
    }
    return true;
  };

  const returnConfirmedBatchToPending = async (techKey, batchId) => {
    if (!canRollbackConfirmedBatch) {
      notifyWarning("Only full payment-access users can return a confirmed payment to pending.");
      return false;
    }

    const rollbackKey = `${techKey}/${batchId}`;
    setRollingBackBatchKey(rollbackKey);

    try {
      const batchRef = doc(
        db,
        "paymentTechBatches",
        techKey,
        BATCH_SUBCOL,
        batchId,
      );
      const batchSnapshot = await getDoc(batchRef);

      if (!batchSnapshot.exists()) throw new Error("Payment batch not found.");

      const batchData = batchSnapshot.data() || {};
      if (!batchData.isConfirmed) {
        throw new Error("This payment batch is already pending.");
      }

      const linkedBookings = new Map();
      (batchData.entries || []).forEach((entry) => {
        const bookingRefPath = entry && entry.bookingRefPath;
        if (!bookingRefPath) return;

        const linkedBooking = linkedBookings.get(bookingRefPath) || {
          ref: doc(db, bookingRefPath),
          phaseMaps: new Map(),
        };

        (entry.phases || []).forEach((phaseEntry) => {
          const phaseMap = PHASE_FIELD_MAP[phaseEntry && phaseEntry.phase];
          if (phaseMap) linkedBooking.phaseMaps.set(phaseEntry.phase, phaseMap);
        });

        if (linkedBooking.phaseMaps.size) {
          linkedBookings.set(bookingRefPath, linkedBooking);
        }
      });

      if (!linkedBookings.size) {
        throw new Error("This payment batch has no linked booking phases to return.");
      }

      const linkedBookingSnapshots = await Promise.all(
        Array.from(linkedBookings.entries()).map(
          async ([bookingRefPath, linkedBooking]) => ({
            bookingRefPath,
            linkedBooking,
            snapshot: await getDoc(linkedBooking.ref),
          }),
        ),
      );

      const conflicts = [];
      linkedBookingSnapshots.forEach(
        ({ bookingRefPath, linkedBooking, snapshot }) => {
          if (!snapshot.exists()) {
            conflicts.push(bookingRefPath);
            return;
          }

          const props = (snapshot.data() || {}).extendedProps || {};
          linkedBooking.phaseMaps.forEach((phaseMap) => {
            if (props[phaseMap.paidBatch] !== batchId) {
              conflicts.push(bookingRefPath);
            }
          });
        },
      );

      if (conflicts.length) {
        throw new Error(
          "Cannot return this batch because one or more booking phases no longer point to it.",
        );
      }

      const write = writeBatch(db);
      linkedBookings.forEach((linkedBooking) => {
        const patch = {};
        linkedBooking.phaseMaps.forEach((phaseMap) => {
          patch[`extendedProps.${phaseMap.paidFlag}`] = false;
          patch[`extendedProps.${phaseMap.paidDate}`] = null;
          patch[`extendedProps.${phaseMap.paidBatch}`] = null;
        });
        write.update(linkedBooking.ref, patch);
      });

      write.update(batchRef, {
        isConfirmed: false,
        isSendEmail: false,
        confirmedBy: null,
        confirmedAt: null,
      });

      await write.commit();

      setReadyToEmail((current) =>
        current.filter(
          (batch) => !(batch.id === batchId && batch.techKey === techKey),
        ),
      );
      if (
        selectedBatch &&
        selectedBatch.id === batchId &&
        selectedBatch.techKey === techKey
      ) {
        setSelectedBatch(null);
      }
      setActiveTab("pending");

      try {
        await Promise.all([loadPendingBatches(), loadReadyToEmailForTech()]);
      } catch (refreshError) {
        console.error("Unable to refresh payment batches after rollback", refreshError);
      }
      notifyTopRight("Confirmed payment returned to Pending Payments.");
      return true;
    } catch (error) {
      console.error("Unable to return confirmed payment to pending", error);
      notifyError("Return to pending failed", error.message || error);
      return false;
    } finally {
      setRollingBackBatchKey("");
    }
  };

  const requestBatchConfirmation = (action, techKey, batchId) => {
    if (action === "confirm" && !paymentAccess) {
      notifyWarning("Only full payment-access users can confirm payments.");
      return;
    }
    if (action === "return" && !canRollbackConfirmedBatch) {
      notifyWarning("Only full payment-access users can return a confirmed payment to pending.");
      return;
    }

    setPendingBatchConfirmation({ action, techKey, batchId });
  };

  const closeBatchConfirmation = () => {
    if (!processingBatchConfirmation) setPendingBatchConfirmation(null);
  };

  const confirmBatchConfirmation = async () => {
    if (!pendingBatchConfirmation) return;

    setProcessingBatchConfirmation(true);
    try {
      const { action, techKey, batchId } = pendingBatchConfirmation;
      const completed =
        action === "return"
          ? await returnConfirmedBatchToPending(techKey, batchId)
          : await confirmPaymentBatch(techKey, batchId);

      if (completed) setPendingBatchConfirmation(null);
    } catch (error) {
      console.error("Unable to confirm payment batch action", error);
      notifyError("Payment update failed", error.message || error);
    } finally {
      setProcessingBatchConfirmation(false);
    }
  };

  const openBatchForReview = (batch) => {
    if (!canAccessBatch(batch)) return;
    setBatchEdit(null);
    setSelectedBatch(batch);
  };

  const startBatchEdit = (batch) => {
    if (!canEditPendingBatch(batch)) return;

    const meta = batch.meta || {};
    const originalAmount = Math.max(0, toNumber(meta.amount));
    const originalWht = Math.max(0, toNumber(meta.wht));

    if (batchEditTransportFileInputRef.current) {
      batchEditTransportFileInputRef.current.value = "";
    }
    setSelectedBatch(batch);
    setBatchEdit({
      id: batch.id,
      techKey: batch.techKey,
      entries: (batch.entries || []).map((entry) => ({
        ...entry,
        phases: (entry.phases || []).map((phase) => ({ ...phase })),
      })),
      whtRate: originalAmount > 0 ? originalWht / originalAmount : 0,
      guaranteeCase: Math.max(0, toNumber(meta.guranteeCase)),
      deduction: String(meta.deduction || 0),
      transport: String(meta.transport || 0),
      adjustmentPay: String(meta.adjustmentPay || 0),
      payDate: timestampToDateInput(meta.payDate),
      payNote: meta.payNote || "",
      transportFiles: getBatchTransportFiles(meta),
      newTransportFiles: [],
      removedTransportFileKeys: [],
    });
  };

  const updateBatchEditPhaseAmount = (entryIndex, phaseIndex, value) => {
    setBatchEdit((previous) => {
      if (!previous) return previous;

      return {
        ...previous,
        entries: previous.entries.map((entry, currentEntryIndex) => {
          if (currentEntryIndex !== entryIndex) return entry;

          return {
            ...entry,
            phases: (entry.phases || []).map((phase, currentPhaseIndex) =>
              currentPhaseIndex === phaseIndex
                ? { ...phase, amount: value }
                : phase,
            ),
          };
        }),
      };
    });
  };

  const getBatchEditTotals = (edit) => {
    const amount = (edit.entries || []).reduce(
      (entryTotal, entry) =>
        entryTotal +
        (entry.phases || []).reduce(
          (phaseTotal, phase) =>
            phaseTotal + Math.max(0, toNumber(phase.amount)),
          0,
        ),
      0,
    );
    const wht = amount * Math.max(0, toNumber(edit.whtRate));
    const guaranteeCase = Math.max(0, toNumber(edit.guaranteeCase));
    const deductionAmount = Math.max(0, toNumber(edit.deduction));
    const transportAmount = Math.max(0, toNumber(edit.transport));
    const adjustmentAmount = Math.max(0, toNumber(edit.adjustmentPay));

    return {
      amount,
      wht,
      guaranteeCase,
      deductionAmount,
      transportAmount,
      adjustmentAmount,
      totalPay: Math.max(
        0,
        amount -
          wht -
          guaranteeCase -
          deductionAmount +
          transportAmount +
          adjustmentAmount,
      ),
    };
  };

  const saveBatchEdit = async () => {
    if (!selectedBatch || !batchEdit || batchEdit.id !== selectedBatch.id)
      return;
    if (!canEditPendingBatch(selectedBatch)) {
      notifyError("Update payment failed", "You do not have access to this draft.");
      return;
    }

    if (!batchEdit.payDate) {
      notifyWarning("Please choose a pay date.");
      return;
    }

    const entries = (batchEdit.entries || []).map((entry) => ({
      ...entry,
      phases: (entry.phases || []).map((phase) => ({
        ...phase,
        amount: Math.max(0, toNumber(phase.amount)),
      })),
    }));

    if (!entries.length || !entries.some((entry) => entry.phases.length)) {
      notifyWarning("A payment batch needs at least one payment phase.");
      return;
    }

    const totals = getBatchEditTotals({ ...batchEdit, entries });
    const batchRef = doc(
      db,
      "paymentTechBatches",
      batchEdit.techKey,
      BATCH_SUBCOL,
      batchEdit.id,
    );

    setSavingBatchEdit(true);
    let uploadedTransportFiles = [];
    let removedTransportFiles = [];
    let nextTransportFiles = [];
    let batchUpdated = false;
    try {
      const snapshot = await getDoc(batchRef);
      if (!snapshot.exists()) throw new Error("Batch not found.");
      const currentBatch = snapshot.data() || {};
      if (currentBatch.isConfirmed)
        throw new Error("Confirmed batches cannot be edited.");

      const previousTransportFiles = getBatchTransportFiles(
        currentBatch.meta || {},
      );
      const removedTransportFileKeys = new Set(
        batchEdit.removedTransportFileKeys || [],
      );
      removedTransportFiles = previousTransportFiles.filter((file) =>
        removedTransportFileKeys.has(getTransportFileKey(file)),
      );
      const retainedTransportFiles = previousTransportFiles.filter(
        (file) => !removedTransportFileKeys.has(getTransportFileKey(file)),
      );

      for (const file of batchEdit.newTransportFiles || []) {
        uploadedTransportFiles.push(
          await uploadTransportFile(file, batchEdit.techKey, batchEdit.id),
        );
      }
      nextTransportFiles = uniqueTransportFiles([
        ...retainedTransportFiles,
        ...uploadedTransportFiles,
      ]);
      const hasTransportFileChanges =
        uploadedTransportFiles.length > 0 || removedTransportFiles.length > 0;

      const batchUpdate = {
        entries,
        "meta.amount": totals.amount,
        "meta.wht": totals.wht,
        "meta.guranteeCase": totals.guaranteeCase,
        "meta.deduction": totals.deductionAmount,
        "meta.transport": totals.transportAmount,
        "meta.adjustmentPay": totals.adjustmentAmount,
        "meta.totalPay": totals.totalPay,
        "meta.payNote": batchEdit.payNote.trim(),
        "meta.payDate": Timestamp.fromDate(
          new Date(`${batchEdit.payDate}T00:00:00`),
        ),
      };
      if (hasTransportFileChanges) {
        if (nextTransportFiles.length) {
          batchUpdate["meta.transportFiles"] = nextTransportFiles;
          // Keep the first file in the legacy field for older payment batches.
          batchUpdate["meta.transportFile"] = nextTransportFiles[0];
        } else {
          batchUpdate["meta.transportFiles"] = deleteField();
          batchUpdate["meta.transportFile"] = deleteField();
        }
      }

      await updateDoc(batchRef, batchUpdate);
      batchUpdated = true;

      if (removedTransportFiles.length) {
        await removeTransportFilesFromStorage(
          removedTransportFiles,
          "Unable to remove a deleted transport file.",
        );
      }

      const updatedBatch = {
        ...selectedBatch,
        entries,
        meta: {
          ...(selectedBatch.meta || {}),
          amount: totals.amount,
          wht: totals.wht,
          guranteeCase: totals.guaranteeCase,
          deduction: totals.deductionAmount,
          transport: totals.transportAmount,
          adjustmentPay: totals.adjustmentAmount,
          totalPay: totals.totalPay,
          payNote: batchEdit.payNote.trim(),
          payDate: Timestamp.fromDate(new Date(`${batchEdit.payDate}T00:00:00`)),
          ...(hasTransportFileChanges
            ? {
                transportFiles: nextTransportFiles,
                transportFile: nextTransportFiles[0] || null,
              }
            : {}),
        },
      };

      setSelectedBatch(updatedBatch);
      setPendingBatches((previous) =>
        previous.map((batch) =>
          batch.id === updatedBatch.id ? updatedBatch : batch,
        ),
      );
      if (batchEditTransportFileInputRef.current) {
        batchEditTransportFileInputRef.current.value = "";
      }
      setBatchEdit(null);
      await loadPendingBatches();
      notifyTopRight("Draft payment updated.");
    } catch (error) {
      if (!batchUpdated && uploadedTransportFiles.length) {
        await removeTransportFilesFromStorage(
          uploadedTransportFiles,
          "Unable to clean up transport files after a failed draft update.",
        );
      }
      console.error("Update payment draft failed", error);
      notifyError("Update payment failed", error.message || error);
    } finally {
      setSavingBatchEdit(false);
    }
  };

  const editingSelectedBatch =
    !!selectedBatch && !!batchEdit && batchEdit.id === selectedBatch.id;
  const batchEditTotals = editingSelectedBatch
    ? getBatchEditTotals(batchEdit)
    : null;
  const reviewBatchEntries = selectedBatch
    ? editingSelectedBatch
      ? batchEdit.entries || []
      : selectedBatch.entries || []
    : [];
  const reviewBatchPhaseCounts = getTechnicianPaymentPhaseCounts(
    reviewBatchEntries,
  );
  const reviewRecordOtherTooltip =
    formatTechnicianPaymentOtherCaseBreakdown(
      reviewBatchPhaseCounts.recordOtherTypes,
    );

  const handleDeleteBatch = async (batchId) => {
    if (!paymentAccess) {
      notifyWarning("Only full payment-access users can delete payment drafts.");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this draft payment?"))
      return;

    console.log(selectedTech);
    console.log("Deleting batch:", batchId);

    const techInfo = technicianList.find((doc) => doc.name === selectedTech);
    if (!techInfo) return;

    const techKey = techInfo.techId || techInfo.id;

    try {
      await deleteDoc(
        doc(db, "paymentTechBatches", techKey, "batches", batchId),
      );
      notifyTopRight("Draft payment deleted");

      // Refresh list or remove from state
      setPendingBatches((prev) => prev.filter((b) => b.id !== batchId));
      if (selectedBatch && selectedBatch.id === batchId) {
        setBatchEdit(null);
        setSelectedBatch(null);
      }
    } catch (err) {
      console.error("Delete failed", err);
      notifyError("Delete failed", err.message || err);
    }
  };

  const getRate = (tech, bookingRow, phase, caseIndex = 1) => {
    if (!tech) return 0;

    const externalScoreRate = getFullTimeExternalScoreRate(tech, bookingRow);
    if (phase === "score" && externalScoreRate !== null) {
      return externalScoreRate;
    }

    const ep = bookingRow.extendedProps || {};
    const isCash = ep.healthInsurance === "ชำระเงินเอง";
    const isSplitCase = [
      "Split Night Sleep Study",
      "CPAP Titration",
      "CPAP Titration Study",
    ].includes(ep.type);
    const isHomeCase = ["Home Full Night Sleep Study"].includes(ep.type);
    const isOPOCase = ["Overnight Pulse Oximetry Study"].includes(ep.type);

    const fields = {
      record: isCash ? "recordCashRate" : "recordSocialSecurityRate",
      score: isCash ? "scoreCashRate" : "scoreSocialSecurityRate",
    };

    // ✅ Base key
    const key = fields[phase];
    let rate = parseNum(tech[key]);

    if (phase == "record" && isSplitCase) {
      rate = rate + parseNum(tech["splitNightTopupRate"]);
    }
    if (phase == "record" && isHomeCase) {
      if (caseIndex === 1) {
        rate = tech.homeFirstCaseRate;
      } else {
        rate = tech.homeSecondCaseRate;
      }
    }
    if (phase == "record" && isOPOCase) {
      if (caseIndex === 1) {
        rate = tech.overnightFirstCaseRate;
      } else {
        rate = tech.overnightSecondCaseRate;
      }
    }
    if (phase == "record" && ep.node === "Royal Phnom Penh Hospital") {
      if (ep.type == "Full Night Sleep Study") {
        rate = tech.RPHFullnightFirstCaseRate;
      } else {
        rate = tech.RPHSplitSleepFirstCaseRate;
      }
    }

    return Number(rate);
  };

  const getPatientNoteStatusForBatchEntry = async (hn) => {
    const patientHn = String(hn || "").trim();
    if (!patientHn) {
      return {
        sleepStudyForm: false,
        consentForm: false,
        sleepStudyFormNotes: [],
        consentFormNotes: [],
      };
    }

    if (patientNoteStatusByHn[patientHn]) {
      return patientNoteStatusByHn[patientHn];
    }

    const noteSnapshot = await getDocs(
      collection(db, "patients", patientHn, "note"),
    );
    const notes = noteSnapshot.docs.map((noteDoc) => {
      const note = noteDoc.data() || {};
      return {
        id: noteDoc.id,
        hn: String(note.hn || patientHn).trim(),
        title: note.title || "",
        detail: note.detail || "",
        filename: Array.isArray(note.filename)
          ? note.filename.filter(Boolean)
          : note.filename
            ? [note.filename]
            : [],
        normalizedTitle: normalizePatientNoteTitle(note.title),
      };
    });
    const sleepStudyFormNotes = notes.filter(
      (note) =>
        note.normalizedTitle ===
        PATIENT_NOTE_TYPES.sleepStudyForm.normalizedTitle,
    );
    const consentFormNotes = notes.filter(
      (note) =>
        note.normalizedTitle === PATIENT_NOTE_TYPES.consentForm.normalizedTitle,
    );
    const status = {
      sleepStudyForm: sleepStudyFormNotes.length > 0,
      consentForm: consentFormNotes.length > 0,
      sleepStudyFormNotes,
      consentFormNotes,
    };

    setPatientNoteStatusByHn((previous) => ({
      ...previous,
      [patientHn]: status,
    }));

    return status;
  };

  const removeBatchEditEntry = (entryIndex) => {
    setBatchEdit((previous) => {
      if (!previous) return previous;

      return {
        ...previous,
        entries: (previous.entries || []).filter(
          (_, currentIndex) => currentIndex !== entryIndex,
        ),
      };
    });
  };

  const addBookingToBatchEdit = async (rawOrder) => {
    if (!selectedBatch || !batchEdit || batchEdit.id !== selectedBatch.id) {
      return false;
    }
    if (!canEditPendingBatch(selectedBatch)) {
      notifyWarning("You do not have access to edit this draft payment.");
      return false;
    }

    const order = normalizeTechnicianPaymentOrder(rawOrder);
    if (!order) {
      notifyWarning("Please enter an order ID.");
      return false;
    }

    if (
      (batchEdit.entries || []).some(
        (entry) => normalizeTechnicianPaymentOrder(entry.order) === order,
      )
    ) {
      notifyWarning(`Order ${order} is already in this draft.`);
      return false;
    }

    const batchTechId = String(selectedBatch.techId || "").trim();
    const batchTechnician = technicianList.find((technician) => {
      const technicianKey = technicianKeyFrom(technician, technician.name);
      return (
        technicianKey === batchEdit.techKey ||
        (batchTechId &&
          (technician.techId === batchTechId ||
            technician.id === batchTechId ||
            technician.name === batchTechId))
      );
    });
    if (!batchTechnician || !batchTechnician.name) {
      notifyError(
        "Unable to add case",
        "The technician for this payment batch could not be found.",
      );
      return false;
    }

    try {
      const bookingDoc = await findTechnicianPaymentBooking(db, order, {
        paymentAccess,
        scopedTechName,
      });

      if (!bookingDoc) {
        notifyWarning(`No booking found for order: ${order}`);
        return false;
      }

      const booking = {
        refPath: bookingDoc.ref.path,
        id: bookingDoc.id,
        ...bookingDoc.data(),
      };
      const props = booking.extendedProps || {};
      const isRecordTechnician = props.techrecord === batchTechnician.name;
      const isScoreTechnician = props.technician === batchTechnician.name;

      if (!isRecordTechnician && !isScoreTechnician) {
        notifyWarning(
          `Order ${order} is assigned to Record ${
            props.techrecord || "-"
          } and Score ${props.technician || "-"}, not ${
            batchTechnician.name
          }.`,
        );
        return false;
      }

      let recordNotesReady = true;
      if (isRecordTechnician && technicianRecordNoteCheckEnabled) {
        if (technicianRecordNoteCheckLoading) {
          notifyWarning("Required patient notes are still loading. Please try again.");
          return false;
        }

        const noteStatus = await getPatientNoteStatusForBatchEntry(props.hn);
        recordNotesReady = Boolean(
          noteStatus.sleepStudyForm && noteStatus.consentForm,
        );
      }

      const blockedScoreStatuses = [
        "Sleep Test Scheduled",
        "Transferred To Center",
        "Assigned",
      ];
      const phases = [];
      const currentRow = bookingWithCaseIndex.find(
        (row) => row.refPath === booking.refPath,
      );
      const matchingDraftRecordCases = (batchEdit.entries || []).filter(
        (entry) =>
          entry.techrecord === batchTechnician.name &&
          entry.latestSleepStudyDate === props.latestSleepStudyDate &&
          (entry.phases || []).some((phase) => phase.phase === "record"),
      ).length;
      const recordCaseIndex = currentRow
        ? currentRow.caseIndex
        : matchingDraftRecordCases + 1;

      if (isRecordTechnician && !props.isPaidRecord && recordNotesReady) {
        phases.push({
          phase: "record",
          amount: getRate(
            batchTechnician,
            booking,
            "record",
            recordCaseIndex,
          ),
        });
      }
      if (
        isScoreTechnician &&
        !props.isPaidScore &&
        !blockedScoreStatuses.includes(props.status)
      ) {
        phases.push({
          phase: "score",
          amount: getRate(batchTechnician, booking, "score"),
        });
      }

      if (!phases.length) {
        const noteMessage =
          isRecordTechnician && !recordNotesReady
            ? " Record payment requires Sleep Study Form and Consent Form."
            : "";
        notifyWarning(
          `No unpaid eligible payment phase is available for order ${order}.${noteMessage}`,
        );
        return false;
      }

      const entry = {
        order,
        bookingRefPath: booking.refPath,
        type: props.type || "",
        hn: props.hn || "",
        node: props.node || "",
        customer: props.customer || "",
        firstname: props.firstname || "",
        lastname: props.lastname || "",
        healthInsurance: props.healthInsurance || "",
        latestSleepStudyDate: props.latestSleepStudyDate || "",
        status: props.status || "",
        technician: props.technician || "",
        techrecord: props.techrecord || "",
        paid: {
          record: {
            isPaid: Boolean(props.isPaidRecord),
            paidDate: props.paidRecordDate || null,
            batchId: props.paidRecordBatchId || null,
          },
          score: {
            isPaid: Boolean(props.isPaidScore),
            paidDate: props.paidScoreDate || null,
            batchId: props.paidScoreBatchId || null,
          },
        },
        phases,
      };

      setBatchEdit((previous) => {
        if (!previous || previous.id !== batchEdit.id) return previous;
        if (
          (previous.entries || []).some(
            (existingEntry) =>
              normalizeTechnicianPaymentOrder(existingEntry.order) === order,
          )
        ) {
          return previous;
        }

        return {
          ...previous,
          entries: [...(previous.entries || []), entry],
        };
      });
      setBatchBookingDetailsByPath((previous) => ({
        ...previous,
        [booking.refPath]: {
          latestSleepStudyDate: props.latestSleepStudyDate || "",
          status: props.status || "",
        },
      }));
      notifyTopRight(`Added order ${order} to this draft.`);
      return true;
    } catch (error) {
      console.error("Unable to add booking to payment draft", error);
      notifyError("Unable to add case", error.message || error);
      return false;
    }
  };

  const removeBooking = (order) => {
    // setActivityTmpList((prev) => prev.filter((row) => row.Order !== order));

    setBookingTmpList((prev) =>
      prev.filter((row) => row.extendedProps.order !== order),
    );
    // also remove from payThisRound selection
    setPayThisRound((prev) => {
      const updated = { ...prev };
      delete updated[order];
      return updated;
    });
  };

  // const eligible = (row, phase) => {
  //   const ep = row.extendedProps || {};
  //   if (phase === "record") return !ep.isPaidRecord;
  //   if (phase === "score") return !ep.isPaidScore;
  //   return false;
  // };

  // const eligible = (row, phase) => {
  //   const ep = row.extendedProps || {};

  //   if (phase === "record") {
  //     // Not eligible if already paid
  //     if (ep.isPaidRecord) return false;

  //     // Not eligible if technician doesn't match
  //     if (ep.techrecord !== selectedTech) return false;

  //     return true;
  //   }

  //   if (phase === "score") {
  //     // Not eligible if already paid
  //     if (ep.isPaidScore) return false;

  //     // Not eligible if technician doesn't match
  //     if (ep.technician !== selectedTech) return false;

  //     return true;
  //   }

  //   return false;
  // };

  const getPatientNoteStatus = React.useCallback(
    (row) => patientNoteStatusByHn[getPatientHn(row)] || null,
    [patientNoteStatusByHn],
  );

  const hasRequiredRecordNotes = React.useCallback(
    (row) => {
      if (!technicianRecordNoteCheckEnabled) return true;
      if (
        technicianRecordNoteCheckLoading ||
        loadingPatientNoteStatuses
      ) {
        return false;
      }

      const noteStatus = getPatientNoteStatus(row);
      return Boolean(
        noteStatus && noteStatus.sleepStudyForm && noteStatus.consentForm,
      );
    },
    [
      getPatientNoteStatus,
      loadingPatientNoteStatuses,
      technicianRecordNoteCheckEnabled,
      technicianRecordNoteCheckLoading,
    ],
  );

  const getRecordPaymentBlockReason = React.useCallback(
    (row) => {
      if (!technicianRecordNoteCheckEnabled) return "";
      if (
        technicianRecordNoteCheckLoading ||
        loadingPatientNoteStatuses
      ) {
        return "Checking required patient notes…";
      }

      const hn = getPatientHn(row);
      if (!hn) return "Patient HN is missing.";

      const noteStatus = getPatientNoteStatus(row);
      const missing = [];
      if (!noteStatus || !noteStatus.sleepStudyForm) {
        missing.push("Sleep Study Form");
      }
      if (!noteStatus || !noteStatus.consentForm) {
        missing.push("Consent Form");
      }

      return missing.length
        ? `Record payment requires: ${missing.join(" and ")}.`
        : "";
    },
    [
      getPatientNoteStatus,
      loadingPatientNoteStatuses,
      technicianRecordNoteCheckEnabled,
      technicianRecordNoteCheckLoading,
    ],
  );

  const eligible = React.useCallback(
    (row, phase) => {
      const ep = row.extendedProps || {};

      // List of statuses that block eligibility
      const blockedStatuses = [
        "Sleep Test Scheduled",
        "Transferred To Center",
        "Assigned",
      ];

      if (phase === "record") {
        if (ep.isPaidRecord) return false;
        if (ep.techrecord !== selectedTech) return false;
        if (!hasRequiredRecordNotes(row)) return false;

        return true;
      }

      if (phase === "score") {
        if (ep.isPaidScore) return false;
        if (blockedStatuses.includes(ep.status)) return false;
        if (ep.technician !== selectedTech) return false;

        return true;
      }

      return false;
    },
    [hasRequiredRecordNotes, selectedTech],
  );

  const paymentBookingList = React.useMemo(
    () => bookingTmpList.filter(hasTechnicianPaymentHn),
    [bookingTmpList],
  );

  // Compute header checkbox states (checked / indeterminate)
  const headerState = React.useMemo(() => {
    const phases = ["record", "score"];
    const out = {};
    phases.forEach((phase) => {
      const eligOrders = paymentBookingList
        .filter((row) => eligible(row, phase))
        .map((row) => (row.extendedProps || {}).order)
        .filter(Boolean);

      if (eligOrders.length === 0) {
        out[phase] = { checked: false, indeterminate: false, disabled: true };
        return;
      }

      let selectedCount = 0;
      eligOrders.forEach((ord) => {
        const sel = (payThisRound[ord] || {})[phase];
        if (sel) selectedCount++;
      });

      out[phase] = {
        checked: selectedCount === eligOrders.length,
        indeterminate: selectedCount > 0 && selectedCount < eligOrders.length,
        disabled: false,
      };
    });
    return out;
  }, [paymentBookingList, payThisRound, eligible]);

  React.useEffect(() => {
    if (recordAllRef.current)
      recordAllRef.current.indeterminate =
        headerState.record.indeterminate || false;
    if (scoreAllRef.current)
      scoreAllRef.current.indeterminate =
        headerState.score.indeterminate || false;
  }, [headerState]);

  useEffect(() => {
    setPayThisRound((previous) => {
      let changed = false;
      const next = { ...previous };

      paymentBookingList.forEach((row) => {
        const order = (row.extendedProps || {}).order;
        const selected = next[order];
        if (!order || !selected || !selected.record || eligible(row, "record")) {
          return;
        }

        next[order] = { ...selected, record: false };
        changed = true;
      });

      return changed ? next : previous;
    });
  }, [paymentBookingList, eligible]);

  // Toggle all for a phase
  const toggleAllPhase = (phase) => (e) => {
    const checked = e.target.checked;

    let recordCount = 0;
    let scoreCount = 0;

    setPayThisRound((prev) => {
      const next = { ...prev };
      paymentBookingList.forEach((row) => {
        if (!eligible(row, phase)) return; // skip already-paid rows
        const ord = (row.extendedProps || {}).order;
        if (!ord) return;
        const cur = next[ord] || {
          record: false,
          score: false,
        };
        next[ord] = { ...cur, [phase]: checked };
      });

      return next;
    });
  };

  const countPhases = (payThisRound) => {
    let recordCount = 0;
    let scoreCount = 0;

    Object.values(payThisRound).forEach((item) => {
      if (item.record) recordCount++;
      if (item.score) scoreCount++;
    });

    return { recordCount, scoreCount };
  };

  const loadPendingBatches = async () => {
    const targetTechName = paymentAccess ? selectedTech : scopedTechName;

    if (!canManagePayments || !targetTechName) {
      setPendingBatches([]);
      return;
    }

    if (paymentAccess && targetTechName === "Summary") {
      setLoadingPending(true);

      try {
        const results = await Promise.all(
          technicianList.map(async (tech) => {
            const techKey = technicianKeyFrom(tech, tech.name);

            const qRef = query(
              collection(db, "paymentTechBatches", techKey, BATCH_SUBCOL),
              where("isConfirmed", "==", false),
              orderBy("createdAt", "desc"),
            );

            const snap = await getDocs(qRef);

            return snap.docs.map((d) => ({
              id: d.id,
              techKey,
              ...d.data(),
              refPath: d.ref.path, // keep for direct updates
            }));
          }),
        );

        // Keep legacy drafts accurate too: older batches did not snapshot type.
        setPendingBatches(await hydratePaymentBatchStudyTypes(results.flat()));
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingPending(false);
      }
    } else {
      const tech = technicianList.find((d) => d.name === targetTechName);
      if (!tech) {
        setPendingBatches([]);
        return;
      }
      const techKey = technicianKeyFrom(tech, targetTechName);
      setLoadingPending(true);
      try {
        const qRef = query(
          collection(db, "paymentTechBatches", techKey, BATCH_SUBCOL),
          where("isConfirmed", "==", false),
          orderBy("createdAt", "desc"),
        );
        const snap = await getDocs(qRef);
        const items = snap.docs.map((d) => ({
          id: d.id,
          techKey,
          ...d.data(),
          refPath: d.ref.path, // keep for direct updates
        }));
        setPendingBatches(await hydratePaymentBatchStudyTypes(items));
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingPending(false);
      }
    }
  };

  const loadReadyToEmailForTech = async () => {
    const targetTechName = paymentAccess ? selectedTech : scopedTechName;

    if (!canViewConfirmedPayments || !targetTechName) {
      setReadyToEmail([]);
      return;
    }

    if (paymentAccess && targetTechName === "Summary") {
      setLoadingReady(true);

      try {
        const results = await Promise.all(
          technicianList.map(async (tech) => {
            const techKey = technicianKeyFrom(tech, tech.name);

            const qRef = query(
              collection(db, "paymentTechBatches", techKey, BATCH_SUBCOL),
              where("isConfirmed", "==", true),
              // where("isSendEmail", "==", false),
              orderBy("createdAt", "desc"),
            );

            const snap = await getDocs(qRef);

            return snap.docs.map((d) => ({
              id: d.id,
              techKey,
              ...d.data(),
              refPath: d.ref.path, // keep for direct updates
            }));
          }),
        );

        // flatten
        setReadyToEmail(results.flat());
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingReady(false);
      }
    } else {
      const tech = technicianList.find((d) => d.name === targetTechName);
      if (!tech) {
        setReadyToEmail([]);
        return;
      }
      const techKey = technicianKeyFrom(tech, targetTechName);

      setLoadingReady(true);
      try {
        const qRef = query(
          collection(db, "paymentTechBatches", techKey, "batches"),
          where("isConfirmed", "==", true),
          // where("isSendEmail", "==", false),
          orderBy("createdAt", "desc"),
        );
        const snap = await getDocs(qRef);
        const items = snap.docs.map((d) => ({
          id: d.id,
          techKey,
          refPath: d.ref.path, // keep for direct updates
          ...d.data(),
        }));
        setReadyToEmail(items);
      } finally {
        setLoadingReady(false);
      }
    }
  };

  useEffect(() => {
    if (paymentScopeLoading) return undefined;

    // Only all-technician scopes may use collection-wide overview queries.
    const hasFullOverviewAccess = canViewAllTechnicians;

    const fetchData = async () => {
      setLoading(true);

      const fetchTechnicianList = async () => {
        const q = hasFullOverviewAccess
          ? query(
              collection(db, "sleeptechnicians"),
              where("status", "==", "Available"),
              //   orderBy("fullname", "desc")
            )
          : query(
              collection(db, "sleeptechnicians"),
              where("name", "==", scopedTechName),
            );
        const querySnapshot = await getDocs(q);
        const data = [];
        querySnapshot.forEach((doc) => {
          data.push({ id: doc.id, ...doc.data() });
        });
        const availableTechnicians = data.filter(
          (technician) => technician.status === "Available",
        );
        availableTechnicians.sort((a, b) =>
          String(a.fullname || a.name || "").localeCompare(
            String(b.fullname || b.name || ""),
          ),
        );
        // console.log("Technician List: ", data);
        setTechnicianList(availableTechnicians);
      };

      const fetchNodeCodeByName = async () => {
        try {
          const querySnapshot = await getDocs(collection(db, "nodes"));
          const data = {};

          querySnapshot.forEach((doc) => {
            const node = doc.data() || {};
            const nodeName = (node.nodename || "").trim();
            const nodeCode = (node.nodecode || "").trim();
            if (nodeName && nodeCode) {
              data[nodeName] = nodeCode;
              data[nodeName.toLowerCase()] = nodeCode;
            }
          });

          setNodeCodeByName(data);
        } catch (err) {
          console.warn("Unable to load node codes for dashboard badges", err);
          setNodeCodeByName({});
        }
      };

      const fetchActivityList = async () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth();
        /////////////////////////////////////////////////////////////////////////////////////
        // Get string date for query booking in this month
        const startDate = new Date(year, month, 1, 0, 0, 0);
        const formattedStart = formatDate(startDate);

        const endDate = new Date(year, month + 1, 0, 23, 59, 59);
        const formattedEnd = formatDate(endDate);

        if (!hasFullOverviewAccess) {
          if (!scopedTechName) {
            setActivityList([]);
            setScoredList([]);
            setAllActivityList([]);
            return;
          }

          // Restricted users load only booking documents that identify their
          // configured technician name. Do not fetch other technicians' cases
          // and filter them later in the browser.
          const bookingsRef = collectionGroup(db, "bookings");
          const [technicianSnapshot, techRecordSnapshot] = await Promise.all([
            getDocs(
              query(
                bookingsRef,
                where("extendedProps.technician", "==", scopedTechName),
              ),
            ),
            getDocs(
              query(
                bookingsRef,
                where("extendedProps.techrecord", "==", scopedTechName),
              ),
            ),
          ]);
          const bookingByPath = new Map();
          [technicianSnapshot, techRecordSnapshot].forEach((snapshot) => {
            snapshot.forEach((bookingDoc) => {
              bookingByPath.set(bookingDoc.ref.path, {
                id: bookingDoc.id,
                ...bookingDoc.data(),
              });
            });
          });
          const ownBookings = Array.from(bookingByPath.values());
          const timestampToMillis = (value) => {
            if (value && typeof value.toMillis === "function")
              return value.toMillis();
            if (value instanceof Date) return value.getTime();
            return null;
          };

          const data = ownBookings
            .filter((booking) => {
              const bookingDate =
                (booking.extendedProps || {}).latestSleepStudyDate || "";
              return bookingDate >= formattedStart && bookingDate <= formattedEnd;
            })
            .sort((a, b) =>
              String(
                (b.extendedProps || {}).latestSleepStudyDate || "",
              ).localeCompare(
                String((a.extendedProps || {}).latestSleepStudyDate || ""),
              ),
            );
          setActivityList(data);

          const startInDate = Timestamp.fromDate(
            new Date(year, month, 1, 0, 0, 0),
          );
          const endInDate = Timestamp.fromDate(
            new Date(year, month + 1, 0, 23, 59, 59),
          );
          const ownOrders = [
            ...new Set(
              ownBookings
                .map((booking) => (booking.extendedProps || {}).order)
                .filter(Boolean),
            ),
          ];
          const activitySnapshots = await Promise.all(
            Array.from(
              { length: Math.ceil(ownOrders.length / 10) },
              (_, index) => ownOrders.slice(index * 10, index * 10 + 10),
            ).map((orderChunk) =>
              getDocs(
                query(
                  collection(db, "activities"),
                  where("order", "in", orderChunk),
                ),
              ),
            ),
          );
          const startMs = startInDate.toMillis();
          const endMs = endInDate.toMillis();
          const data2 = [];
          activitySnapshots.forEach((snapshot) => {
            snapshot.forEach((activityDoc) => {
              const activity = { id: activityDoc.id, ...activityDoc.data() };
              const activityMs = timestampToMillis(activity.createdAt);
              if (
                ["Technician Scored", "Assigned to Doctor"].includes(
                  activity.activity,
                ) &&
                activityMs !== null &&
                activityMs >= startMs &&
                activityMs <= endMs
              ) {
                data2.push(activity);
              }
            });
          });

          const byOrder = new Map();
          data2.forEach((item) => {
            const ord =
              item.order ||
              (item.extendedProps && item.extendedProps.order) ||
              "";
            if (!ord) return;

            const createdMs = timestampToMillis(item.createdAt) || -Infinity;
            if (!byOrder.has(ord)) {
              byOrder.set(ord, {
                tech: null,
                techMs: -Infinity,
                assigned: null,
                assignedMs: -Infinity,
              });
            }

            const slot = byOrder.get(ord);
            if (item.activity === "Technician Scored" && createdMs > slot.techMs) {
              slot.tech = item;
              slot.techMs = createdMs;
            } else if (
              item.activity === "Assigned to Doctor" &&
              createdMs > slot.assignedMs
            ) {
              slot.assigned = item;
              slot.assignedMs = createdMs;
            }
          });
          const uniqueData2 = [];
          byOrder.forEach((slot) => {
            if (slot.tech) uniqueData2.push(slot.tech);
            else if (slot.assigned) uniqueData2.push(slot.assigned);
          });
          setScoredList(uniqueData2);

          const prevMonth = new Date(year, month - 1, 1);
          const startDatePrevMonth = Timestamp.fromDate(
            new Date(prevMonth.getFullYear(), prevMonth.getMonth(), 1, 0, 0, 0),
          );
          const endDateCurrentMonth = Timestamp.fromDate(
            new Date(year, month + 1, 0, 23, 59, 59),
          );
          const rangeStartMs = startDatePrevMonth.toMillis();
          const rangeEndMs = endDateCurrentMonth.toMillis();
          setAllActivityList(
            ownBookings.filter((booking) => {
              const bookingStartMs = timestampToMillis(booking.start);
              return (
                bookingStartMs !== null &&
                bookingStartMs >= rangeStartMs &&
                bookingStartMs <= rangeEndMs
              );
            }),
          );
          return;
        }

        const q = query(
          collectionGroup(db, "bookings"),
          where("extendedProps.latestSleepStudyDate", ">=", formattedStart),
          where("extendedProps.latestSleepStudyDate", "<=", formattedEnd),
          orderBy("extendedProps.latestSleepStudyDate", "desc"),
        );

        /////////////////////////////////////////////////////////////////////////////////////
        // Get date for query activities status Technician Scored in this month
        const startInDate = Timestamp.fromDate(
          new Date(year, month, 1, 0, 0, 0),
        );

        const endInDate = Timestamp.fromDate(
          new Date(year, month + 1, 0, 23, 59, 59),
        ); // day=0 means last day of previous month

        const q2 = query(
          collection(db, "activities"),
          where("activity", "in", ["Technician Scored", "Assigned to Doctor"]),
          where("createdAt", ">=", startInDate),
          where("createdAt", "<=", endInDate),
        );

        /////////////////////////////////////////////////////////////////////////////////////
        // Get date for query all booking in previous month to current month
        const prevMonth = new Date(year, month - 1, 1);
        const startDatePrevMonth = Timestamp.fromDate(
          new Date(prevMonth.getFullYear(), prevMonth.getMonth(), 1, 0, 0, 0),
        );
        console.log("Start Date Prev Month: ", startDatePrevMonth.toDate());

        // End of current month
        const endDateCurrentMonth = Timestamp.fromDate(
          new Date(year, month + 1, 0, 23, 59, 59),
        );
        console.log("End Date Prev Month: ", endDateCurrentMonth.toDate());

        const q3 = query(
          collectionGroup(db, "bookings"),
          where("start", ">=", startDatePrevMonth),
          where("start", "<=", endDateCurrentMonth),
          //   where("extendedProps.status", "not-in", ["Cancel","Failure"]),
        );

        /////////////////////////////////////////////////////////////////////////////////////

        const [querySnapshot, querySnapshot2, querySnapshot3] =
          await Promise.all([getDocs(q), getDocs(q2), getDocs(q3)]);

        const data = [];
        querySnapshot.forEach((doc) => {
          data.push({ id: doc.id, ...doc.data() });
        });
        setActivityList(data);

        // const data2 = [];
        // querySnapshot2.forEach((doc) => {
        //   data2.push({ id: doc.id, ...doc.data() });
        // });
        // const uniqueData2 = data2.filter(
        //   (item, index, self) =>
        //     index === self.findIndex((t) => t.order === item.order)
        // );

        const data2 = [];
        querySnapshot2.forEach((doc) =>
          data2.push({ id: doc.id, ...doc.data() }),
        );

        // Helper: convert Firestore Timestamp → milliseconds safely
        const getMs = (ts) =>
          ts && typeof ts.toMillis === "function" ? ts.toMillis() : -Infinity;

        // Group by order, and track both activity types
        const byOrder = new Map();

        data2.forEach((item) => {
          const ord =
            item.order ||
            (item.extendedProps && item.extendedProps.order) ||
            "";
          if (!ord) return;

          const createdMs = getMs(item.createdAt);
          if (!byOrder.has(ord)) {
            byOrder.set(ord, {
              tech: null,
              techMs: -Infinity,
              assigned: null,
              assignedMs: -Infinity,
            });
          }

          const slot = byOrder.get(ord);
          if (item.activity === "Technician Scored") {
            if (createdMs > slot.techMs) {
              slot.tech = item;
              slot.techMs = createdMs;
            }
          } else if (item.activity === "Assigned to Doctor") {
            if (createdMs > slot.assignedMs) {
              slot.assigned = item;
              slot.assignedMs = createdMs;
            }
          }
        });

        // Now build the uniqueData2 array — priority: Technician Scored > Assigned to Doctor
        const uniqueData2 = [];
        byOrder.forEach((slot) => {
          if (slot.tech) {
            uniqueData2.push(slot.tech);
          } else if (slot.assigned) {
            uniqueData2.push(slot.assigned);
          }
        });

        // console.log("Scored List: ", uniqueData2);
        setScoredList(uniqueData2);

        const data3 = [];
        querySnapshot3.forEach((doc) => {
          data3.push({ id: doc.id, ...doc.data() });
        });
        // console.log("All Activity List: ", data3);
        setAllActivityList(data3);
      };
      try {
        if (!hasFullOverviewAccess && !scopedTechName) {
          setTechnicianList([]);
          setActivityList([]);
          setScoredList([]);
          setAllActivityList([]);
          setNodeCodeByName({});
          return;
        }

        if (!canViewOverall) {
          if (!scopedTechName) {
            setTechnicianList([]);
          } else {
            await fetchTechnicianList();
          }
          setActivityList([]);
          setScoredList([]);
          setAllActivityList([]);
          setNodeCodeByName({});
          return;
        }

        await Promise.all([
          fetchActivityList(),
          fetchTechnicianList(),
          fetchNodeCodeByName(),
        ]);
      } catch (err) {
        console.error("Error fetching data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    return undefined;
  }, [
    canViewOverall,
    canViewAllTechnicians,
    paymentAccess,
    paymentScopeLoading,
    scopedTechName,
  ]);

  useEffect(() => {
    if (paymentScopeLoading) return;

    setSelectedTech((current) =>
      paymentAccess ? current || "Summary" : scopedTechName || "Summary",
    );
    if (!paymentAccess) {
      setActiveTab(canViewOverall ? "overview" : "payments");
      setBookingTmpList([]);
      setActivityTmpList([]);
      setPayThisRound({});
      setSelectedBatch(null);
      setBatchEdit(null);
    }
  }, [canViewOverall, paymentAccess, paymentScopeLoading, scopedTechName]);

  useEffect(() => {
    setPayThisRound((prev) => {
      const next = { ...prev };
      const orders = new Set(
        paymentBookingList
          .map((r) => (r.extendedProps || {}).order)
          .filter(Boolean),
      );

      // add missing orders with defaults
      orders.forEach((ord) => {
        if (!next[ord]) next[ord] = { record: false, score: false };
      });

      // remove entries that are no longer in the table (optional)
      Object.keys(next).forEach((ord) => {
        if (!orders.has(ord)) delete next[ord];
      });

      return next;
    });
  }, [paymentBookingList]);

  useEffect(() => {
    if (
      canManagePayments &&
      (paymentAccess ? selectedTech : scopedTechName)
    ) {
      loadPendingBatches();
    }
    if (
      canViewConfirmedPayments &&
      (paymentAccess ? selectedTech : scopedTechName)
    ) {
      loadReadyToEmailForTech();
    }
  }, [
    canManagePayments,
    canViewConfirmedPayments,
    paymentAccess,
    scopedTechName,
    selectedTech,
    technicianList,
  ]);

  const excludeStatus = ["Cancel", "Failure"];

  const techniciansSortedByRecord = technicianList
    .map((technician) => {
      const recordList = activityList.filter(
        (activity) => activity.extendedProps.techrecord === technician.name,
        //  && !excludeStatus.includes(activity.extendedProps.status)
      );
      return { ...technician, recordListLength: recordList.length };
    })
    .sort((a, b) => b.recordListLength - a.recordListLength);

  const SelectTechnician = (tech) => {
    if (!paymentAccess) return;
    setSelectedTech(tech);
    console.log("Selected Technician: ", tech);
  };

  const exportActivityData = (
    recordedList,
    matchedScoredBookings,
    selectedTechnician,
    startDateStr,
    endDateStr,
  ) => {
    const toRow = (item) => {
      const props = item.extendedProps || {};
      return {
        Order: props.order || "",
        Firstname: props.firstname || "",
        Lastname: props.lastname || "",
        HN: props.hn || "",
        HospitalNumber: props.hospitalNumber || "",
        Gender: props.gender || "",
        NationalID: props.nationalId || "",
        TechRecord: props.techrecord || "",
        TechScore: props.technician || "",
        Doctor: props.doctor || "",
        SleepStudyDate: props.latestSleepStudyDate || "",
        Status: props.status || "",
        Phone: props.phone || "",
        Customer: props.customer || "",
        Node: props.node || "",
        AHI: props.ahi || "",
        Note: props.note || "",
      };
    };

    var data1 = recordedList.map(toRow);
    console.log("Data1: ", data1);
    if (data1.length === 0) {
      data1 = [
        {
          Order: "",
          Firstname: "",
          Lastname: "",
          HN: "",
          HospitalNumber: "",
          Gender: "",
          NationalID: "",
          TechRecord: "",
          TechScore: "",
          Doctor: "",
          SleepStudyDate: "",
          Status: "",
          Phone: "",
          Customer: "",
          Node: "",
          AHI: "",
          Note: "",
        },
      ];
    }
    const data2 = matchedScoredBookings.map(toRow);
    const headers = Object.keys(data1[0]);

    const title = `Sleep Study Report: ${startDateStr} to ${endDateStr} (Technician: ${selectedTechnician})`;

    const sheetData = [];

    // Title row
    sheetData.push([title]);
    sheetData.push([]);

    // Recorded List section
    sheetData.push(["Recorded List"]);
    sheetData.push(headers);
    sheetData.push(...data1.map((item) => headers.map((h) => item[h])));
    sheetData.push([`Total: ${data1.length} items`]);

    // 5 empty rows
    for (let i = 0; i < 3; i++) sheetData.push([]);

    // Matched Scored Bookings section
    sheetData.push(["Matched Scored Bookings"]);
    sheetData.push(headers);
    sheetData.push(...data2.map((item) => headers.map((h) => item[h])));
    sheetData.push([`Total: ${data2.length} items`]);

    // Create and export workbook
    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Activity Report");

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    // saveAs(
    //   new Blob([wbout], { type: "application/octet-stream" }),
    //   "Activity_Report.xlsx"
    // );
  };

  const ExportSummaryCountExcel = (summary, startDate, endDate) => {
    const ws = XLSX.utils.json_to_sheet(summary);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Summary");

    // const fileName = `Technician_Summary_${startDate}_to_${endDate}.xlsx`;
    // XLSX.writeFile(wb, fileName);
  };

  // const Export = async () => {
  //   // console.log("Export Data");
  //   console.log("selectedTech: ", selectedTech);
  //   console.log("Start Date: ", startDate);
  //   console.log("End Date: ", endDate);

  //   if (
  //     startDate === "" ||
  //     endDate === "" ||
  //     startDate === null ||
  //     endDate === null
  //   ) {
  //     console.log("Please select start and end date");
  //     return;
  //   }

  //   const fetchActivityList = async (start, end) => {
  //     if (start === "" || end === "") {
  //       return { activity: [], scored: [], allActivity: [] };
  //     }
  //     /////////////////////////////////////////////////////////////////////////////////////
  //     // Get string date for query booking in this month
  //     const startDate = new Date(start + "T00:00:00");
  //     const formattedStart = formatDate(startDate);

  //     const endDate = new Date(end + "T23:59:59");
  //     const formattedEnd = formatDate(endDate);

  //     const q = query(
  //       collectionGroup(db, "bookings"),
  //       where("extendedProps.latestSleepStudyDate", ">=", formattedStart),
  //       where("extendedProps.latestSleepStudyDate", "<=", formattedEnd),
  //       orderBy("extendedProps.latestSleepStudyDate", "desc")
  //     );

  //     /////////////////////////////////////////////////////////////////////////////////////
  //     // Get date for query activities status Technician Scored in this month
  //     const startInDate = Timestamp.fromDate(new Date(start + "T00:00:00"));

  //     const endInDate = Timestamp.fromDate(new Date(end + "T23:59:59")); // day=0 means last day of previous month

  //     const q2 = query(
  //       collection(db, "activities"),
  //       where("activity", "in", ["Technician Scored", "Assigned to Doctor"]),
  //       where("createdAt", ">=", startInDate),
  //       where("createdAt", "<=", endInDate)
  //     );

  //     /////////////////////////////////////////////////////////////////////////////////////
  //     // Get date for query all booking in previous month to current month

  //     const thirtyDaysBefore = new Date(startDate);
  //     thirtyDaysBefore.setDate(startDate.getDate() - 30);
  //     thirtyDaysBefore.setHours(0, 0, 0, 0); // ⬅️ ensures time is 00:00:00.000
  //     const startDatethirtyDaysBefore = Timestamp.fromDate(thirtyDaysBefore);

  //     const endDay = new Date(endDate);
  //     endDay.setDate(endDay.getDate());
  //     endDay.setHours(23, 59, 59, 0); // ⬅️ ensures time is 00:00:00.000
  //     const endDateCurrentMonth = Timestamp.fromDate(endDay);

  //     const q3 = query(
  //       collectionGroup(db, "bookings"),
  //       where("start", ">=", startDatethirtyDaysBefore),
  //       where("start", "<=", endDateCurrentMonth)
  //       //   where("extendedProps.status", "not-in", ["Cancel","Failure"]),
  //     );

  //     /////////////////////////////////////////////////////////////////////////////////////

  //     const [querySnapshot, querySnapshot2, querySnapshot3] = await Promise.all(
  //       [getDocs(q), getDocs(q2), getDocs(q3)]
  //     );

  //     const data = [];
  //     querySnapshot.forEach((doc) => {
  //       data.push({ id: doc.id, ...doc.data() });
  //     });

  //     // const data2 = [];
  //     // querySnapshot2.forEach((doc) => {
  //     //   data2.push({ id: doc.id, ...doc.data() });
  //     // });
  //     // const uniqueData2 = data2.filter(
  //     //   (item, index, self) =>
  //     //     index === self.findIndex((t) => t.order === item.order)
  //     // );

  //     const data2 = [];
  //     querySnapshot2.forEach((doc) =>
  //       data2.push({ id: doc.id, ...doc.data() })
  //     );

  //     // Helper: convert Firestore Timestamp → milliseconds safely
  //     const getMs = (ts) =>
  //       ts && typeof ts.toMillis === "function" ? ts.toMillis() : -Infinity;

  //     // Group by order, and track both activity types
  //     const byOrder = new Map();

  //     data2.forEach((item) => {
  //       const ord =
  //         item.order || (item.extendedProps && item.extendedProps.order) || "";
  //       if (!ord) return;

  //       const createdMs = getMs(item.createdAt);
  //       if (!byOrder.has(ord)) {
  //         byOrder.set(ord, {
  //           tech: null,
  //           techMs: -Infinity,
  //           assigned: null,
  //           assignedMs: -Infinity,
  //         });
  //       }

  //       const slot = byOrder.get(ord);
  //       if (item.activity === "Technician Scored") {
  //         if (createdMs > slot.techMs) {
  //           slot.tech = item;
  //           slot.techMs = createdMs;
  //         }
  //       } else if (item.activity === "Assigned to Doctor") {
  //         if (createdMs > slot.assignedMs) {
  //           slot.assigned = item;
  //           slot.assignedMs = createdMs;
  //         }
  //       }
  //     });

  //     // Now build the uniqueData2 array — priority: Technician Scored > Assigned to Doctor
  //     const uniqueData2 = [];
  //     byOrder.forEach((slot) => {
  //       if (slot.tech) {
  //         uniqueData2.push(slot.tech);
  //       } else if (slot.assigned) {
  //         uniqueData2.push(slot.assigned);
  //       }
  //     });

  //     const data3 = [];
  //     querySnapshot3.forEach((doc) => {
  //       data3.push({ id: doc.id, ...doc.data() });
  //     });

  //     return { activity: data, scored: uniqueData2, allActivity: data3 };
  //   };

  //   const result = await fetchActivityList(startDate, endDate);

  //   if (
  //     result.activity.length === 0 ||
  //     result.scored.length === 0 ||
  //     result.allActivity.length === 0
  //   ) {
  //     console.log("No data found");
  //   } else {
  //     // const excludeStatus = ["Cancel", "Failure"];

  //     const start = new Date(startDate + "T00:00:00");
  //     // 1st of the month at 00:00:00
  //     const formattedStart = formatDate(start);
  //     // Yesterday at 23:59:59
  //     const end = new Date(endDate + "T23:59:59");
  //     const formattedEnd = formatDate(end);
  //     if (selectedTech === "Summary") {
  //       const techList = [
  //         ...new Set(
  //           result.activity
  //             .map((a) => a.extendedProps.techrecord)
  //             .filter((v) => v && v !== "" && v !== "กรุณาเลือก")
  //         ),
  //       ];

  //       const summary = techList.map((tech) => {
  //         const recordList = result.activity.filter(
  //           (activity) => activity.extendedProps.techrecord === tech
  //           // && !excludeStatus.includes(activity.extendedProps.status)
  //         );

  //         const recordedList = recordList.filter((activity) => {
  //           const scheduled = activity.extendedProps.latestSleepStudyDate;
  //           return scheduled >= formattedStart && scheduled <= formattedEnd;
  //         });

  //         const matchedScored = result.allActivity.filter((booking) =>
  //           result.scored.some(
  //             (scored) =>
  //               scored.order === booking.title &&
  //               booking.extendedProps.technician === tech
  //             // && !excludeStatus.includes(booking.extendedProps.status)
  //           )
  //         );

  //         return {
  //           Technician: tech,
  //           Recorded: recordedList.length,
  //           Scored: matchedScored.length,
  //         };
  //       });

  //       // 🔽 Export summary count as Excel
  //       ExportSummaryCountExcel(summary, startDate, endDate);
  //     } else {
  //       const recordList = result.activity.filter(
  //         (activity) => activity.extendedProps.techrecord === selectedTech
  //         // && !excludeStatus.includes(activity.extendedProps.status)
  //       );
  //       // Split the list by createdAt timestamp
  //       const recordedList = recordList.filter((activity) => {
  //         const scheduled = activity.extendedProps.latestSleepStudyDate; // ✅ Convert Firestore Timestamp
  //         return (
  //           scheduled >= formattedStart &&
  //           scheduled <= formattedEnd &&
  //           activity.extendedProps.techrecord === selectedTech
  //         );
  //       });
  //       const recordLeftList = recordList.filter(
  //         (activity) => !recordedList.includes(activity)
  //       );
  //       const cancelList = result.activity.filter(
  //         (activity) =>
  //           activity.extendedProps.techrecord === selectedTech &&
  //           activity.extendedProps.status === "Cancel"
  //       );
  //       const failureList = result.activity.filter(
  //         (activity) =>
  //           activity.extendedProps.techrecord === selectedTech &&
  //           activity.extendedProps.status === "Failure"
  //       );
  //       const matchedScoredBookings = result.allActivity.filter((booking) =>
  //         result.scored.some(
  //           (scored) =>
  //             scored.order === booking.title &&
  //             booking.extendedProps.technician === selectedTech
  //           // && !excludeStatus.includes(booking.extendedProps.status)
  //         )
  //       );

  //       console.log("Recorded List: ", recordedList);
  //       console.log("Matched Scored Bookings: ", matchedScoredBookings);

  //       // exportActivityData(
  //       //   recordedList,
  //       //   matchedScoredBookings,
  //       //   selectedTech,
  //       //   startDate,
  //       //   endDate
  //       // );
  //     }
  //   }
  // };

  const makeRow = (a, b) => {
    const ext = safeExt(b);
    const bb = b || {};
    return {
      ScoredAt: tsToLocalString(a.startDate || null), // first column
      Order: a.order || ext.order || "",
      Firstname: ext.firstname || bb.firstname || "",
      Lastname: ext.lastname || bb.lastname || "",
      HN: ext.hn || bb.hn || "",
      HospitalNumber: ext.hospitalNumber || bb.hospitalNumber || "",
      Gender: ext.gender || bb.gender || "",
      NationalID: ext.nationalId || bb.nationalId || "",
      TechRecord: ext.techrecord || "",
      TechScore: ext.technician || "",
      Doctor: ext.doctor || bb.doctor || "",
      SleepStudyDate: ext.latestSleepStudyDate || bb.latestSleepStudyDate || "",
      Status: ext.status || bb.status || "",
      Phone: ext.phone || bb.phone || "",
      Customer: ext.customer || bb.customer || "",
      Node: ext.node || bb.node || "",
      AHI: ext.ahi || bb.ahi || "",
      Note: ext.note || bb.note || "",
    };
  };

  const toYMD = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const Export = async (rangeStart = startDate, rangeEnd = endDate) => {
    try {
      if (!rangeStart || !rangeEnd) {
        alert("Please pick Start and End dates.");
        return;
      }

      setStartDate(rangeStart);
      setEndDate(rangeEnd);
      setLoading(true);
      setPayThisRound({});
      setDeduction(0);
      setTransport(0);
      setAdjustmentPay(0);
      setPayDate("");
      setPayNote("");
      setPaymentDraftResetKey((previous) => previous + 1);
      clearTransportFileSelection();

      const startTs = Timestamp.fromDate(
        dateFromInputYMD(rangeStart, 0, 0, 0, 0),
      );
      const endTs = Timestamp.fromDate(
        dateFromInputYMD(rangeEnd, 23, 59, 59, 999),
      );
      const startYMD = toYMD(startTs.toDate());
      const endYMD = toYMD(endTs.toDate());

      // Overview visibility is separate from payment-case query access. A
      // super admin without payment access still uses the provider-only query.
      const hasFullExportReadAccess = canQueryAllTechnicianCases;
      const selectedProviderName = hasFullExportReadAccess
        ? selectedTech
        : scopedTechName;
      let activities = [];
      let bookings = [];

      if (hasFullExportReadAccess) {
        // Full-access users may query all activity records for the date range.
        const activitiesSnap = await getDocs(
          query(
            collectionGroup(db, "activities"),
            where("activity", "in", ["Assigned to Doctor", "Technician Scored"]),
            where("startDate", ">=", startTs),
            where("startDate", "<=", endTs),
          ),
        );
        activities = activitiesSnap.docs.map((activityDoc) => {
          const activity = activityDoc.data() || {};
          return {
            order: activity.order || null,
            hn: activity.hn || null,
            detail2: activity.detail2 || null,
            startDate: activity.startDate || null,
          };
        });

        if (selectedProviderName && selectedProviderName !== "Summary") {
          const scheduledSnapshot = await getDocs(
            query(
              collectionGroup(db, "bookings"),
              where("extendedProps.latestSleepStudyDate", ">=", startYMD),
              where("extendedProps.latestSleepStudyDate", "<=", endYMD),
            ),
          );
          bookings.push(
            ...scheduledSnapshot.docs
              .map((bookingDoc) => ({
                refPath: bookingDoc.ref.path,
                ...bookingDoc.data(),
              }))
              .filter((booking) => {
                const props = booking.extendedProps || {};
                return (
                  props.technician === selectedProviderName ||
                  props.techrecord === selectedProviderName
                );
              }),
          );
        }
      } else {
        // Restricted users first fetch only bookings that name their configured
        // technician. Activity lookups are then limited to those order IDs.
        const ownBookingsSnapshot = await getDocs(
          query(
            collectionGroup(db, "bookings"),
            or(
              where("extendedProps.technician", "==", selectedProviderName),
              where("extendedProps.techrecord", "==", selectedProviderName),
            ),
          ),
        );
        bookings = ownBookingsSnapshot.docs.map((bookingDoc) => ({
          refPath: bookingDoc.ref.path,
          ...bookingDoc.data(),
        }));

        const ownOrderList = [
          ...new Set(
            bookings
              .map((booking) => (booking.extendedProps || {}).order)
              .filter(hasText),
          ),
        ];
        const activitySnapshots = await Promise.all(
          Array.from(
            { length: Math.ceil(ownOrderList.length / 10) },
            (_, index) => ownOrderList.slice(index * 10, index * 10 + 10),
          ).map((orderChunk) =>
            getDocs(
              query(
                collectionGroup(db, "activities"),
                where("order", "in", orderChunk),
                where("startDate", ">=", startTs),
                where("startDate", "<=", endTs),
              ),
            ),
          ),
        );
        activitySnapshots.forEach((activitySnapshot) => {
          activitySnapshot.docs.forEach((activityDoc) => {
            const activity = activityDoc.data() || {};
            if (
              !["Assigned to Doctor", "Technician Scored"].includes(
                activity.activity,
              )
            ) {
              return;
            }
            activities.push({
              order: activity.order || null,
              hn: activity.hn || null,
              detail2: activity.detail2 || null,
              startDate: activity.startDate || null,
            });
          });
        });
      }

      setBookingTmpList([]);
      // setActivityTmpList([]);

      // 2) DEDUPE by order: keep the latest createdAt per order
      // DEDUPE by order: first wins
      const byOrder = new Map();

      for (let i = 0; i < activities.length; i++) {
        const a = activities[i];
        if (!hasText(a.order)) continue;
        if (!byOrder.has(a.order)) byOrder.set(a.order, a);
      }

      const uniqueActivities = Array.from(byOrder.values());

      // Include scheduled cases in the payment period as well as scored
      // activities. Record payment can be eligible before a case is scored.
      const orderList = getTechnicianPaymentCandidateOrders({
        activities: uniqueActivities,
        bookings,
        startYMD,
        endYMD,
      });
      if (!orderList.length) {
        alert("No technician cases found in the selected payment period.");
        return;
      }

      if (hasFullExportReadAccess) {
        if (selectedProviderName && selectedProviderName !== "Summary") {
          const bookingSnapshots = await Promise.all(
            Array.from(
              { length: Math.ceil(orderList.length / 10) },
              (_, index) => orderList.slice(index * 10, index * 10 + 10),
            ).map((orderChunk) =>
              getDocs(
                query(
                  collectionGroup(db, "bookings"),
                  where("extendedProps.order", "in", orderChunk),
                ),
              ),
            ),
          );

          bookings.push(
            ...bookingSnapshots
              .reduce(
                (bookingDocs, bookingSnapshot) =>
                  bookingDocs.concat(bookingSnapshot.docs),
                [],
              )
              .map((bookingDoc) => ({
                refPath: bookingDoc.ref.path,
                ...bookingDoc.data(),
              }))
              .filter((booking) => {
                const props = booking.extendedProps || {};
                return (
                  props.technician === selectedProviderName ||
                  props.techrecord === selectedProviderName
                );
              }),
          );
        }

      } else {
        bookings = bookings.filter((booking) => {
          const props = booking.extendedProps || {};
          const order = props.order;
          const studyDate = props.latestSleepStudyDate || "";
          return (
            orderList.includes(order) ||
            (studyDate >= startYMD && studyDate <= endYMD)
          );
        });
      }

      // 5) index bookings by order (strict join)
      // const bookingByOrder = new Map();
      // for (const b of bookings) {
      //   const ord = b.extendedProps.order;
      //   if (hasText(ord) && !bookingByOrder.has(ord))
      //     bookingByOrder.set(ord, b);
      // }

      const bookingByOrder = new Map();

      for (const b of bookings) {
        if (!b.extendedProps) continue;

        const ord = b.extendedProps.order;
        if (!hasText(ord)) continue;

        const existing = bookingByOrder.get(ord);

        if (!existing) {
          bookingByOrder.set(ord, b);
        } else {
          const dNew = b.extendedProps.latestSleepStudyDate; // "YYYY-MM-DD"
          const dOld = existing.extendedProps.latestSleepStudyDate;

          // works because format is YYYY-MM-DD
          if (dNew > dOld) {
            bookingByOrder.set(ord, b);
          }
        }
      }

      // const uniqueBookings = Array.from(bookingByOrder.values());

      // 6) workbook
      const wb = XLSX.utils.book_new();
      // console.log("unique: ", uniqueActivities);

      if (selectedProviderName && selectedProviderName !== "Summary") {
        // 3. Convert bookingByOrder to array
        var arr = Array.from(bookingByOrder.values());
        // console.log("Arr Booking before sort:", arr[0]);
        const excludeStatus = ["Cancel", "Failure"];

        arr = arr.filter(
          (booking) =>
            !excludeStatus.includes((booking.extendedProps || {}).status) &&
            hasTechnicianPaymentHn(booking),
        );

        // 4. Sort booking arr by the index
        // arr.sort((b1, b2) => {
        //   const o1 = orderIndex.get(b1.extendedProps.order);
        //   const o2 = orderIndex.get(b2.extendedProps.order);
        //   return (
        //     (o1 !== undefined ? o1 : Infinity) -
        //     (o2 !== undefined ? o2 : Infinity)
        //   );
        // });

        setBookingTmpList(arr);
      } else {
        //nothing here
      }
    } catch (err) {
      console.error("Export failed:", err);
      alert("Export failed. See console for details.");
    } finally {
      setLoading(false);
    }
  };

  // const handleSendEmail = async (batch) => {
  //   if (!window.confirm("Send email for this confirmed batch?")) return;

  //   try {
  //     const batchRef = doc(db, batch.refPath);
  //     await updateDoc(batchRef, {
  //       isSendEmail: true,
  //       sendEmailAt: serverTimestamp(),
  //       sendEmailBy: currentUser.displayName,
  //     });
  //     notifyTopRight("Marked as emailed.");

  //     // remove from list
  //     setReadyToEmail((prev) => prev.filter((b) => b.id !== batch.id));
  //   } catch (e) {
  //     console.error(e);
  //     notifyError("Update failed", e.message || e);
  //   }
  // };

  // const buildPaymentBatchWorkbookBytes = (batch, techName) => {
  //   if (!batch || !Array.isArray(batch.entries) || batch.entries.length === 0) {
  //     throw new Error("No entries to export.");
  //   }

  //   const rows = batch.entries.map((e, index) => {
  //     const phases = Array.isArray(e.phases) ? e.phases : [];
  //     const record = phases.find((p) => p.phase === "record");
  //     const score = phases.find((p) => p.phase === "score");

  //     const recordAmt = record ? Number(record.amount || 0) : 0;
  //     const scoreAmt = score ? Number(score.amount || 0) : 0;

  //     return {
  //       No: index + 1,
  //       Order: e.order || "",
  //       HN: e.hn || "",
  //       Firstname: e.firstname || "",
  //       Lastname: e.lastname || "",
  //       Node: e.node || "",
  //       Customer: e.customer || "",
  //       HealthInsurance: e.healthInsurance || "-",
  //       TechRecord: e.techrecord || "-",
  //       TechScore: e.technician || "-",
  //       "Record Amount (THB)": recordAmt || 0,
  //       "Score Amount (THB)": scoreAmt || 0,
  //       "Total (THB)": recordAmt + scoreAmt || 0,
  //     };
  //   });

  //   const ws = XLSX.utils.json_to_sheet(rows);
  //   ws["!cols"] = [
  //     { wch: 4 },
  //     { wch: 16 },
  //     { wch: 14 },
  //     { wch: 16 },
  //     { wch: 16 },
  //     { wch: 24 },
  //     { wch: 24 },
  //     { wch: 16 },
  //     { wch: 18 },
  //     { wch: 20 },
  //     { wch: 22 },
  //     { wch: 24 },
  //     { wch: 14 },
  //   ];

  //   const m = batch.meta || {};
  //   const toDate = (ts) =>
  //     ts && ts.seconds ? new Date(ts.seconds * 1000).toLocaleString() : "";
  //   const toDateOnly = (ts) =>
  //     ts && ts.seconds ? new Date(ts.seconds * 1000).toLocaleDateString() : "";

  //   const summaryAoA = [
  //     ["Technician", techName || "-"],
  //     ["Month", batch.month || ""],
  //     ["Period Start", batch.period.start || ""],
  //     ["Period End", batch.period.end || ""],
  //     ["Confirmed", batch.isConfirmed ? "Yes" : "No"],
  //     // ["Email Sent", batch.isSendEmail ? "Yes" : "No"],
  //     ["Created By", batch.createdBy || ""],
  //     ["Confirmed By", batch.confirmedBy || ""],
  //     ["Created At", toDate(batch.createdAt)],
  //     ["Confirmed At", toDate(batch.confirmedAt)],
  //     [],
  //     ["Amount", m.amount || 0],
  //     ["WHT 3%", -(m.wht || 0)],
  //     ["Guarantee Case", -(m.guaranteeCase || 0)],
  //     ["Deduction", -(m.deduction || 0)],
  //     ["Transport", +(m.transport || 0)],
  //     ["Adjustment Pay", +(m.adjustmentPay || 0)],
  //     ["Total Pay", m.totalPay || 0],
  //     ["Pay Date", toDateOnly(m.payDate)],
  //     ["Note", m.payNote || ""],
  //   ];

  //   const wsMeta = XLSX.utils.aoa_to_sheet(summaryAoA);

  //   const wb = XLSX.utils.book_new();
  //   XLSX.utils.book_append_sheet(wb, ws, "Batch Details");
  //   XLSX.utils.book_append_sheet(wb, wsMeta, "Summary");

  //   // return bytes for upload
  //   const arrayBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  //   return new Uint8Array(arrayBuffer);
  // };

  const buildPaymentBatchWorkbookBytes = (batch, techName) => {
    if (!batch || !Array.isArray(batch.entries) || batch.entries.length === 0) {
      throw new Error("No entries to export.");
    }

    const rows = batch.entries.map((e, index) => {
      const phases = Array.isArray(e.phases) ? e.phases : [];
      const record = phases.find((p) => p.phase === "record");
      const score = phases.find((p) => p.phase === "score");

      const recordAmt = record ? Number(record.amount || 0) : 0;
      const scoreAmt = score ? Number(score.amount || 0) : 0;

      return {
        No: index + 1,
        Order: e.order || "",
        HN: e.hn || "",
        Firstname: e.firstname || "",
        Lastname: e.lastname || "",
        Node: e.node || "",
        Customer: e.customer || "",
        HealthInsurance: e.healthInsurance || "-",
        TechRecord: e.techrecord || "-",
        TechScore: e.technician || "-",
        "Record Amount (THB)": recordAmt || 0,
        "Score Amount (THB)": scoreAmt || 0,
        "Total (THB)": recordAmt + scoreAmt || 0,
      };
    });

    const ws = XLSX.utils.json_to_sheet(rows);

    ws["!cols"] = [
      { wch: 4 },
      { wch: 16 },
      { wch: 14 },
      { wch: 16 },
      { wch: 16 },
      { wch: 24 },
      { wch: 24 },
      { wch: 16 },
      { wch: 18 },
      { wch: 20 },
      { wch: 22 },
      { wch: 24 },
      { wch: 14 },
    ];

    const m = batch.meta || {};

    const toDate = (ts) => {
      if (!ts) return "";
      if (ts.toDate) return ts.toDate().toLocaleString(); // Firestore Timestamp
      if (ts.seconds) return new Date(ts.seconds * 1000).toLocaleString(); // {seconds}
      return new Date(ts).toLocaleString();
    };

    const toDateOnly = (ts) => {
      if (!ts) return "";
      if (ts.toDate)
        return ts.toDate().toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      if (ts.seconds)
        return new Date(ts.seconds * 1000).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      return new Date(ts).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    };

    // ===== 1) Summary sheet (AoA) =====
    const metaLines = [
      ["Amount", money(m.amount || 0)],
      ["WHT 3%", "-" + money(m.wht || 0)],
      ["Guarantee Case", "-" + money(m.guaranteeCase || m.guaranteeCase || 0)],
      ["Deduction", "-" + money(m.deduction || 0)],
      ["Transport", "+" + money(m.transport || 0)],
      ["Adjustment Pay", "+" + money(m.adjustmentPay || 0)],
      [],
      ["Total Pay", money(m.totalPay || 0)],
      ["Pay Date", toDateOnly(m.payDate)],
      // ["Note", m.payNote || ""],
      ["Note", (m.payNote || "").toString()], // มี \n อยู่แล้วจาก textarea
    ];

    const summaryAoA = [
      ["Technician", techName || "-"],
      ["Month", batch.month || ""],
      ["Period Start", batch.period.start || ""],
      ["Period End", batch.period.end || ""],
      ["Confirmed", batch.isConfirmed ? "Yes" : "No"],
      ["Created By", batch.createdBy || ""],
      ["Confirmed By", batch.confirmedBy || ""],
      ["Created At", toDate(batch.createdAt)],
      ["Confirmed At", toDate(batch.confirmedAt)],
      [],
      ...metaLines,
    ];

    const wsMeta = XLSX.utils.aoa_to_sheet(summaryAoA);

    // ===== 2) Append meta summary to bottom of Batch Details sheet =====
    // We add at column A so it sits below the table nicely
    const detailsAppendAoA = [
      [],
      ["--- Payment Summary ---", ""],
      ...metaLines,
    ];

    XLSX.utils.sheet_add_aoa(ws, detailsAppendAoA, { origin: -1 });

    // ===== 3) Workbook: Summary first tab =====
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsMeta, "Summary"); // first
    XLSX.utils.book_append_sheet(wb, ws, "Batch Details"); // second

    const arrayBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    return new Uint8Array(arrayBuffer);
  };

  const uint8ToBase64 = (u8) => {
    let binary = "";
    const chunkSize = 0x8000;
    for (let i = 0; i < u8.length; i += chunkSize) {
      binary += String.fromCharCode(...u8.subarray(i, i + chunkSize));
    }
    return btoa(binary);
  };

  const handleSendEmail = async (batch) => {
    if (!paymentAccess) {
      notifyWarning("Only full payment-access users can send payment emails.");
      return;
    }
    if (!window.confirm("Send email for this confirmed batch?")) return;

    try {
      if (!batch.entries.length) {
        notifyWarning("No entries to export.");
        return;
      }

      const techName =
        selectedTech === "Summary"
          ? technicianList.find((t) => t.techId === batch.techKey).name ||
            batch.techId ||
            "Technician"
          : selectedTech || batch.techId || batch.techKey || "Technician";

      const safeTech = techName.replace(/[\\\/:*?\[\]]/g, "_");
      const fileName = `PaymentBatch_${safeTech}_${batch.month || ""}.xlsx`;

      // 1) build excel bytes
      const bytes = buildPaymentBatchWorkbookBytes(batch, techName); // Uint8Array

      // 2) base64
      const base64 = uint8ToBase64(bytes);

      // 3) recipient
      const to =
        technicianList.find((t) => t.techId === batch.techKey).email ||
        batch.emailTo ||
        batch.techEmail ||
        batch.financeEmail ||
        "noppasit3@braindynamics.co.th";

      // const to = batch.emailTo || batch.techEmail || batch.financeEmail || "";
      // const to = "pisanu@braindynamics.co.th"; // TEST

      if (!to) {
        notifyWarning("No recipient email found for this batch.");
        return;
      }

      const m = batch.meta || {};
      const payNoteHtml = (m.payNote || "-").replace(/\n/g, "<br/>");

      const toDateOnly = (ts) => {
        if (!ts) return "-";
        // Firestore Timestamp
        if (ts.toDate)
          return ts.toDate().toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
        // {seconds:...}
        if (ts.seconds)
          return new Date(ts.seconds * 1000).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
        // Date/string
        return new Date(ts).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      };

      const html = `
      <h3>Payment Batch</h3>
      <p><strong>Technician</strong>: ${techName}</p>
      <p><strong>Month</strong>: ${batch.month || "-"}</p>

      <div style="border:1px solid #999; padding:12px; background:#eeeeee; border-radius:6px;">
        <h4 style="margin:0 0 8px 0;">Payment Summary</h4>

        <p style="margin:0;">
          <strong>Amount</strong>: ${money(m.amount ? m.amount : 0)} THB<br/>
          <strong>WHT 3%</strong>: -${money(m.wht ? m.wht : 0)} THB<br/>
          <strong>Guarantee Case</strong>: -${money(m.guranteeCase ? m.guranteeCase : m.guaranteeCase ? m.guaranteeCase : 0)} THB<br/>
          <strong>Deduction</strong>: -${money(m.deduction ? m.deduction : 0)} THB<br/>
          <strong>Transport</strong>: +${money(m.transport ? m.transport : 0)} THB<br/>
          <strong>Adjustment Pay</strong>: +${money(m.adjustmentPay ? m.adjustmentPay : 0)} THB<br/><br/>
          <strong>Total Pay</strong>: <strong>${money(m.totalPay ? m.totalPay : 0)} THB</strong><br/>
          <strong>Pay Date</strong>: <strong>${toDateOnly(m.payDate)}</strong><br/>
          <strong>Note</strong>: ${payNoteHtml}
        </p>
      </div>

      <p style="margin-top:12px;">Attached: ${fileName}</p>
      <p><strong>Submitted</strong>: ${currentUser.displayName}</p>
    `;
      // 4) send mail with base64 attachment
      await setDoc(doc(collection(db, "mail")), {
        to,
        message: {
          subject: `Payment Batch ${batch.month || ""} - ${techName}`,
          html,
          attachments: [
            {
              filename: fileName,
              content: base64,
              encoding: "base64",
              contentType:
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            },
          ],
        },
      });

      // 5) update batch
      const batchRef = doc(db, batch.refPath);
      await updateDoc(batchRef, {
        isSendEmail: true,
        sendEmailAt: serverTimestamp(),
        sendEmailBy: currentUser.displayName,
        sendEmailAttachmentName: fileName,
        sendEmailAttachmentMode: "base64",
      });

      notifyTopRight("Email sent with attachment.");
      // setReadyToEmail((prev) => prev.filter((b) => b.id !== batch.id));
      setReadyToEmail((prev) =>
        prev.map((b) =>
          b.id === batch.id
            ? {
                ...b,
                isSendEmail: true,
                sendEmailAt: new Date(), // optional (UI only)
                sendEmailBy: currentUser.displayName, // optional (UI only)
              }
            : b,
        ),
      );
    } catch (e) {
      console.error(e);
      notifyError("Send email failed", e.message || e);
    }
  };

  const exportBatchToExcel = (batch, selectedTech) => {
    if (!canAccessBatch(batch)) {
      notifyWarning("You can only export your own payment cases.");
      return;
    }
    if (!batch || !Array.isArray(batch.entries) || batch.entries.length === 0) {
      notifyWarning("No entries to export.");
      return;
    }

    try {
      // 1) resolve techName (รองรับ Summary)
      const techName =
        selectedTech === "Summary"
          ? (technicianList.find((t) => t.techId === batch.techKey)
              ? technicianList.find((t) => t.techId === batch.techKey).name
              : batch.techId) || "Technician"
          : selectedTech || batch.techId || batch.techKey || "Technician";

      const safeTech = techName.replace(/[\\\/:*?\[\]]/g, "_");
      const fileName = `PaymentBatch_${safeTech}_${batch.month || ""}.xlsx`;

      // 2) build bytes (Summary first tab + meta at bottom already)
      const bytes = buildPaymentBatchWorkbookBytes(batch, techName); // Uint8Array

      // 3) write file from bytes
      const wb = XLSX.read(bytes, { type: "array" });
      XLSX.writeFile(wb, fileName);

      // notifyTopRight("Exported batch to Excel.");
    } catch (err) {
      console.error("Export error:", err);
      notifyError("Export failed", err.message || err);
    }
  };
  // const exportBatchToExcel = (batch, selectedTech) => {
  //   if (!batch || !Array.isArray(batch.entries) || batch.entries.length === 0) {
  //     notifyWarning("No entries to export.");
  //     return;
  //   }

  //   try {
  //     const rows = batch.entries.map((e, index) => {
  //       const phases = Array.isArray(e.phases) ? e.phases : [];

  //       const record = phases.find((p) => p.phase === "record");
  //       const score = phases.find((p) => p.phase === "score");
  //       // const cpap = phases.find((p) => p.phase === "cpapFollowup");

  //       console.log("Entry phases:", phases);

  //       const paid = e.paid || {};

  //       const recordAmt = record ? Number(record.amount || 0) : 0;
  //       const scoreAmt = score ? Number(score.amount || 0) : 0;

  //       return {
  //         No: index + 1,
  //         Order: e.order || "",
  //         HN: e.hn || "",
  //         Firstname: e.firstname || "",
  //         Lastname: e.lastname || "",
  //         Node: e.node || "",
  //         Customer: e.customer || "",
  //         HealthInsurance: e.healthInsurance || "-",
  //         TechRecord: e.techrecord || "-",
  //         TechScore: e.technician || "-",

  //         "Record Amount (THB)": recordAmt || "0",

  //         "Score Amount (THB)": scoreAmt || "0",

  //         // Row total
  //         "Total (THB)": recordAmt + scoreAmt || "0",
  //       };
  //     });

  //     const ws = XLSX.utils.json_to_sheet(rows);

  //     ws["!cols"] = [
  //       { wch: 4 }, // No
  //       { wch: 16 }, // Order
  //       { wch: 14 }, // HN
  //       { wch: 16 }, // Firstname
  //       { wch: 16 }, // Lastname
  //       { wch: 24 }, // Node
  //       { wch: 24 }, // Customer
  //       { wch: 16 }, // HealthInsurance
  //       { wch: 18 }, // TechRecord
  //       { wch: 20 }, // TechScore

  //       { wch: 22 }, // Record Amount

  //       { wch: 24 }, // Score Amount

  //       { wch: 14 }, // Total
  //     ];

  //     // Summary sheet
  //     const m = batch.meta || {};
  //     const toDate = (ts) =>
  //       ts && ts.seconds ? new Date(ts.seconds * 1000).toLocaleString() : "";

  //     const toDateOnly = (ts) =>
  //       ts && ts.seconds
  //         ? new Date(ts.seconds * 1000).toLocaleDateString("en-GB", {
  //             day: "2-digit",
  //             month: "2-digit",
  //             year: "numeric",
  //           })
  //         : "";

  //     const summaryAoA = [
  //       ["Technician", selectedTech || batch.techId || batch.techKey || "-"],
  //       ["Month", batch.month || ""],
  //       ["Period Start", batch.period.start || ""],
  //       ["Period End", batch.period.end || ""],
  //       ["Confirmed", batch.isConfirmed ? "Yes" : "No"],
  //       ["Email Sent", batch.isSendEmail ? "Yes" : "No"],
  //       ["Created By", batch.createdBy || ""],
  //       ["Confirmed By", batch.confirmedBy || ""],
  //       ["Created At", toDate(batch.createdAt)],
  //       ["Confirmed At", toDate(batch.confirmedAt)],
  //       [],
  //       ["Amount", m.amount || 0],
  //       ["WHT 3%", -m.wht || 0],
  //       ["Guarantee Case", -m.guaranteeCase || 0],
  //       ["Deduction", -m.deduction || 0],
  //       ["Transport", +m.transport || 0],
  //       ["Adjustment Pay", +m.adjustmentPay || 0],
  //       ["Total Pay", m.totalPay || 0],
  //       ["Pay Date", toDateOnly(m.payDate)],
  //       ["Note", m.payNote || ""],
  //     ];

  //     const wsMeta = XLSX.utils.aoa_to_sheet(summaryAoA);

  //     const wb = XLSX.utils.book_new();
  //     XLSX.utils.book_append_sheet(wb, ws, "Batch Details");
  //     XLSX.utils.book_append_sheet(wb, wsMeta, "Summary");

  //     const safeDoctor = (
  //       selectedTech ||
  //       batch.techId ||
  //       batch.techKey ||
  //       "Technician"
  //     ).replace(/[\\\/:*?\[\]]/g, "_");
  //     const fileName = `PaymentBatch_${safeDoctor}_${batch.month || ""}.xlsx`;

  //     XLSX.writeFile(wb, fileName);
  //     notifyTopRight("Exported batch to Excel.");
  //   } catch (err) {
  //     console.error("Export error:", err);
  //     notifyError("Export failed", err.message || err);
  //   }
  // };

  const bookingWithCaseIndex = React.useMemo(() => {
    const counter = {};

    return paymentBookingList.map((row) => {
      const ep = row.extendedProps || {};
      const date = ep.latestSleepStudyDate || "";
      const techKey = ep.techrecord || "";

      const key = `${date}_${techKey}`;

      if (!counter[key]) counter[key] = 0;
      counter[key] += 1;

      return {
        ...row,
        caseIndex: counter[key], // 1,2,3,...
      };
    });
  }, [paymentBookingList]);

  const sortedConfirmedBatches = React.useMemo(
    () =>
      [...readyToEmail].sort((a, b) => {
        // Keep email-pending batches first, then show newest confirmations first.
        const aSent = a.isSendEmail === true ? 1 : 0;
        const bSent = b.isSendEmail === true ? 1 : 0;
        if (aSent !== bSent) return aSent - bSent;

        return paymentTimestamp(b.confirmedAt) - paymentTimestamp(a.confirmedAt);
      }),
    [readyToEmail],
  );
  const confirmedPaymentsPageCount = Math.max(
    1,
    Math.ceil(sortedConfirmedBatches.length / CONFIRMED_BATCHES_PER_PAGE),
  );
  const currentConfirmedPaymentsPage = Math.min(
    confirmedPaymentsPage,
    confirmedPaymentsPageCount,
  );
  const confirmedPaymentsPageStart =
    (currentConfirmedPaymentsPage - 1) * CONFIRMED_BATCHES_PER_PAGE;
  const confirmedBatchesOnPage = sortedConfirmedBatches.slice(
    confirmedPaymentsPageStart,
    confirmedPaymentsPageStart + CONFIRMED_BATCHES_PER_PAGE,
  );
  const confirmedPaginationPages = Array.from(
    new Set(
      [
        1,
        currentConfirmedPaymentsPage - 1,
        currentConfirmedPaymentsPage,
        currentConfirmedPaymentsPage + 1,
        confirmedPaymentsPageCount,
      ].filter(
        (page) => page >= 1 && page <= confirmedPaymentsPageCount,
      ),
    ),
  ).sort((a, b) => a - b);

  useEffect(() => {
    setConfirmedPaymentsPage(1);
  }, [selectedTech]);

  if (loading) {
    return <DashboardLoading />;
  }

  if (!canManagePayments && !canViewOverall) {
    return (
      <React.Fragment>
        <Toast />
        <PageTitle activeMenu="Technician Payments" motherMenu="Dashboard" />
        <div className="card">
          <div className="card-body">
            <h4 className="mb-2">Payment access is not configured</h4>
            <p className="mb-0 text-muted">
              Ask a super admin to map your account email to the exact technician
              name used in booking data before you can create or edit payments.
            </p>
          </div>
        </div>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Toast />

      <Modal
        centered
        show={Boolean(pendingBatchConfirmation)}
        onHide={closeBatchConfirmation}
      >
        <Modal.Header closeButton={!processingBatchConfirmation}>
          <Modal.Title>
            {pendingBatchConfirmation && pendingBatchConfirmation.action === "return"
              ? "Return payment to Pending?"
              : "Confirm payment batch?"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendingBatchConfirmation && pendingBatchConfirmation.action === "return" ? (
            <p className="mb-0">
              Return batch <strong>#{String(pendingBatchConfirmation.batchId).slice(-8)}</strong>{" "}
              to Pending Payments? This clears the paid flags linked to this
              batch so it can be edited and confirmed again.
            </p>
          ) : (
            <p className="mb-0">
              Confirm batch <strong>#{String((pendingBatchConfirmation || {}).batchId || "").slice(-8)}</strong>?
              Its included Record and Score phases will be marked as paid.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            onClick={closeBatchConfirmation}
            disabled={processingBatchConfirmation}
          >
            Cancel
          </Button>
          <Button
            variant={
              pendingBatchConfirmation && pendingBatchConfirmation.action === "return"
                ? "warning"
                : "danger"
            }
            onClick={confirmBatchConfirmation}
            disabled={processingBatchConfirmation}
          >
            {processingBatchConfirmation
              ? pendingBatchConfirmation && pendingBatchConfirmation.action === "return"
                ? "Returning…"
                : "Confirming…"
              : pendingBatchConfirmation && pendingBatchConfirmation.action === "return"
                ? "Return to Pending"
                : "Confirm Payment"}
          </Button>
        </Modal.Footer>
      </Modal>

      <div className="mb-3 text-center">
        <h2>
          ภาพรวมประจำเดือน{" "}
          <strong>{getThaiMonth(new Date().getMonth())}</strong>
        </h2>
      </div>
      <div className="row mb-3">
        {paymentAccess ? (
          <Dropdown
            className="align-items-end d-inline-flex ml-2"
            onSelect={SelectTechnician}
          >
          <Dropdown.Toggle variant="outline-primary" id="dropdown-basic">
            <i className="flaticon-381-controls-3"></i> by {selectedTech}
          </Dropdown.Toggle>
          <Dropdown.Menu
            className="dropdown-menu-right"
            style={{ maxHeight: "500px", overflowY: "auto" }}
          >
            <Dropdown.Item key={"Summary"} href="#" eventKey={"Summary"}>
              {"Summary"}
            </Dropdown.Item>
            {technicianList.map((technician) => (
              <Dropdown.Item
                key={technician.id}
                href="#"
                eventKey={technician.name}
              >
                {technician.name} ({technician.nickname})
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
          </Dropdown>
        ) : (
          <div className="align-items-center d-inline-flex ml-2">
            <span className="badge badge-outline-primary p-2">
              <i className="flaticon-381-controls-3"></i>{" "}
              {canViewAllTechnicians && !paymentAccess
                ? `All technicians overview · payment cases by ${scopedTechName}`
                : `by ${scopedTechName}`}
            </span>
          </div>
        )}

        {canManagePayments && (
          <PaymentPeriodControls
            startDate={startDate}
            endDate={endDate}
            onQuery={Export}
          />
        )}
      </div>

      <Tabs
        id="tech-dashboard-tabs"
        activeKey={activeTab}
        onSelect={(k) => {
          setActiveTab(k);
          setBatchEdit(null);
          setSelectedBatch(null);
        }}
        className="mb-3"
        mountOnEnter
        unmountOnExit
      >
        {canViewOverall && <Tab eventKey="overview" title="Overview">
          <div className="row">
            {techniciansSortedByRecord.map((technician) => {
              const today = new Date();
              const year = today.getFullYear();
              const month = today.getMonth();

              // 1st of the month at 00:00:00
              const startOfMonth = new Date(year, month, 1, 0, 0, 0);
              const formattedStartOfMonth = formatDate(startOfMonth);

              // Yesterday at 23:59:59
              const endOfYesterday = new Date(
                year,
                month,
                today.getDate() - 1,
                23,
                59,
                59,
              );
              const formattedEndOfYesterday = formatDate(endOfYesterday);

              const recordList = activityList.filter(
                (activity) =>
                  activity.extendedProps.techrecord === technician.name &&
                  !excludeStatus.includes(activity.extendedProps.status),
              );
              const scheduledNodeSummary = getScheduledNodeSummary(
                recordList,
                nodeCodeByName,
              );

              // Split the list by createdAt timestamp
              const recordedList = recordList.filter((activity) => {
                const scheduled = activity.extendedProps.latestSleepStudyDate; // ✅ Convert Firestore Timestamp
                return (
                  scheduled >= formattedStartOfMonth &&
                  scheduled <= formattedEndOfYesterday
                );
              });

              const recordLeftList = recordList.filter(
                (activity) => !recordedList.includes(activity),
              );

              const cancelList = activityList.filter(
                (activity) =>
                  activity.extendedProps.techrecord === technician.name &&
                  activity.extendedProps.status === "Cancel",
              );
              const failureList = activityList.filter(
                (activity) =>
                  activity.extendedProps.techrecord === technician.name &&
                  activity.extendedProps.status === "Failure",
              );
              const matchedScoredBookings = allActivityList.filter((booking) =>
                scoredList.some(
                  (scored) =>
                    scored.order === booking.title &&
                    booking.extendedProps.technician === technician.name &&
                    !excludeStatus.includes(booking.extendedProps.status),
                ),
              );

              return (
                <div className="col-12 col-md-6 col-lg-4" key={technician.id}>
                  <Card
                    className="mb-3 shadow-sm"
                    style={{ borderRadius: "10px" }}
                  >
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center w-100">
                        {/* Technician Info Section */}
                        <div className="d-flex align-items-center">
                          <img
                            src={avatar}
                            alt=""
                            className="rounded-circle me-4"
                            width="50"
                          />
                          <div
                            style={{ maxWidth: "250px", marginLeft: "1rem" }}
                          >
                            <h5 className="mb-0 fw-semibold text-break">
                              {technician.name} ({technician.nickname})
                            </h5>
                            <p className="mb-0 text-muted text-wrap text-break">
                              {technician.email}
                            </p>
                          </div>
                        </div>

                        {/* Scheduled Section */}
                        <div className="d-flex flex-column align-items-end">
                          <h6
                            className="text-primary text-center"
                            style={{ width: "100px" }}
                          >
                            Scheduled
                          </h6>
                          <h1 className="mb-0 text-primary text-center">
                            {recordList.length}
                          </h1>
                        </div>
                      </div>

                      <div className="mt-3">
                        <div className="row">
                          <div className="col-6">
                            <div className="d-flex flex-column">
                              <div className="d-flex flex-row">
                                <h6
                                  className="text-success"
                                  style={{ width: "100px" }}
                                >
                                  Recorded:
                                </h6>
                                <h5 className="mb-0 ms-3 text-success">
                                  {recordedList.length}
                                </h5>
                              </div>
                              <div className="d-flex flex-row">
                                <h6
                                  className="text-info"
                                  style={{ width: "100px" }}
                                >
                                  Cases Left:
                                </h6>
                                <h5 className="mb-0 ms-3 text-info">
                                  {recordLeftList.length}
                                </h5>
                              </div>
                              <div className="d-flex flex-row">
                                <h6 style={{ width: "100px" }}>Scored:</h6>
                                <h5 className="mb-0 ms-3">
                                  {matchedScoredBookings.length}
                                </h5>
                              </div>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="d-flex flex-column">
                              <div className="d-flex flex-row">
                                <h6
                                  className="text-dark"
                                  style={{ width: "100px" }}
                                >
                                  Cancel:
                                </h6>
                                <h5 className="mb-0 text-dark ms-3">
                                  {cancelList.length}
                                </h5>
                              </div>
                              <div className="d-flex flex-row">
                                <h6
                                  className="text-danger"
                                  style={{ width: "100px" }}
                                >
                                  Failure:
                                </h6>
                                <h5 className="mb-0 text-danger ms-3">
                                  {failureList.length}
                                </h5>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-3 pt-2 border-top">
                        <div className="d-flex align-items-center">
                          <h6
                            className="mb-0 text-muted"
                            style={{ width: "100px", flexShrink: 0 }}
                          >
                            Node:
                          </h6>
                          <div
                            className="d-flex flex-row flex-wrap align-items-center"
                            style={{ flex: 1, minWidth: 0 }}
                          >
                            {scheduledNodeSummary.length > 0 ? (
                              scheduledNodeSummary.map(
                                ({ node, label, count }) => (
                                  <span
                                    key={node}
                                    className="badge badge-light border text-dark mr-2 d-inline-flex align-items-center flex-shrink-0"
                                    title={`${node}: ${count} scheduled`}
                                    style={{
                                      width: "calc(33.333% - 0.5rem)",
                                      fontSize: "14px",
                                    }}
                                  >
                                    <span
                                      className="d-inline-block text-truncate align-bottom"
                                      style={{
                                        flex: 1,
                                        minWidth: 0,
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {label}
                                    </span>
                                    <span className="badge badge-primary ml-1">
                                      {count}
                                    </span>
                                  </span>
                                ),
                              )
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              );
            })}
          </div>{" "}
        </Tab>}

        {canManagePayments && <Tab eventKey="payments" title="Payments">
          {paymentBookingList.length > 0 &&
            canManagePayments && (
              <div className="row">
                <div className="col-12">
                  <div className="d-flex flex-wrap justify-content-between align-items-end mb-3">
                    <div className="mr-4 mb-2">
                      <small
                        className="d-block text-muted text-uppercase font-weight-bold mb-1"
                        style={{ letterSpacing: "0.04em" }}
                      >
                        Technician
                      </small>
                      <h4 className="mb-0">
                        {
                          technicianList.find(
                            (doc) => doc.name === selectedTech,
                          ).name
                        }
                      </h4>
                      <div className="text-muted">
                        {
                          technicianList.find(
                            (doc) => doc.name === selectedTech,
                          ).email
                        }
                      </div>
                    </div>
                    <div className="d-flex align-items-end mb-2">
                      <div className="mr-2">
                        <label
                          className="mb-1 text-muted font-weight-bold"
                          htmlFor="technician-payment-order"
                        >
                          Add by Order
                        </label>
                        <input
                          id="technician-payment-order"
                          type="text"
                          className="form-control"
                          style={{ minWidth: 220 }}
                          placeholder="e.g. CHG-SS-123"
                          onChange={(e) =>
                            (manualOrderRef.current = e.target.value)
                          }
                        />
                      </div>
                      <Button className="mb-0" onClick={addBookingByOrder}>
                        <i className="fa fa-plus mr-1" />
                        Add
                      </Button>
                      {technicianList.some(
                        (tech) =>
                          tech.name === selectedTech &&
                          tech.employmentType === "Full Time",
                      ) && (
                        <small className="text-info ml-3 align-self-center" role="note">
                          <i className="fa fa-info-circle mr-1" aria-hidden="true" />
                          Score: 300 THB
                          <br />
                          Recorded by another technician
                        </small>
                      )}
                    </div>
                  </div>

                  <div className="table-responsive border rounded shadow-sm mb-2">
                    <Table
                      hover
                      className="table-striped table-sm mb-0"
                      style={{ minWidth: 1250 }}
                    >
                  <thead className="thead-light">
                    <tr>
                      <th
                        rowSpan="2"
                        className="align-middle text-nowrap"
                        style={getPaymentStickyColumnStyle({
                          side: "left",
                          offset: 0,
                          width: PAYMENT_TABLE_WIDTHS.case,
                          header: true,
                          edge: true,
                        })}
                      >
                        Patient / Order
                      </th>
                      {/* <th rowSpan="2" className="text-center align-middle">
                        Doctor
                      </th> */}
                      <th
                        rowSpan="2"
                        className="align-middle text-nowrap"
                        style={{ minWidth: 210 }}
                      >
                        Insurance / Status
                      </th>
                      <th
                        rowSpan="2"
                        className="align-middle text-nowrap"
                        style={{ minWidth: 220 }}
                      >
                        Technicians
                      </th>

                      <th
                        rowSpan="2"
                        className="text-center align-middle border-left text-nowrap"
                        style={{ minWidth: 180 }}
                      >
                        Patient Notes
                      </th>

                      {/* Group 1: Paid Status */}
                      <th
                        colSpan="2"
                        className="text-center border-left text-nowrap"
                      >
                        Paid Status
                      </th>

                      {/* Group 2: Payment (This Round) */}
                      <th
                        colSpan="2"
                        className="text-center border-left text-nowrap"
                        style={{
                          ...getPaymentStickyColumnStyle({
                            side: "right",
                            offset:
                              PAYMENT_TABLE_WIDTHS.total +
                              PAYMENT_TABLE_WIDTHS.action,
                            width: PAYMENT_TABLE_WIDTHS.phase * 2,
                            header: true,
                            edge: true,
                          }),
                          zIndex: 5,
                        }}
                      >
                        Phases (This Batch)
                      </th>
                      <th
                        rowSpan="2"
                        className="text-right align-middle text-nowrap border-left"
                        style={getPaymentStickyColumnStyle({
                          side: "right",
                          offset: PAYMENT_TABLE_WIDTHS.action,
                          width: PAYMENT_TABLE_WIDTHS.total,
                          header: true,
                        })}
                      >
                        <span className="d-block">Total</span>
                        <small className="d-block font-weight-normal">THB</small>
                      </th>
                      <th
                        rowSpan="2"
                        className="align-middle text-center"
                        style={getPaymentStickyColumnStyle({
                          side: "right",
                          offset: 0,
                          width: PAYMENT_TABLE_WIDTHS.action,
                          header: true,
                        })}
                      >
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                    <tr>
                      {/* Paid sub-cols */}
                      <th
                        className="text-center text-nowrap border-left"
                        style={{ minWidth: 72 }}
                      >
                        Record
                      </th>
                      <th
                        className="text-center text-nowrap"
                        style={{ minWidth: 72 }}
                      >
                        Score
                      </th>

                      {/* Payment sub-cols with Select All */}
                      <th
                        className="text-center text-nowrap border-left"
                        style={getPaymentStickyColumnStyle({
                          side: "right",
                          offset:
                            PAYMENT_TABLE_WIDTHS.phase +
                            PAYMENT_TABLE_WIDTHS.total +
                            PAYMENT_TABLE_WIDTHS.action,
                          width: PAYMENT_TABLE_WIDTHS.phase,
                          header: true,
                          edge: true,
                        })}
                      >
                        <label className="d-inline-flex align-items-center justify-content-center mb-0">
                          <span className="font-weight-bold mr-2">
                            Record
                          </span>
                          <input
                            ref={recordAllRef}
                            type="checkbox"
                            className="position-static m-0"
                            checked={!!headerState.record.checked}
                            onChange={toggleAllPhase("record")}
                            disabled={!!headerState.record.disabled}
                            aria-label="Select all eligible record payments"
                          />
                        </label>
                      </th>

                      <th
                        className="text-center text-nowrap"
                        style={getPaymentStickyColumnStyle({
                          side: "right",
                          offset:
                            PAYMENT_TABLE_WIDTHS.total +
                            PAYMENT_TABLE_WIDTHS.action,
                          width: PAYMENT_TABLE_WIDTHS.phase,
                          header: true,
                        })}
                      >
                        <label className="d-inline-flex align-items-center justify-content-center mb-0">
                          <span className="font-weight-bold mr-2">Score</span>
                          <input
                            ref={scoreAllRef}
                            type="checkbox"
                            className="position-static m-0"
                            checked={!!headerState.score.checked}
                            onChange={toggleAllPhase("score")}
                            disabled={!!headerState.score.disabled}
                            aria-label="Select all eligible score payments"
                          />
                        </label>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookingWithCaseIndex.map((row, index) => {
                      const ep = row.extendedProps || {};
                      const ord = ep.order;
                      const patientHn = getPatientHn(row);
                      const patientName =
                        [ep.firstname, ep.lastname]
                          .filter(Boolean)
                          .join(" ") || "-";
                      const caseIndex = row.caseIndex;
                      const sel = payThisRound[ord] || {
                        record: false,
                        score: false,
                      };

                      const type = ep.type || "";
                      var typeSymbol = "";

                      const paidRecord = !!ep.isPaidRecord;
                      const paidScore = !!ep.isPaidScore;
                      const recordEligible = eligible(row, "record");
                      const scoreEligible = eligible(row, "score");
                      const recordSelected = Boolean(
                        sel.record && recordEligible,
                      );
                      const scoreSelected = Boolean(sel.score && scoreEligible);
                      const recordBlockReason =
                        !paidRecord && ep.techrecord === selectedTech
                          ? getRecordPaymentBlockReason(row)
                          : "";

                      const tech = technicianList.find(
                        (doc) => doc.name === selectedTech,
                      );
                      const sum =
                        (recordSelected
                          ? getRate(tech, row, "record", caseIndex)
                          : 0) +
                        (scoreSelected
                          ? getRate(tech, row, "score", caseIndex)
                          : 0);

                      var typeSymbol = "";
                      if (type === "Split Night Sleep Study") {
                        typeSymbol = "[SN]";
                      } else if (type === "Full Night Sleep Study") {
                        typeSymbol = "[FN]";
                      } else if (type === "Home Full Night Sleep Study") {
                        typeSymbol = "[HN]";
                      } else if (
                        type === "CPAP Titration" ||
                        type === "CPAP Titration Study"
                      ) {
                        typeSymbol = "[CT]";
                      } else if (type === "Overnight Pulse Oximetry Study") {
                        typeSymbol = "[OPO]";
                      } else if (type === "Multiple Sleep Latency Test") {
                        typeSymbol = "[MSLT]";
                      }

                      return (
                        <tr key={row.id || ord}>
                          <td
                            className="align-middle"
                            style={getPaymentStickyColumnStyle({
                              side: "left",
                              offset: 0,
                              width: PAYMENT_TABLE_WIDTHS.case,
                              edge: true,
                            })}
                          >
                            <div
                              className="d-flex align-items-center text-nowrap"
                              style={{
                                fontWeight: 800,
                                fontSize: 13,
                                lineHeight: "18px",
                              }}
                              title={ord || "-"}
                            >
                              <span className="text-truncate">{ord || "-"}</span>
                              {typeSymbol && (
                                <span
                                  className="badge badge-light border text-dark ml-2 flex-shrink-0"
                                  title={type}
                                >
                                  {typeSymbol}
                                </span>
                              )}
                            </div>
                            {patientHn ? (
                              <Link
                                to={getPatientPagePath(patientHn)}
                                className="d-block font-weight-bold text-primary text-truncate"
                                style={{
                                  lineHeight: "18px",
                                  textDecoration: "none",
                                }}
                                title={`Open patient: ${patientName}`}
                              >
                                {patientName}
                              </Link>
                            ) : (
                              <div
                                className="font-weight-bold text-truncate"
                                style={{ lineHeight: "18px" }}
                                title={patientName}
                              >
                                {patientName}
                              </div>
                            )}
                            <div
                              className="text-muted text-truncate"
                              style={{ fontSize: 11, lineHeight: "15px" }}
                              title={`Sleep Study Date: ${
                                ep.latestSleepStudyDate || "-"
                              }`}
                            >
                              <i className="fa fa-calendar-o mr-1" />
                              {ep.latestSleepStudyDate || "-"}
                            </div>
                          </td>
                          <td className="align-middle">
                            <div className="d-flex align-items-center mb-1">
                              <small
                                className="text-muted mr-2"
                                style={{ minWidth: 58 }}
                              >
                                Insurance
                              </small>
                              <span
                                className="badge badge-light border text-dark d-inline-block text-truncate"
                                style={{ maxWidth: 135 }}
                                title={ep.healthInsurance || "-"}
                              >
                                {ep.healthInsurance || "-"}
                              </span>
                            </div>
                            <div className="d-flex align-items-center">
                              <small
                                className="text-muted mr-2"
                                style={{ minWidth: 58 }}
                              >
                                Status
                              </small>
                              <span
                                className="badge badge-light border text-dark d-inline-block text-truncate"
                                style={{ maxWidth: 135 }}
                                title={ep.status || "-"}
                              >
                                {ep.status || "-"}
                              </span>
                            </div>
                          </td>
                          <td className="align-middle">
                            <div className="d-flex align-items-center mb-1">
                              <small
                                className="text-muted mr-2"
                                style={{ minWidth: 42 }}
                              >
                                Record
                              </small>
                              <span
                                className="d-inline-block text-truncate"
                                style={{ maxWidth: 158 }}
                                title={ep.techrecord || "-"}
                              >
                                <i className="fa fa-user-o text-muted mr-1" />
                                {ep.techrecord || "-"}
                              </span>
                            </div>
                            <div className="d-flex align-items-center">
                              <small
                                className="text-muted mr-2"
                                style={{ minWidth: 42 }}
                              >
                                Score
                              </small>
                              <span
                                className="d-inline-block text-truncate"
                                style={{ maxWidth: 158 }}
                                title={ep.technician || "-"}
                              >
                                <i className="fa fa-user-o text-muted mr-1" />
                                {ep.technician || "-"}
                              </span>
                            </div>
                          </td>

                          <td className="align-middle border-left">
                            <div className="d-flex align-items-center justify-content-between mb-1">
                              <small className="text-muted text-nowrap mr-2">
                                Sleep Study Form
                              </small>
                              {renderPatientNoteAvailability(
                                row,
                                "sleepStudyForm",
                              )}
                            </div>
                            <div className="d-flex align-items-center justify-content-between">
                              <small className="text-muted text-nowrap mr-2">
                                Consent Form
                              </small>
                              {renderPatientNoteAvailability(
                                row,
                                "consentForm",
                              )}
                            </div>
                          </td>

                          {/* Paid Status (icons) */}
                          <td className="text-center align-middle border-left">
                            <PaidIcon
                              on={paidRecord}
                              title={
                                paidRecord
                                  ? getPaidDateTooltip(
                                      "Record",
                                      ep.paidRecordDate,
                                    )
                                  : undefined
                              }
                            />
                          </td>
                          <td className="text-center align-middle">
                            <PaidIcon
                              on={paidScore}
                              title={
                                paidScore
                                  ? getPaidDateTooltip(
                                      "Score",
                                      ep.paidScoreDate,
                                    )
                                  : undefined
                              }
                            />
                          </td>

                          {/* Payment (This Round) */}
                          <td
                            className="text-center align-middle border-left"
                            style={getPaymentStickyColumnStyle({
                              side: "right",
                              offset:
                                PAYMENT_TABLE_WIDTHS.phase +
                                PAYMENT_TABLE_WIDTHS.total +
                                PAYMENT_TABLE_WIDTHS.action,
                              width: PAYMENT_TABLE_WIDTHS.phase,
                              edge: true,
                            })}
                          >
                            <div
                              className="form-check d-flex justify-content-center align-items-center m-0"
                              title={recordBlockReason}
                            >
                              <input
                                className="position-static m-0"
                                type="checkbox"
                                checked={recordSelected}
                                onChange={togglePayTechnician(
                                  ord,
                                  "record",
                                  setPayThisRound,
                                  selectedTech,
                                  ep.techrecord,
                                  ep.status,
                                )}
                                disabled={!recordEligible}
                                aria-label={`Pay record phase for ${ord}`}
                                style={{ width: 18, height: 18 }}
                              />
                            </div>
                          </td>
                          <td
                            className="text-center align-middle"
                            style={getPaymentStickyColumnStyle({
                              side: "right",
                              offset:
                                PAYMENT_TABLE_WIDTHS.total +
                                PAYMENT_TABLE_WIDTHS.action,
                              width: PAYMENT_TABLE_WIDTHS.phase,
                            })}
                          >
                            <div className="form-check d-flex justify-content-center align-items-center m-0">
                              <input
                                className="position-static m-0"
                                type="checkbox"
                                checked={scoreSelected}
                                onChange={togglePayTechnician(
                                  ord,
                                  "score",
                                  setPayThisRound,
                                  selectedTech,
                                  ep.technician,
                                  ep.status,
                                )}
                                disabled={!scoreEligible}
                                aria-label={`Pay score phase for ${ord}`}
                                style={{ width: 18, height: 18 }}
                              />
                            </div>
                          </td>
                          <td
                            className="text-right align-middle border-left text-nowrap"
                            style={getPaymentStickyColumnStyle({
                              side: "right",
                              offset: PAYMENT_TABLE_WIDTHS.action,
                              width: PAYMENT_TABLE_WIDTHS.total,
                            })}
                          >
                            <strong className={sum > 0 ? "text-primary" : ""}>
                              {money(sum)}
                            </strong>
                            {scoreSelected &&
                              getFullTimeExternalScoreRate(tech, row) !== null && (
                                <span
                                  className="text-info ml-2"
                                  tabIndex={0}
                                  role="img"
                                  title="Score: 300 THB — Recorded by another technician"
                                  aria-label="Score: 300 THB — Recorded by another technician"
                                >
                                  <i className="fa fa-info-circle" aria-hidden="true" />
                                </span>
                              )}
                          </td>
                          <td
                            className="text-center align-middle"
                            style={getPaymentStickyColumnStyle({
                              side: "right",
                              offset: 0,
                              width: PAYMENT_TABLE_WIDTHS.action,
                            })}
                          >
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removeBooking(ord)}
                              title={`Remove ${ord} from this payment table`}
                              aria-label={`Remove ${ord} from this payment table`}
                            >
                              <i className="fa fa-trash" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                    </Table>
                  </div>
                </div>

                {/* Footer: grand total + action */}
                {(() => {
                  const tech = technicianList.find(
                    (doc) => doc.name === selectedTech,
                  );

                  // 1) amount: sum of selected phases
                  const amount = bookingWithCaseIndex.reduce((acc, row) => {
                    const ep = row.extendedProps || {};
                    const ord = ep.order;
                    const sel = payThisRound[ord] || {};
                    const caseIndex = row.caseIndex;
                    const recordSelected = Boolean(
                      sel.record && eligible(row, "record"),
                    );
                    const scoreSelected = Boolean(
                      sel.score && eligible(row, "score"),
                    );
                    return (
                      acc +
                      (recordSelected
                        ? getRate(tech, row, "record", caseIndex)
                        : 0) +
                      (scoreSelected
                        ? getRate(tech, row, "score", caseIndex)
                        : 0)
                    );
                  }, 0);

                  // 2) WHT 3%
                  const wht =
                    tech.employmentType === "Full Time" ? 0 : amount * 0.03;

                  const guranteeCase =
                    tech.employmentType === "Full Time"
                      ? parseNum(tech.recordSocialSecurityRate) *
                        parseNum(tech.guaranteeCase)
                      : 0;

                  // Build entries from selections (grouped by order with phases)
                  // const buildEntries = () => {
                  //   const byOrder = new Map();
                  //   bookingTmpList.forEach((row) => {
                  //     const ep = row.extendedProps || {};
                  //     const ord = ep.order;
                  //     if (!ord) return;
                  //     const sel = payThisRound[ord] || {};
                  //     const phases = [];
                  //     if (sel.diagnosis)
                  //       phases.push({
                  //         phase: "diagnosis",
                  //         amount: getRate(doctor, row, "diagnosis"),
                  //       });
                  //     if (sel.post)
                  //       phases.push({
                  //         phase: "postConsult",
                  //         amount: getRate(doctor, row, "post"),
                  //       });
                  //     if (sel.cpap)
                  //       phases.push({
                  //         phase: "cpapFollowup",
                  //         amount: getRate(doctor, row, "cpap"),
                  //       });
                  //     if (!phases.length) return;

                  //     const existing = byOrder.get(ord);
                  //     if (existing) {
                  //       existing.phases.push(...phases);
                  //     } else {
                  //       byOrder.set(ord, {
                  //         order: ord,
                  //         bookingRefPath: row.refPath, // ensure you carried refPath when querying
                  //         phases,
                  //       });
                  //     }
                  //   });
                  //   return Array.from(byOrder.values());
                  // };

                  const buildEntries = () => {
                    const byOrder = new Map();

                    bookingWithCaseIndex.forEach((row) => {
                      const ep = row.extendedProps || {};
                      const ord = ep.order;
                      const caseIndex = row.caseIndex;
                      if (!ord) return;
                      if (
                        !paymentAccess &&
                        ep.technician !== scopedTechName &&
                        ep.techrecord !== scopedTechName
                      ) {
                        return;
                      }

                      const sel = payThisRound[ord] || {};
                      const phases = [];

                      if (sel.record && eligible(row, "record"))
                        phases.push({
                          phase: "record",
                          amount: getRate(tech, row, "record", caseIndex),
                        });
                      if (sel.score && eligible(row, "score"))
                        phases.push({
                          phase: "score",
                          amount: getRate(tech, row, "score", caseIndex),
                        });

                      if (!phases.length) return;

                      // Snapshot of current paid status from booking
                      const paid = {
                        record: {
                          isPaid: !!ep.isPaidRecord,
                          paidDate: ep.paidRecordDate || null,
                          batchId: ep.paidRecordBatchId || null,
                        },
                        score: {
                          isPaid: !!ep.isPaidScore,
                          paidDate: ep.paidScoreDate || null,
                          batchId: ep.paidScoreBatchId || null,
                        },
                      };

                      const baseEntry = {
                        order: ord,
                        bookingRefPath: row.refPath, // keep for later confirm step
                        type: ep.type || "",
                        hn: ep.hn || "",
                        node: ep.node || "",
                        customer: ep.customer || "",
                        firstname: ep.firstname || "",
                        lastname: ep.lastname || "",
                        healthInsurance: ep.healthInsurance || "",
                        latestSleepStudyDate: ep.latestSleepStudyDate || "",
                        status: ep.status || "",
                        technician: ep.technician || "",
                        techrecord: ep.techrecord || "",
                        paid, // 👈 add snapshot here
                        phases, // selected phases + amounts this round
                      };

                      const existing = byOrder.get(ord);
                      if (existing) {
                        existing.phases.push(...phases);
                        // merge is unnecessary for "paid" snapshot; first one is fine
                      } else {
                        byOrder.set(ord, baseEntry);
                      }
                    });

                    return Array.from(byOrder.values());
                  };

                  const onCreatePayment = async ({
                    deduction: draftDeduction,
                    transport: draftTransport,
                    adjustmentPay: draftAdjustmentPay,
                    payDate: draftPayDate,
                    payNote: draftPayNote,
                  }) => {
                    if (
                      !canManagePayments ||
                      (!paymentAccess && selectedTech !== scopedTechName)
                    ) {
                      notifyWarning("You can only create payments for your own cases.");
                      return;
                    }

                    const tech = technicianList.find(
                      (d) => d.name === selectedTech,
                    );
                    if (!tech) {
                      notifyWarning("Doctor not found.");
                      return;
                    }

                    const entries = buildEntries();
                    if (!entries.length) {
                      notifyWarning("Please select at least one item to pay.");
                      return;
                    }
                    if (!draftPayDate) {
                      notifyWarning("Please choose a pay date.");
                      return;
                    }

                    const ded = toNumber(draftDeduction);
                    const trans = toNumber(draftTransport);
                    const adjust = toNumber(draftAdjustmentPay);
                    const note = draftPayNote || "";
                    const totalPay = Math.max(
                      0,
                      amount - wht - ded - guranteeCase + trans + adjust,
                    );

                    // doctorKey used as parent doc id under paymentBatches/{doctorKey}/batches/{autoId}
                    const techKey =
                      (tech.techId || "").trim() || (selectedTech || "").trim();

                    // Compute totals (assumes amount, wht, ded, totalPay already computed in your block)
                    const payload = {
                      techId: tech.techId || tech.id || selectedTech,
                      // month: payDate.slice(0, 7), // "YYYY-MM"
                      month: startDate.slice(0, 7), // "YYYY-MM"
                      period: { start: startDate || "", end: endDate || "" },
                      entries,
                      isConfirmed: false, // draft by default
                      createdBy: currentUser.displayName,
                      createdAt: new Date(),
                      confirmedBy: null,
                      confirmedAt: null,
                      meta: {
                        amount,
                        wht,
                        deduction: ded,
                        guranteeCase,
                        transport: trans,
                        adjustmentPay: adjust,
                        totalPay,
                        payNote: note,
                        payDate: draftPayDate, // "YYYY-MM-DD"
                      },
                    };

                    // Convert to Firestore-friendly doc
                    const batchDoc = {
                      techId: payload.techId,
                      month: payload.month,
                      period: payload.period,
                      entries: payload.entries, // [{ order, bookingRefPath, phases:[{phase, amount}]}]
                      isConfirmed: false,
                      createdBy: payload.createdBy,
                      createdAt: serverTimestamp(),
                      confirmedBy: null,
                      confirmedAt: null,
                      meta: {
                        amount: Number(payload.meta.amount || 0),
                        wht: Number(payload.meta.wht || 0),
                        deduction: Number(payload.meta.deduction || 0),
                        guranteeCase: Number(payload.meta.guranteeCase || 0),
                        transport: Number(payload.meta.transport || 0),
                        adjustmentPay: Number(payload.meta.adjustmentPay || 0),
                        totalPay: Number(payload.meta.totalPay || 0),
                        payNote: payload.meta.payNote || "",
                        payDate: Timestamp.fromDate(
                          new Date(`${payload.meta.payDate}T00:00:00`),
                        ),
                      },
                    };
                    console.log("Batch to create:", batchDoc);

                    const batchRef = doc(
                      collection(
                        db,
                        "paymentTechBatches",
                        techKey,
                        "batches",
                      ),
                    );
                    let uploadedTransportFiles = [];
                    let batchCreated = false;

                    setUploadingTransportFile(true);
                    try {
                      for (const file of transportFiles) {
                        uploadedTransportFiles.push(
                          await uploadTransportFile(file, techKey, batchRef.id),
                        );
                      }
                      if (uploadedTransportFiles.length) {
                        batchDoc.meta.transportFiles = uploadedTransportFiles;
                        // Keep this field for payment batches created before multiple files.
                        batchDoc.meta.transportFile = uploadedTransportFiles[0];
                      }

                      await setDoc(batchRef, batchDoc);
                      batchCreated = true;
                      clearTransportFileSelection();
                      loadPendingBatches();
                      loadReadyToEmailForTech();
                      notifyTopRight(
                        `Draft created (Batch ID: ${batchRef.id}).`,
                      );
                    } catch (err) {
                      if (!batchCreated && uploadedTransportFiles.length) {
                        await removeTransportFilesFromStorage(
                          uploadedTransportFiles,
                          "Unable to clean up transport files after a failed batch creation.",
                        );
                      }
                      console.error(err);
                      notifyError("Create payment failed", err.message || err);
                    } finally {
                      setUploadingTransportFile(false);
                    }
                  };

                  const countsByPhase = getTechnicianPaymentPhaseCounts(
                    paymentBookingList.map((row) => {
                      const ep = row.extendedProps || {};
                      const sel = payThisRound[ep.order] || {};
                      const phases = [];

                      if (sel.record && eligible(row, "record")) {
                        phases.push({ phase: "record" });
                      }
                      if (sel.score && eligible(row, "score")) {
                        phases.push({ phase: "score" });
                      }

                      return { type: ep.type || "", phases };
                    }),
                  );
                  const recordOtherTooltip =
                    formatTechnicianPaymentOtherCaseBreakdown(
                      countsByPhase.recordOtherTypes,
                    );

                  return (
                    <div className="row p-3 w-100 justify-content-end">
                      <div className="col-12 col-md-6 col-lg-5">
                        <div className="border rounded shadow-sm bg-white p-3">
                          {/* Header */}
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <div>
                              <div className="fw-bold">Payment Summary</div>
                            </div>
                          </div>

                          {/* Counts */}
                          <div className="border rounded p-2 mb-3">
                            <div className="d-flex flex-wrap">
                              <div
                                className="px-2 py-1 rounded border mr-3 mb-2"
                                style={{ minWidth: 190 }}
                              >
                                <div
                                  className="text-muted font-weight-bold mb-1"
                                  style={{ fontSize: 12 }}
                                >
                                  Record
                                </div>
                                <div className="d-flex justify-content-between">
                                  <small>Full Night</small>
                                  <strong>{countsByPhase.recordFullNight}</strong>
                                </div>
                                <div className="d-flex justify-content-between">
                                  <small>Split Night</small>
                                  <strong>{countsByPhase.recordSplitNight}</strong>
                                </div>
                                <div
                                  className="d-flex justify-content-between"
                                  title={recordOtherTooltip}
                                  style={{ cursor: "help" }}
                                >
                                  <small>Other</small>
                                  <strong>{countsByPhase.recordOther}</strong>
                                </div>
                              </div>
                              <div className="px-2 py-1 rounded border d-flex align-items-center mr-3 mb-2">
                                <span
                                  className="text-muted"
                                  style={{ fontSize: 12 }}
                                >
                                  Score
                                </span>
                                <span className="fw-bold ml-3">
                                  {countsByPhase.score}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="d-flex justify-content-between mb-2">
                            <span>Amount</span>
                            <strong>{money(amount)} THB</strong>
                          </div>
                          <div className="d-flex justify-content-between mb-2">
                            <span>WHT 3%</span>
                            <strong>- {money(wht)} THB</strong>
                          </div>
                          <div className="d-flex align-items-start mb-2">
                            {/* Left: label (fixed width so it won't touch the middle text) */}
                            <span
                              className="text-muted"
                              style={{ minWidth: 140, whiteSpace: "nowrap" }}
                            >
                              Guarantee Case
                            </span>

                            {/* Middle: breakdown (centered) */}
                            <div className="flex-grow-1 text-center">
                              <div className="small text-muted">
                                Full night : {parseNum(tech.guaranteeCase)} ×{" "}
                                {money(tech.recordSocialSecurityRate)} THB
                              </div>
                            </div>

                            {/* Right: amount (pinned right) */}
                            <strong className="ms-3 text-nowrap">
                              - {money(guranteeCase)} THB
                            </strong>
                          </div>

                          <PaymentDraftControls
                            amount={amount}
                            wht={wht}
                            guaranteeCase={guranteeCase}
                            initialDeduction={deduction}
                            initialTransport={transport}
                            initialAdjustmentPay={adjustmentPay}
                            initialPayDate={payDate}
                            initialPayNote={payNote}
                            resetKey={paymentDraftResetKey}
                            canManagePayments={canManagePayments}
                            uploadingTransportFile={uploadingTransportFile}
                            transportFiles={transportFiles}
                            transportFileInputRef={transportFileInputRef}
                            onTransportFileChange={handleTransportFileChange}
                            onClearTransportFiles={clearTransportFileSelection}
                            onRemoveTransportFile={removeSelectedTransportFile}
                            getTransportFileKey={getTransportFileKey}
                            getTransportFileName={getTransportFileName}
                            onCreatePayment={onCreatePayment}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
        </Tab>}

        {/* ---- Pending tab ---- */}
        {canManagePayments && <Tab eventKey="pending" title="Pending Payments">
          <div className="mt-3">
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
              <div>
                <h4 className="mb-1">
                  Pending Payments{" "}
                  <span className="badge badge-warning">{pendingBatches.length}</span>
                </h4>
                <small className="text-muted">
                  Drafts can be reviewed and edited before confirmation.
                </small>
              </div>
              <Button
                variant="light"
                size="sm"
                onClick={loadPendingBatches}
                disabled={loadingPending}
              >
                Refresh
              </Button>
            </div>

            {loadingPending && <div>Loading…</div>}

            {!loadingPending && pendingBatches.length === 0 && (
              <div className="text-muted">
                No pending batches for this technician.
              </div>
            )}

            {!loadingPending &&
              pendingBatches.length > 0 &&
              canManagePayments && (
                <Table
                  responsive
                  hover
                  className="table-striped table-sm mb-0"
                  style={{ minWidth: 980 }}
                >
                  <thead className="thead-light">
                    <tr>
                      <th>Draft</th>
                      <th>Technician</th>
                      <th>Payment period</th>
                      <th>Payment summary</th>
                      <th className="text-right">Net pay</th>
                      <th className="text-center">Cases</th>
                      <th>Created By</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...pendingBatches]
                      .sort(
                        (a, b) =>
                          paymentTimestamp(b.createdAt) -
                          paymentTimestamp(a.createdAt),
                      )
                      .map((b) => {
                        const meta = b.meta || {};
                        const phaseCounts = getTechnicianPaymentPhaseCounts(
                          b.entries,
                        );
                        const recordOtherTooltip =
                          formatTechnicianPaymentOtherCaseBreakdown(
                            phaseCounts.recordOtherTypes,
                          );
                        const technician =
                          (technicianList.find(
                            (item) => item.techId === b.techKey,
                          ) || {}).name ||
                          b.techId ||
                          b.techKey ||
                          "-";

                        return (
                        <tr key={`${b.techKey}-${b.id}`} className="align-middle">
                          <td>
                            <span className="badge badge-warning">Draft</span>
                            <small className="d-block text-muted mt-1">
                              {formatPaymentDate(b.createdAt, true)}
                            </small>
                            <small className="d-block text-muted">
                              #{String(b.id || "").slice(-8)}
                            </small>
                          </td>
                          <td>
                            <div className="font-weight-bold">{technician}</div>
                            <small className="text-muted">{b.techKey || "-"}</small>
                          </td>
                          <td>
                            <span className="badge badge-light">{b.month || "-"}</span>
                            <small className="d-block text-muted mt-1">
                              Pay: {formatPaymentDate(meta.payDate)}
                            </small>
                          </td>
                          <td>
                            <div>
                              Gross: <strong>{money(meta.amount || 0)} THB</strong>
                            </div>
                            <small className="d-block text-muted">
                              WHT -{money(meta.wht || 0)} · Guarantee -
                              {money(meta.guranteeCase || 0)} · Deduction -
                              {money(meta.deduction || 0)}
                            </small>
                            <small className="d-block text-success">
                              Transport +{money(meta.transport || 0)} · Adjustment +
                              {money(meta.adjustmentPay || 0)}
                            </small>
                            {renderTransportFileLinks(
                              getBatchTransportFiles(meta),
                              "mt-1",
                            )}
                          </td>
                          <td className="text-right">
                            <strong className="text-primary">
                              {money(meta.totalPay || 0)} THB
                            </strong>
                          </td>
                          <td className="text-center" style={{ minWidth: 215 }}>
                            <div className="d-flex flex-column align-items-center">
                              <span className="badge badge-info">Record</span>
                              <small className="text-muted text-nowrap mt-1">
                                Full Night: {phaseCounts.recordFullNight}
                              </small>
                              <small className="text-muted text-nowrap">
                                Split Night: {phaseCounts.recordSplitNight}
                              </small>
                              <small
                                className="text-muted text-nowrap"
                                title={recordOtherTooltip}
                                style={{ cursor: "help" }}
                              >
                                Other: {phaseCounts.recordOther}
                              </small>
                              <span className="badge badge-primary mt-1">
                                Score {phaseCounts.score}
                              </span>
                            </div>
                          </td>
                          <td>{b.createdBy || "-"}</td>
                          <td className="text-right">
                            <div className="d-flex flex-wrap justify-content-end">
                              <Button
                                size="sm"
                                className="mr-2 mb-1"
                                variant="secondary"
                                onClick={() => openBatchForReview(b)}
                              >
                                Review
                              </Button>
                              {canEditPendingBatch(b) && (
                                <Button
                                  size="sm"
                                  className="mr-2 mb-1"
                                  variant="outline-primary"
                                  onClick={() => startBatchEdit(b)}
                                >
                                  Edit
                                </Button>
                              )}
                              {paymentAccess && (
                                <Button
                                  size="sm"
                                  className="mb-1"
                                  variant="outline-danger"
                                  onClick={() => handleDeleteBatch(b.id)}
                                  aria-label={`Delete draft ${b.id}`}
                                  title="Delete draft"
                                >
                                  <i className="fa fa-trash"></i>
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                  </tbody>
                </Table>
              )}

            {/* Review Drawer/Modal (inline simple block here) */}
            {selectedBatch && (
              <div className="card mt-3">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">
                      {editingSelectedBatch ? "Edit Draft Batch" : "Review Batch"}: {" "}
                      {selectedBatch.id}
                    </h5>
                    <Button
                      variant="light"
                      className="btn btn-sm mr-2"
                      size="sm"
                      onClick={() => {
                        setBatchEdit(null);
                        setSelectedBatch(null);
                      }}
                    >
                      Close
                    </Button>
                  </div>

                  {editingSelectedBatch && (
                    <div className="text-muted small mb-2">
                      Update the included phase amounts and payment details, then
                      save the draft before confirming it.
                    </div>
                  )}

                  {editingSelectedBatch && (
                    <div className="d-flex flex-wrap justify-content-between align-items-end border-bottom pb-3 mb-3">
                      <small className="text-muted mb-2 mr-3">
                        Add an order to include its unpaid eligible phase(s) for
                        this technician.
                      </small>
                      <PaymentBatchAddByOrderControls
                        onAdd={addBookingToBatchEdit}
                        disabled={savingBatchEdit}
                      />
                    </div>
                  )}

                  {editingSelectedBatch && (
                    <div className="border rounded bg-light p-2 mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <strong>Transport files</strong>
                        <small className="text-muted">
                          Delete applies when you save changes.
                        </small>
                      </div>
                      {(batchEdit.transportFiles || []).length > 0 ? (
                        (batchEdit.transportFiles || []).map((file) => {
                          const fileKey = getTransportFileKey(file);
                          const isMarkedForDeletion = (
                            batchEdit.removedTransportFileKeys || []
                          ).includes(fileKey);

                          return (
                            <div
                              key={fileKey}
                              className="d-flex align-items-center flex-wrap mb-1"
                            >
                              <button
                                type="button"
                                className={`btn btn-link btn-sm p-0 mr-2 ${
                                  isMarkedForDeletion ? "text-muted" : ""
                                }`}
                                style={
                                  isMarkedForDeletion
                                    ? { textDecoration: "line-through" }
                                    : undefined
                                }
                                onClick={() => openTransportFilePreview(file)}
                              >
                                <i className="fa fa-paperclip mr-1" />
                                {getTransportFileName(file)}
                              </button>
                              <Button
                                type="button"
                                size="sm"
                                variant={
                                  isMarkedForDeletion
                                    ? "outline-secondary"
                                    : "danger"
                                }
                                onClick={() => {
                                  if (
                                    isMarkedForDeletion ||
                                    window.confirm(
                                      "Delete this transport file when you save the draft payment?",
                                    )
                                  ) {
                                    toggleBatchEditTransportFileDeletion(file);
                                  }
                                }}
                                disabled={savingBatchEdit}
                              >
                                <i className="fa fa-trash mr-1" />
                                {isMarkedForDeletion
                                  ? "Keep file"
                                  : "Delete file"}
                              </Button>
                              {isMarkedForDeletion && (
                                <small className="text-danger ml-2">
                                  Will be deleted on save
                                </small>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <small className="d-block text-muted">
                          No saved transport files in this batch.
                        </small>
                      )}
                    </div>
                  )}

                  <PaymentBatchReviewTable
                    entries={
                      editingSelectedBatch
                        ? batchEdit.entries
                        : selectedBatch.entries || []
                    }
                    editing={editingSelectedBatch}
                    onUpdatePhaseAmount={updateBatchEditPhaseAmount}
                    onRemoveEntry={
                      editingSelectedBatch ? removeBatchEditEntry : undefined
                    }
                    renderPatientNoteAvailability={renderPatientNoteAvailability}
                    bookingDetailsByPath={batchBookingDetailsByPath}
                  />

                  <div className="d-flex justify-content-end mb-3">
                    <div
                      className="border rounded bg-light p-2"
                      style={{ minWidth: 210 }}
                    >
                      <div className="font-weight-bold text-muted mb-2">
                        Case Counts
                      </div>
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="mr-4">
                          <span className="badge badge-info">
                            Record {reviewBatchPhaseCounts.record}
                          </span>
                          <small className="d-block text-muted mt-1">
                            Full Night: {reviewBatchPhaseCounts.recordFullNight}
                          </small>
                          <small className="d-block text-muted">
                            Split Night: {reviewBatchPhaseCounts.recordSplitNight}
                          </small>
                          <small
                            className="d-block text-muted"
                            title={reviewRecordOtherTooltip}
                            style={{ cursor: "help" }}
                          >
                            Other: {reviewBatchPhaseCounts.recordOther}
                          </small>
                        </div>
                        <span className="badge badge-primary mt-1">
                          Score {reviewBatchPhaseCounts.score}
                        </span>
                      </div>
                    </div>
                  </div>

                  <hr />

                  {editingSelectedBatch ? (
                    <div className="row justify-content-end">
                      <div className="col-12 col-md-6 col-lg-5">
                        <div className="border rounded bg-light p-3">
                          <div className="d-flex justify-content-between mb-2">
                            <span>Amount</span>
                            <strong>{money(batchEditTotals.amount)} THB</strong>
                          </div>
                          <div className="d-flex justify-content-between mb-2">
                            <span>WHT 3%</span>
                            <strong>- {money(batchEditTotals.wht)} THB</strong>
                          </div>
                          <div className="d-flex justify-content-between mb-3">
                            <span>Guarantee Case</span>
                            <strong>
                              - {money(batchEditTotals.guaranteeCase)} THB
                            </strong>
                          </div>

                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="mb-0">Deduction</label>
                            <input
                              type="number"
                              min="0"
                              className="form-control form-control-sm text-end"
                              style={{ width: 160 }}
                              value={batchEdit.deduction}
                              onChange={(event) =>
                                setBatchEdit((previous) => ({
                                  ...previous,
                                  deduction: event.target.value,
                                }))
                              }
                            />
                          </div>

                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="mb-0">Transport</label>
                            <input
                              type="number"
                              min="0"
                              className="form-control form-control-sm text-end"
                              style={{ width: 160 }}
                              value={batchEdit.transport}
                              onChange={(event) =>
                                setBatchEdit((previous) => ({
                                  ...previous,
                                  transport: event.target.value,
                                }))
                              }
                            />
                          </div>

                          <div className="mb-3">
                            <label className="mb-1">
                              Transport documents{" "}
                              <small className="text-muted">
                                (optional PDFs or images)
                              </small>
                            </label>
                            {(batchEdit.transportFiles || []).length > 0 ? (
                              <div className="small mb-2">
                                {(batchEdit.transportFiles || []).map((file) => {
                                  const fileKey = getTransportFileKey(file);
                                  const isMarkedForDeletion = (
                                    batchEdit.removedTransportFileKeys || []
                                  ).includes(fileKey);

                                  return (
                                    <div
                                      key={fileKey}
                                      className="d-flex align-items-center flex-wrap mb-1"
                                    >
                                      <button
                                        type="button"
                                        className={`btn btn-link btn-sm p-0 align-baseline mr-2 ${
                                          isMarkedForDeletion
                                            ? "text-muted"
                                            : ""
                                        }`}
                                        style={
                                          isMarkedForDeletion
                                            ? { textDecoration: "line-through" }
                                            : undefined
                                        }
                                        onClick={() =>
                                          openTransportFilePreview(file)
                                        }
                                      >
                                        <i className="fa fa-paperclip mr-1" />
                                        {getTransportFileName(file)}
                                      </button>
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant={
                                          isMarkedForDeletion
                                            ? "outline-secondary"
                                            : "outline-danger"
                                        }
                                        onClick={() => {
                                          if (
                                            isMarkedForDeletion ||
                                            window.confirm(
                                              "Delete this transport file when you save the draft payment?",
                                            )
                                          ) {
                                            toggleBatchEditTransportFileDeletion(
                                              file,
                                            );
                                          }
                                        }}
                                        disabled={savingBatchEdit}
                                      >
                                        {isMarkedForDeletion
                                          ? "Keep file"
                                          : "Delete file"}
                                      </Button>
                                      {isMarkedForDeletion && (
                                        <small className="text-danger ml-2">
                                          Will be deleted on save
                                        </small>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <small className="d-block text-muted mb-2">
                                No transport files uploaded.
                              </small>
                            )}
                            <input
                              ref={batchEditTransportFileInputRef}
                              type="file"
                              className="form-control-file"
                              accept="application/pdf,image/*"
                              multiple
                              onChange={handleBatchEditTransportFileChange}
                              disabled={savingBatchEdit}
                            />
                            {(batchEdit.newTransportFiles || []).length > 0 && (
                              <div className="small text-muted mt-1">
                                {(batchEdit.newTransportFiles || []).map((file) => (
                                  <div
                                    key={getTransportFileKey(file)}
                                    className="d-flex align-items-center flex-wrap mb-1"
                                  >
                                    <span className="mr-2">
                                      New file: {getTransportFileName(file)}
                                    </span>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline-secondary"
                                      onClick={() =>
                                        removeBatchEditNewTransportFile(file)
                                      }
                                      disabled={savingBatchEdit}
                                    >
                                      Remove
                                    </Button>
                                  </div>
                                ))}
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline-secondary"
                                  onClick={clearBatchEditTransportFileSelection}
                                  disabled={savingBatchEdit}
                                >
                                  Remove all new files
                                </Button>
                              </div>
                            )}
                          </div>

                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <label className="mb-0">Adjustment Pay</label>
                            <input
                              type="number"
                              min="0"
                              className="form-control form-control-sm text-end"
                              style={{ width: 160 }}
                              value={batchEdit.adjustmentPay}
                              onChange={(event) =>
                                setBatchEdit((previous) => ({
                                  ...previous,
                                  adjustmentPay: event.target.value,
                                }))
                              }
                            />
                          </div>

                          <div className="mb-3">
                            <label className="mb-1">Payment Note</label>
                            <textarea
                              className="form-control form-control-sm"
                              rows={3}
                              value={batchEdit.payNote}
                              onChange={(event) =>
                                setBatchEdit((previous) => ({
                                  ...previous,
                                  payNote: event.target.value,
                                }))
                              }
                            />
                          </div>

                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <label className="mb-0">Pay Date</label>
                            <input
                              type="date"
                              className="form-control form-control-sm"
                              style={{ width: 180 }}
                              value={batchEdit.payDate}
                              onChange={(event) =>
                                setBatchEdit((previous) => ({
                                  ...previous,
                                  payDate: event.target.value,
                                }))
                              }
                            />
                          </div>

                          <div className="border-top pt-3 d-flex justify-content-between align-items-center">
                            <div>
                              <span className="text-muted">Total Pay</span>
                              <h4 className="mb-0">
                                {money(batchEditTotals.totalPay)} THB
                              </h4>
                            </div>
                            <div>
                              <Button
                                variant="light"
                                size="sm"
                                className="mr-2"
                                onClick={() => setBatchEdit(null)}
                                disabled={savingBatchEdit}
                              >
                                Cancel
                              </Button>
                              <Button
                                size="sm"
                                onClick={saveBatchEdit}
                                disabled={savingBatchEdit}
                              >
                                {savingBatchEdit ? "Saving…" : "Save Changes"}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="d-flex justify-content-end align-items-start"
                      style={{ gap: "20px" }}
                    >
                      <div className="text-right">
                        <div>
                          Amount:{" "}
                          <strong>
                            {money(selectedBatch.meta.amount || 0)} THB
                          </strong>
                        </div>
                        <div>
                          WHT 3%:{" "}
                          <strong>
                            - {money(selectedBatch.meta.wht || 0)} THB
                          </strong>
                        </div>
                        <div>
                          Guarantee Case:{" "}
                          <strong>
                            - {money(selectedBatch.meta.guranteeCase || 0)} THB
                          </strong>
                        </div>

                        <div>
                          Deduction:{" "}
                          <strong>
                            - {money(selectedBatch.meta.deduction || 0)} THB
                          </strong>
                        </div>
                        <div>
                          Transport:{" "}
                          <strong>
                            + {money(selectedBatch.meta.transport || 0)} THB
                          </strong>
                        </div>
                        {getBatchTransportFiles(selectedBatch.meta).length >
                          0 && (
                          <div>
                            <div>Transport files:</div>
                            {renderTransportFileLinks(
                              getBatchTransportFiles(selectedBatch.meta),
                            )}
                          </div>
                        )}

                        <div>
                          Adjustment Pay:{" "}
                          <strong>
                            + {money(selectedBatch.meta.adjustmentPay || 0)} THB
                          </strong>
                        </div>
                        <div className="mt-2">
                          Total Pay:{" "}
                          <h3 className="d-inline">
                            <strong>
                              {money(selectedBatch.meta.totalPay || 0)} THB
                            </strong>
                          </h3>
                        </div>
                        <div className="mt-1">
                          Pay Date:{" "}
                          <strong>
                            {selectedBatch.meta.payDate.toDate
                              ? selectedBatch.meta.payDate
                                  .toDate()
                                  .toLocaleDateString("en-GB", {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                  })
                              : "-"}
                          </strong>
                        </div>
                        <div>
                          Payment Note:{" "}
                          <strong>
                            {(selectedBatch.meta.payNote || "")
                              .split("\n")
                              .map((line, i) => (
                                <React.Fragment key={i}>
                                  {line}
                                  <br />
                                </React.Fragment>
                              ))}
                          </strong>
                        </div>
                      </div>

                      <div className="d-flex" style={{ gap: "8px" }}>
                        {canEditPendingBatch(selectedBatch) && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            style={{
                              height: "fit-content",
                              padding: "10px 16px",
                            }}
                            onClick={() => startBatchEdit(selectedBatch)}
                          >
                            Edit
                          </Button>
                        )}
                        {paymentAccess && (
                          <Button
                            variant="danger"
                            size="sm"
                            style={{ height: "fit-content", padding: "10px 16px" }}
                            onClick={() =>
                              requestBatchConfirmation(
                                "confirm",
                                selectedBatch.techKey,
                                selectedBatch.id,
                              )
                            }
                            disabled={processingBatchConfirmation}
                          >
                            Confirm Payment
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </Tab>}

        {canViewConfirmedPayments && <Tab
          eventKey="confirmed"
          title={paymentAccess ? "Confirmed Payments" : "Paid Cases"}
        >
          {/* Ready to Send Email tab */}
          <div className="mt-3">
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
              <div>
                <h4 className="mb-1">
                  {paymentAccess ? "Confirmed Payments" : "Paid Cases"}{" "}
                  <span className="badge badge-success">{readyToEmail.length}</span>
                </h4>
                <small className="text-muted">
                  {paymentAccess
                    ? "Review confirmation and email status for each completed batch."
                    : "View your confirmed paid cases. This section is read-only."}
                </small>
              </div>
              <Button
                variant="light"
                size="sm"
                onClick={loadReadyToEmailForTech}
                disabled={loadingReady}
              >
                Refresh
              </Button>
            </div>

            {loadingReady && <div>Loading…</div>}

            {!loadingReady && readyToEmail.length === 0 && (
              <div className="text-muted">
                {paymentAccess
                  ? "No confirmed payments."
                  : "No paid cases found for this technician."}
              </div>
            )}

            {!loadingReady &&
              readyToEmail.length > 0 &&
              canViewConfirmedPayments && (
                <React.Fragment>
                <Table
                  responsive
                  hover
                  className="table-striped table-sm mb-0"
                  style={{ minWidth: 980 }}
                >
                  <thead className="thead-light">
                    <tr>
                      <th>Status</th>
                      <th>Technician</th>
                      <th>Payment period</th>
                      <th>Payment summary</th>
                      <th className="text-right">Net pay</th>
                      <th className="text-center">Cases</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {confirmedBatchesOnPage.map((b) => {
                        const meta = b.meta || {};
                        const phaseCounts = getTechnicianPaymentPhaseCounts(
                          b.entries,
                        );
                        const technician =
                          (technicianList.find(
                            (item) => item.techId === b.techKey,
                          ) || {}).name ||
                          b.techId ||
                          b.techKey ||
                          "-";

                        return (
                        <tr key={`${b.techKey}-${b.id}`} className="align-middle">
                          <td>
                            <span className="badge badge-success">Confirmed</span>
                            <small className="d-block text-muted mt-1">
                              {formatPaymentDate(b.confirmedAt, true)}
                            </small>
                            <span
                              className={`badge mt-1 ${
                                b.isSendEmail ? "badge-success" : "badge-light"
                              }`}
                            >
                              {b.isSendEmail ? "Email sent" : "Email pending"}
                            </span>
                          </td>
                          <td>
                            <div className="font-weight-bold">{technician}</div>
                            <small className="text-muted">{b.techKey || "-"}</small>
                          </td>
                          <td>
                            <span className="badge badge-light">{b.month || "-"}</span>
                            <small className="d-block text-muted mt-1">
                              Pay: {formatPaymentDate(meta.payDate)}
                            </small>
                          </td>
                          <td>
                            <div>
                              Gross: <strong>{money(meta.amount || 0)} THB</strong>
                            </div>
                            <small className="d-block text-muted">
                              WHT -{money(meta.wht || 0)} · Guarantee -
                              {money(meta.guranteeCase || 0)} · Deduction -
                              {money(meta.deduction || 0)}
                            </small>
                            <small className="d-block text-success">
                              Transport +{money(meta.transport || 0)} · Adjustment +
                              {money(meta.adjustmentPay || 0)}
                            </small>
                            {renderTransportFileLinks(
                              getBatchTransportFiles(meta),
                              "mt-1",
                            )}
                          </td>
                          <td className="text-right">
                            <strong className="text-primary">
                              {money(meta.totalPay || 0)} THB
                            </strong>
                          </td>
                          <td className="text-center">
                            <span className="badge badge-info mr-1" title="Record cases">
                              Record {phaseCounts.record}
                            </span>
                            <span className="badge badge-primary" title="Score cases">
                              Score {phaseCounts.score}
                            </span>
                          </td>
                          <td className="text-right">
                            <div className="d-flex flex-wrap justify-content-end">
                              <Button
                                size="sm"
                                variant="outline-primary"
                                className="mr-2 mb-1"
                                onClick={() => openBatchForReview(b)}
                              >
                                Review
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                className="mr-2 mb-1"
                                onClick={() => exportBatchToExcel(b, selectedTech)}
                              >
                                Export Excel
                              </Button>{" "}
                              {canRollbackConfirmedBatch && (
                                <Button
                                  size="sm"
                                  variant="warning"
                                  className="mr-2 mb-1"
                                  onClick={() =>
                                    requestBatchConfirmation(
                                      "return",
                                      b.techKey,
                                      b.id,
                                    )
                                  }
                                  disabled={
                                    Boolean(rollingBackBatchKey) ||
                                    processingBatchConfirmation
                                  }
                                >
                                  {rollingBackBatchKey === `${b.techKey}/${b.id}`
                                    ? "Returning…"
                                    : "Return to Pending"}
                                </Button>
                              )}
                              {paymentAccess && (
                                <Button
                                  size="sm"
                                  variant={
                                    b.isSendEmail
                                      ? "outline-success"
                                      : "success"
                                  }
                                  className="mb-1"
                                  onClick={() => handleSendEmail(b)}
                                >
                                  {b.isSendEmail
                                    ? "Resend Email"
                                    : "Send Email"}
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                        );
                    })}
                  </tbody>
                </Table>
                <div className="d-flex flex-wrap justify-content-between align-items-center mt-3">
                  <small className="text-muted mb-2">
                    Showing {confirmedPaymentsPageStart + 1}–
                    {Math.min(
                      confirmedPaymentsPageStart + CONFIRMED_BATCHES_PER_PAGE,
                      sortedConfirmedBatches.length,
                    )} of {sortedConfirmedBatches.length} confirmed payments · 15
                    per page
                  </small>
                  {confirmedPaymentsPageCount > 1 && (
                    <Pagination className="mb-2 pagination-primary pagination-circle">
                      <Pagination.First
                        onClick={() => setConfirmedPaymentsPage(1)}
                        disabled={currentConfirmedPaymentsPage === 1}
                      />
                      <Pagination.Prev
                        onClick={() =>
                          setConfirmedPaymentsPage(
                            currentConfirmedPaymentsPage - 1,
                          )
                        }
                        disabled={currentConfirmedPaymentsPage === 1}
                      />
                      {confirmedPaginationPages.map((page, index) => {
                        const previousPage = confirmedPaginationPages[index - 1];
                        return (
                          <React.Fragment key={page}>
                            {previousPage && page - previousPage > 1 && (
                              <Pagination.Ellipsis disabled />
                            )}
                            <Pagination.Item
                              active={page === currentConfirmedPaymentsPage}
                              onClick={() => setConfirmedPaymentsPage(page)}
                            >
                              {page}
                            </Pagination.Item>
                          </React.Fragment>
                        );
                      })}
                      <Pagination.Next
                        onClick={() =>
                          setConfirmedPaymentsPage(
                            currentConfirmedPaymentsPage + 1,
                          )
                        }
                        disabled={
                          currentConfirmedPaymentsPage ===
                          confirmedPaymentsPageCount
                        }
                      />
                      <Pagination.Last
                        onClick={() =>
                          setConfirmedPaymentsPage(confirmedPaymentsPageCount)
                        }
                        disabled={
                          currentConfirmedPaymentsPage ===
                          confirmedPaymentsPageCount
                        }
                      />
                    </Pagination>
                  )}
                </div>
                </React.Fragment>
              )}

            {/* Optional: reuse the same Review card/modal you already built for pending batches */}
            {selectedBatch && (
              <div className="card mt-3">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Review Batch: {selectedBatch.id}</h5>
                    <Button
                      variant="light"
                      size="sm"
                      onClick={() => setSelectedBatch(null)}
                    >
                      Close
                    </Button>
                  </div>

                  <PaymentBatchReviewTable
                    entries={selectedBatch.entries || []}
                    renderPatientNoteAvailability={renderPatientNoteAvailability}
                    bookingDetailsByPath={batchBookingDetailsByPath}
                  />

                  <hr />

                  <div
                    className="d-flex justify-content-end align-items-start"
                    style={{ gap: "20px" }}
                  >
                    <div className="text-right">
                      <div>
                        Amount:{" "}
                        <strong>
                          {money(selectedBatch.meta.amount || 0)} THB
                        </strong>
                      </div>
                      <div>
                        WHT 3%:{" "}
                        <strong>
                          - {money(selectedBatch.meta.wht || 0)} THB
                        </strong>
                      </div>
                      <div>
                        Guarantee Case:{" "}
                        <strong>
                          - {money(selectedBatch.meta.guranteeCase || 0)} THB
                        </strong>
                      </div>

                      <div>
                        Deduction:{" "}
                        <strong>
                          - {money(selectedBatch.meta.deduction || 0)} THB
                        </strong>
                      </div>
                      <div>
                        Transport:{" "}
                        <strong>
                          + {money(selectedBatch.meta.transport || 0)} THB
                        </strong>
                      </div>
                      {getBatchTransportFiles(selectedBatch.meta).length > 0 && (
                        <div>
                          <div>Transport files:</div>
                          {renderTransportFileLinks(
                            getBatchTransportFiles(selectedBatch.meta),
                          )}
                        </div>
                      )}
                      <div>
                        Adjustment Pay:{" "}
                        <strong>
                          + {money(selectedBatch.meta.adjustmentPay || 0)} THB
                        </strong>
                      </div>
                      <div className="mt-2">
                        Total Pay:{" "}
                        <h3 className="d-inline">
                          {money(selectedBatch.meta.totalPay || 0)} THB
                        </h3>
                      </div>
                      <div className="mt-1">
                        Pay Date:{" "}
                        <strong>
                          {selectedBatch.meta.payDate.toDate()
                            ? selectedBatch.meta.payDate
                                .toDate()
                                .toLocaleDateString("en-GB", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                })
                            : "-"}
                        </strong>
                      </div>
                      {/* <div>
                        Payment Note:{" "}
                        <strong>{selectedBatch.meta.payNote || ""}</strong>
                      </div> */}
                      <div>
                        Payment Note:{" "}
                        <strong>
                          {(selectedBatch.meta.payNote || "")
                            .split("\n")
                            .map((line, i) => (
                              <React.Fragment key={i}>
                                {line}
                                <br />
                              </React.Fragment>
                            ))}
                        </strong>
                      </div>
                    </div>

                    <div className="d-flex flex-wrap justify-content-end" style={{ gap: "8px" }}>
                      {canRollbackConfirmedBatch && (
                        <Button
                          variant="warning"
                          size="sm"
                          style={{ height: "fit-content", padding: "10px 16px" }}
                          onClick={() =>
                            requestBatchConfirmation(
                              "return",
                              selectedBatch.techKey,
                              selectedBatch.id,
                            )
                          }
                          disabled={
                            Boolean(rollingBackBatchKey) ||
                            processingBatchConfirmation
                          }
                        >
                          {rollingBackBatchKey ===
                          `${selectedBatch.techKey}/${selectedBatch.id}`
                            ? "Returning…"
                            : "Return to Pending to Edit"}
                        </Button>
                      )}
                      {paymentAccess && (
                        <Button
                          variant="success"
                          size="sm"
                          style={{
                            height: "fit-content",
                            padding: "10px 16px",
                          }}
                          onClick={() => handleSendEmail(selectedBatch)}
                        >
                          Send Email
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Tab>}
      </Tabs>

      <Modal
        show={Boolean(patientNoteViewer)}
        onHide={closePatientNoteViewer}
        centered
        size="xl"
      >
        <Modal.Header closeButton>
          <div>
            <Modal.Title>{patientNoteViewer && patientNoteViewer.title}</Modal.Title>
            {patientNoteViewer && (
              <small className="text-muted">
                {[patientNoteViewer.patientName, patientNoteViewer.hn]
                  .filter(Boolean)
                  .join(" · ")}
              </small>
            )}
          </div>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ minHeight: "70vh" }}>
          {patientNoteViewer && patientNoteViewer.files.length > 0 ? (
            <div className="row no-gutters" style={{ minHeight: "70vh" }}>
              <div
                className="col-12 col-md-4 col-lg-3 border-right bg-light p-3"
                style={{ maxHeight: "70vh", overflowY: "auto" }}
              >
                <div className="font-weight-bold mb-2">
                  Files ({patientNoteViewer.files.length})
                </div>
                {patientNoteViewer.files.map((file, index) => {
                  const isSelected =
                    patientNoteFilePreview &&
                    getPatientNoteFileKey(patientNoteFilePreview) ===
                      getPatientNoteFileKey(file);

                  return (
                    <button
                      key={`${getPatientNoteFileKey(file)}-${index}`}
                      type="button"
                      className={`btn btn-sm btn-block text-left mb-2 ${
                        isSelected ? "btn-primary" : "btn-outline-primary"
                      }`}
                      style={{ whiteSpace: "normal", wordBreak: "break-word" }}
                      onClick={() => openPatientNoteFile(file)}
                    >
                      <i className="fa fa-paperclip mr-1" />
                      {file.fileName}
                    </button>
                  );
                })}
              </div>
              <div
                className="col-12 col-md-8 col-lg-9 d-flex flex-column"
                style={{ minHeight: "70vh" }}
              >
                {loadingPatientNoteFilePreview ? (
                  <div className="d-flex flex-column justify-content-center align-items-center flex-grow-1">
                    <div className="spinner-border text-primary mb-3" role="status">
                      <span className="sr-only">Loading file…</span>
                    </div>
                    <div className="text-muted">Loading file…</div>
                  </div>
                ) : patientNoteFilePreview &&
                  patientNoteFilePreview.url &&
                  isPatientNoteImageFile(patientNoteFilePreview) ? (
                  <div className="d-flex justify-content-center align-items-center flex-grow-1 p-3">
                    <img
                      src={patientNoteFilePreview.url}
                      alt={patientNoteFilePreview.fileName}
                      style={{ maxWidth: "100%", maxHeight: "66vh" }}
                    />
                  </div>
                ) : patientNoteFilePreview &&
                  patientNoteFilePreview.url &&
                  isPatientNotePdfFile(patientNoteFilePreview) ? (
                  <iframe
                    title="Patient note file preview"
                    src={`${patientNoteFilePreview.url}#toolbar=0`}
                    style={{
                      width: "100%",
                      minHeight: "70vh",
                      border: 0,
                    }}
                  />
                ) : patientNoteFilePreview && patientNoteFilePreview.url ? (
                  <div className="d-flex flex-column justify-content-center align-items-center text-center flex-grow-1 p-4">
                    <i className="fa fa-file-o fa-3x text-muted mb-3" />
                    <p className="mb-1">{patientNoteFilePreview.fileName}</p>
                    <p className="text-muted mb-0">
                      This file type cannot be previewed here. Use Open file
                      below.
                    </p>
                  </div>
                ) : null}
              </div>
            </div>
          ) : patientNoteViewer ? (
            <div className="d-flex flex-column justify-content-center align-items-center text-center p-4" style={{ minHeight: "70vh" }}>
              <i className="fa fa-file-o fa-3x text-muted mb-3" />
              <h5>No files attached</h5>
              <p className="text-muted mb-0">
                The {patientNoteViewer.title} note exists, but it does not have
                an attached file.
              </p>
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          {patientNoteFilePreview && patientNoteFilePreview.url && (
            <Button
              as="a"
              href={patientNoteFilePreview.url}
              target="_blank"
              rel="noopener noreferrer"
              variant="outline-primary"
            >
              Open file
            </Button>
          )}
          <Button variant="light" onClick={closePatientNoteViewer}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={Boolean(transportFilePreview)}
        onHide={() => setTransportFilePreview(null)}
        centered
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-truncate">
            {getTransportFileName(transportFilePreview)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ minHeight: "70vh" }}>
          {transportFilePreview && isTransportImageFile(transportFilePreview) ? (
            <div className="d-flex justify-content-center align-items-center p-3 h-100 bg-light">
              <img
                src={transportFilePreview.url}
                alt={getTransportFileName(transportFilePreview)}
                style={{ maxWidth: "100%", maxHeight: "68vh" }}
              />
            </div>
          ) : transportFilePreview && isTransportPdfFile(transportFilePreview) ? (
            <iframe
              title="Transport document preview"
              src={transportFilePreview.url}
              style={{ width: "100%", minHeight: "70vh", border: 0 }}
            />
          ) : transportFilePreview ? (
            <div className="d-flex flex-column justify-content-center align-items-center text-center h-100 p-4">
              <i className="fa fa-file-o fa-3x text-muted mb-3" />
              <p className="mb-0">
                This file type cannot be previewed in the payment page.
              </p>
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          {transportFilePreview && transportFilePreview.url && (
            <Button
              as="a"
              href={transportFilePreview.url}
              target="_blank"
              rel="noopener noreferrer"
              variant="outline-primary"
            >
              Open file
            </Button>
          )}
          <Button
            variant="light"
            onClick={() => setTransportFilePreview(null)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default TechDashboard;
